import type { AdminReport } from "@/services/mindshareApi";
import { MasterScope } from "@/constants/masterScopes";
import { authAxios, publicAxios } from "./axiosInstance";
import { getEnvVar } from "@/utils/util";
import { AxiosResponse } from "axios";
import { GoalsGroup } from "@/types/goals";
import { GoalItem, GoalStatus, MANUAL_GOALS_GROUP } from "@/types/goals";
import { TaskStatus, TaskGroup, TaskItem } from "@/types/tasks";
import type { StarPortfolioEntry, StarPortfolioStory } from "@/utils/starPortfolio";
import type { SchedulerConfigResult } from "@/utils/schedulerConfig";
import type {
  AdminBulkCouponInput,
  AdminBulkCouponRecipient,
  AdminCouponCodeCheck,
  AdminCouponDetail,
  AdminCouponInput,
  AdminCouponListParams,
  AdminCouponPricePreview,
  AdminCouponPricePreviewParams,
  AdminCouponProgramOption,
  AdminCouponRedemptionRow,
  AdminCouponRow,
  AdminCouponStats,
} from "@/types/adminCoupons";
import {
  LeadSignupRequest,
  LeadSignupResponse,
  LeadVerificationResponse,
  LeadMatchingResponse,
  DemoCallBookingRequest,
  DemoCallBookingResponse,
  LeadConversionRequest,
  LeadConversionResponse,
} from "@/types/lead";

export enum ExperienceEnum {
  ZERO_TO_TWO = "0-2",
  TWO_TO_FIVE = "2-5",
  FIVE_TO_TEN = "5-10",
  TEN_TO_FIFTEEN = "10-15",
  FIFTEEN_TO_TWENTY = "15-20",
  TWENTY_PLUS = "20+",
}

export interface LocationOption {
  /** Saved to the profile as-is, e.g. "Houston, TX, USA". */
  name: string;
  kind: "COUNTRY" | "STATE" | "CITY" | "SPECIAL";
}

export interface ConnectedAccount {
  userId: string;
  email: string;
  fullName: string;
  role: string;
  roles: string[];
  profilePictureUrl: string | null;
}

/**
 * OKT (Objective Key Task) type based on backend response
 */
export interface OKT {
  id: number;
  title: string;
  description: string;
  status: TaskStatus;
  startDate: string | null;
  dueDate: string | null;
  isSuggested: boolean;
  isFinalized: boolean;
  evaluationComment: string | null;
  notesCount?: number;
  /** Expert Program tasks only — set when this task was synced down from an advisor's template. */
  clonedFromId?: number | null;
}

/**
 * OKR Category type based on backend response
 */
export interface OKRCategory {
  group: string;
  description: string;
  type: "Hard" | "Soft";
}

/**
 * OKR Alignment type based on backend response
 */
export interface OKRAlignment {
  company: number;
  team: number;
  personal: number;
}

/**
 * OKTs Summary type based on backend response
 */
export interface OKTsSummary {
  total: number;
  completed: number;
  inProgress: number;
  notStarted: number;
  progressPercentage: number;
}

/**
 * OKR type based on backend response
 */
export interface OKR {
  id: number;
  title: string;
  description: string;
  status: GoalStatus;
  dueDate: string | null;
  progressPercentage: number;
  category: OKRCategory;
  smartGoalScore: number;
  alignment: OKRAlignment;
  isSuggested: boolean;
  isFinalized: boolean;
  visibility: "PRIVATE" | "PUBLIC";
  evaluationComment: string | null;
  okts: OKT[];
  oktsSummary: OKTsSummary;
}

/**
 * Dashboard Summary response type
 */
export interface DashboardSummaryResponse {
  okrs: OKR[];
}

export interface AchieverMetricsResponse {
  completedTasks: number;
  achievedGoals: number;
  attendedSessions: number;
}

/**
 * Goals dashboard metrics response
 */
export interface GoalsMetrics {
  total_goals: number;
  completed_goals: number;
  total_tasks: number;
  completed_tasks: number;
  overall_progress: number;
}

/**
 * OKR Advisor assignment
 */
export interface OKRAdvisor {
  // v3: backend OKRAdvisorOut sends membership_id (program_members.id), not
  // the legacy pab_board_id. Note this schema is plain snake_case on the
  // wire (not CamelModel-backed), unlike most of the API.
  membership_id: number;
  is_assigned: boolean;
  name: string | null;
  current_title: string | null;
  profile_url: string | null;
  email: string | null;
}

/**
 * OKT Note
 */
export interface OKTNoteUser {
  id: string;
  name: string | null;
  role: string | null;
}

export interface OKTNote {
  id: number;
  okt_id: number;
  content: string;
  created_at: string;
  updated_at: string;
  user: OKTNoteUser | null;
}

export interface SessionOkrAdvisors {
  okr_id: number;
  advisors: OKRAdvisor[];
}

/**
 * Update OKT request body
 */
export interface UpdateOKTRequest {
  title: string;
  description: string;
  status: TaskStatus;
  isSuggested: boolean;
  isFinalized: boolean;
  dueDate: string | null;
  evaluationComment: string | null;
}

/**
 * Update OKR request body
 */
export interface UpdateOKRRequest {
  title: string;
  description: string;
  category: string;
  smartGoalScore: number;
  alignment: {
    [key: string]: number;
  };
  isSuggested: boolean;
  isFinalized: boolean;
  dueDate: string | null;
  progressPercentage: number;
  evaluationComment: string | null;
  visibility: "PRIVATE" | "PUBLIC";
  status: GoalStatus;
}

/**
 * Result of sending (or staging) advisor invitations.
 */
export interface AdvisorInviteResponse {
  message: string;
  invitedCount: number;
  failedCount: number;
  failedEmails?: string[] | null;
  /**
   * True when advisors were recorded on the board but NOT emailed, because the
   * achiever still owes payment. They are contacted only once Stripe confirms
   * the charge. The server decides this — entitlement is never evaluated
   * client-side.
   */
  staged?: boolean;
}

/**
 * Advisor type based on API response
 */
export interface SuggestedAdvisor {
  id: string;
  name: string;
  currentTitle: string;
  currentCompany: string;
  bio: string;
  matchedGoals: string[];
  matchScore: number;
  topMatch: boolean;
  availabilityIssue: boolean;
  yearsOfExperience: string;
  location: string;
  profileUrl: string;
  experienceContext: string[];
  organisationType?: string[];
  pabApprovalStatus?: string;
}

export interface AdvisorInviteSummaryDraft {
  // v3: keyed on the advisor's user_id (SHA256 string).
  advisorUserId: string;
  advisorName: string;
  // Structured components (backend AdvisorInviteSummaryDraft) plus the fully
  // assembled message the UI actually renders.
  greeting?: string;
  bioHighlight?: string;
  whyNow?: string;
  whyYou?: string;
  theWhat?: string;
  cta?: string;
  summaryDraft: string;
}

export interface AchieverInviteSummaryRequest {
  // session_id == program_id in v3.
  sessionId: number;
  // v3: selected advisor user_ids (SHA256 strings), 3–5 entries.
  advisorUserIds: string[];
}

export interface AchieverInviteSummaryResponse {
  achieverUserId: string;
  sessionId: number;
  summaries: AdvisorInviteSummaryDraft[];
  generatedAt: string;
}

/**
 * Paginated response type for advisors
 */
export interface PaginatedAdvisorsResponse {
  items: SuggestedAdvisor[];
  page: number;
  pages: number;
  size: number;
  totalCount: number;
}

/**
 * Selected OKR type based on API response
 */
export interface SelectedOkr {
  title: string;
  description: string;
  pabSessionId: number;
  category: string;
  smartGoalScore: number;
  alignment: Record<string, number>;
  isSuggested: boolean;
  isFinalized: boolean;
  dueDate: string;
  progressPercentage: number;
  evaluationComment: string;
  visibility: string;
  id: number;
  status: string;
}

/**
 * Paginated response type for PAB sessions
 */
export interface PaginatedPabSessionsResponse {
  items: PabSession[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

/**
 * Paginated response type for selected OKRs
 */
export interface PaginatedSelectedOkrsResponse {
  items: SelectedOkr[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

/**
 * Selected advisor type based on API response
 */
export interface SelectedAdvisor {
  // program_invitations.id (legacy int; NOT the advisor's identity).
  id: number;
  // v3: the advisor's identity is user_id (SHA256 string). The backend
  // SelectedAdvisor schema never sends `advisorId` — use `userId` for any
  // matching/lookup/API call. (Several older call sites still read a
  // nonexistent `.advisorId`; those are pre-existing bugs tracked
  // separately, outside the signup/onboarding flows.)
  userId: string;
  name: string;
  currentTitle: string;
  currentCompany: string;
  bio: string;
  matchedGoals: string[];
  matchScore: number;
  isSelected: boolean;
  approvalStatus: string;
  declineReason: string;
  primaryFunction?: string;
  experienceContext?: string[];
  advisoryStyles?: string[];
  profileUrl?: string;
  createdAt?: string;
  updatedAt?: string;
  hasCalendarConnected?: boolean;
}

/**
 * Selected advisors response type
 */
export interface SelectedAdvisorsResponse {
  sessionId: number;
  selectedAdvisors: SelectedAdvisor[];
  totalCount: number;
}

/**
 * Available time slot type for overlapping advisor availability
 */
export interface OverlappingTimeSlot {
  dayOfWeek: string;
  slotStart: string; // Time in advisorTimezone format
  slotEnd: string; // Time in advisorTimezone format
  timezone: string; // Usually "UTC"
  availableAdvisorIds: number[]; // IDs of advisors available in this slot
}

/**
 * Advisors availability response type with overlapping slots
 */
export interface AdvisorsAvailabilityResponse {
  advisorsAvailableSlots: OverlappingTimeSlot[];
  achieverTimezone: string; // UTC offset format (e.g., "UTC+02:00")
  advisorTimezone: string; // Timezone of the slot times (e.g., "UTC")
}

/**
 * Individual advisor time slot
 */
export interface IndividualAdvisorTimeSlot {
  dayOfWeek: string;
  slotStart: string; // Time format (HH:MM:SS)
  slotEnd: string; // Time format (HH:MM:SS)
  timeSlot: string; // Human readable time (e.g., "6 am to 7 am")
  timezone: string; // UTC offset format (e.g., "UTC+02:00")
}

/**
 * Individual advisor availability response type
 */
export interface IndividualAdvisorAvailabilityResponse {
  advisor: {
    advisorId: number;
    advisorName: string;
    timezone: string; // UTC offset format (e.g., "UTC+02:00")
    availableSlots: IndividualAdvisorTimeSlot[];
  };
  achieverTimezone: string; // UTC offset format (e.g., "UTC+02:00")
}

export interface AdvisorProfile {
  fullName?: string;
  currentTitle: string;
  currentCompany: string;
  location: string;
  yearsOfExperience: ExperienceEnum;
  primaryFunction: string;
  currentRoleLevel?: string;
  topGrowthAreas?: string[];
  experienceContext: string[];
  jobRole?: string;
  industry?: string;
  organisationType?: string[];
  functionsAdvised: string[];
  levelsMentored: string[];
  advisoryStyles?: string[];
  coachingStrengths?: string[];
  bio: string;
  profilePictureUrl?: string;
  linkedIn?: string;
  personalWebsite?: string;
  publicSlug?: string | null;
  isPublic?: boolean;
  id: number;
  advisorId: number;
}

export interface PublicAdvisorProfile {
  fullName: string;
  email: string;
  currentTitle?: string;
  currentCompany?: string;
  location?: string;
  yearsOfExperience?: ExperienceEnum;
  primaryFunction?: string;
  currentRoleLevel?: string;
  experienceContext?: string[];
  bio?: string;
  linkedIn?: string;
  personalWebsite?: string;
  profilePictureUrl?: string | null;
}

export interface AdvisorPlatformStats {
  totalSessions: number;
  acceptedPabs: number;
  platformExperience: number; // months
}

export interface AdvisorLeadLookupResponse {
  data: AdvisorFormPreFillData;
}

/**
 * GET /crm-leads/me/expert-prefill. Already normalised by the API: option
 * fields hold only values their dropdown offers, lists are split, goals and
 * years use stored values, and the STAR text is split into stories (max 3).
 */
export interface ExpertSignupPrefill {
  firstName: string | null;
  lastName: string | null;
  jobTitle: string | null;
  companyName: string | null;
  jobRole: string | null;
  industry: string | null;
  location: string | null;
  yearsOfExperience: string | null;
  primaryFunction: string | null;
  currentRoleLevel: string | null;
  linkedIn: string | null;
  personalWebsite: string | null;
  contributionGoals: string[];
  coreIndustries: string[];
  organisationTypes: string[];
  operatorThesis: string | null;
  problemAndInsight: string | null;
  advisoryPhilosophy: string | null;
  starPortfolio: StarPortfolioStory[];
}

export interface AdvisorFormPreFillData {
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  jobTitle: string;
  companyName: string;
  stateRegion: string;
  countryRegion: string;
  yearsOfExperience: string;
  primaryOperatorFunction: string;
  currentRoleLevel: string;
  coreIndustries: string;
  organizationTypes: string;
  operatorThesis: string;
  advisoryPhilosophy: string;
}

/**
 * Message type for PAB session messages
 */
export interface PabMessage {
  id: number;
  senderId: string;
  senderName: string;
  conversationId: number;
  body: string;
  status: string;
  createdAt: string;
  isRead: boolean;
  messageType?: string;
  /** Files sent with this message. The same rows appear in the programme's
   *  Resources tab — an attachment IS a programme resource, not a copy. */
  attachments?: ProgramResource[];
}

/**
 * Messages response type
 */
export type PabMessagesResponse = PabMessage[];

/**
 * Program conversation (group or direct) creation response
 * POST /api/v1/programs/{program_id}/messages/conversation
 */
export interface ProgramConversation {
  id: number;
  programId: number;
  pabSessionId: number | null;
  type: "group" | "direct";
}

/**
 * Send program message request body
 * POST /api/v1/programs/{program_id}/messages
 */
export interface SendProgramMessageRequest {
  attachmentIds?: number[];
  body: string;
  advisor_id?: number | null;
}

export interface Notification {
  id: number;
  notificationType: string;
  isActionable: boolean;
  isRead: boolean;
  title: string;
  body: string;
  actionUrl?: string | null;
  metadataJson?: string | null;
  createdAt: string;
}

export interface NotificationListResponse {
  items: Notification[];
  total: number;
  page: number;
  size: number;
  pages: number;
  unreadCount: number;
}

const API_BASE = getEnvVar("API_BASE");

// User types
export interface User {
  id: string;
  email: string;
  fullName: string;
  mobileNumber: string;
  role: string;
  roles: string[];
  currentRole: string;
  onboardingStep?: string | null;
  wrapupStep?: number | null;
  buildPabStep?: number | null;
  buildExpertStep?: number | null;
  createdAt?: string | null;
  codeOfConductAccepted?: boolean;
  /** Which role's workspace the user is currently viewing the app as (dual-role support). */
  activeWorkspace?: string | null;
  /** May start the Expert application: allowed by an admin, or already an Expert. */
  canApplyAsExpert?: boolean;
  /** "manual" (email magic link) or "linkedin". */
  authProvider?: string | null;
  /** Company part of the Group / Company Code (the backend splits "ORG-TEAM"). */
  organizationCode?: string | null;
  /** Team part of the Group / Company Code, if one was given. */
  teamCode?: string | null;
}

export interface WrapupStep {
  step: number;
}

export interface BuildPabStep {
  step: number;
}

export interface BuildExpertStep {
  step: number;
}

/** Shape returned by GET/PATCH/PUT /capabilities/me -- the Expert Application questionnaire. */
/* ==================== ADMIN PANEL ==================== */

/** fastapi_pagination's envelope, as returned by every /admin list route. */
export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  size: number;
  pages?: number;
}

/** Mirrors CapabilityReviewStatusEnum on the backend. */
export type AdminApplicationStatus =
  | "NOT_SUBMITTED"
  | "SUBMITTED"
  | "PENDING"
  | "COMMITTEE_REVIEW"
  | "APPROVED"
  | "REJECTED";

/** The subset of review statuses a reviewer is allowed to set. */
export type AdminApplicationDecision =
  | "SUBMITTED"
  | "PENDING"
  | "COMMITTEE_REVIEW"
  | "APPROVED"
  | "REJECTED";

export type AdminProgramType = "PAB" | "EXPERT" | "PEER_ADVISORY" | "ORGANISATION";

export type AdminProgramStatus =
  | "DRAFT"
  | "PENDING_REVIEW"
  | "REJECTED"
  | "ACTIVE"
  | "LIVE"
  | "COMPLETED"
  | "ARCHIVED";

/**
 * The only statuses the panel may set directly. Approve/reject has its own
 * endpoint, and LIVE/COMPLETED are owned by the backend's daily lifecycle
 * task, which would revert a hand-set value.
 */
export type AdminSettableProgramStatus = "ACTIVE" | "ARCHIVED";

export interface AdminStats {
  totalUsers: number;
  activeUsers: number;
  pendingApplications: number;
  pendingPrograms: number;
  livePrograms: number;
  activePabs: number;
  activeExpertPrograms: number;
}

/** How a cohort member's enrolment was funded. */
export type AdminPaymentMode = "CARD" | "ORGANISATION" | "NONE";

export interface AdminRevenueMember {
  userId: string;
  fullName: string;
  email: string;
  role: string;
  /** Net of refunds. 0 when there is no settled payment. */
  amountCents: number;
  currency: string;
  paymentStatus: string | null;
  paymentMode: AdminPaymentMode;
  /** Only set when paymentMode is ORGANISATION. */
  sponsorshipCode: string | null;
  sponsoringCompanyName: string | null;
  couponCode: string | null;
  discountCents: number;
  paidAt: string | null;
}

export interface AdminRevenueRow {
  programId: number;
  externalId: string | null;
  name: string;
  programType: AdminProgramType;
  status: string;
  ownerName: string | null;
  /** Catalogue price. 0 when no price plan resolves — shown as $0, not hidden. */
  listPriceCents: number;
  currency: string;
  memberCount: number;
  collectedCents: number;
  members: AdminRevenueMember[];
}

export interface AdminRevenueSummary {
  totalCollectedCents: number;
  expertCollectedCents: number;
  pabCollectedCents: number;
  payingMemberCount: number;
  organisationPaidCount: number;
  programCount: number;
  pabCount: number;
}

export interface AdminRevenue {
  summary: AdminRevenueSummary;
  programs: AdminRevenueRow[];
  pabs: AdminRevenueRow[];
}

export type AdminUserStatus = "ACTIVE" | "UNVERIFIED" | "DEACTIVATED";

export interface AdminUserListParams {
  page?: number;
  size?: number;
  search?: string;
  role?: string;
  isActive?: boolean;
  /**
   * ISO instant; only users who signed up at or after it. The Joined filter
   * computes this from the admin's own clock, so "Today" means their calendar
   * day rather than UTC's — see JOINED_FILTERS in UsersTab.
   */
  createdAfter?: string;
}

export interface AdminUserRow {
  id: string;
  fullName: string;
  email: string;
  /** Every role from user_role_map, e.g. ["achiever", "super_admin"]. */
  roles: string[];
  primaryRole: string;
  isActive: boolean;
  /**
   * Derived server-side, because isActive=false covers three cases:
   *   ACTIVE      -- verified and usable
   *   UNVERIFIED  -- signed up, never confirmed their email
   *   DEACTIVATED -- was active, then self-deleted or suspended here
   */
  status: AdminUserStatus;
  currentTitle?: string | null;
  currentCompany?: string | null;
  createdAt?: string | null;
  lastLoggedIn?: string | null;
  programCount: number;
  /**
   * How many accounts signed up through this person's referral link, split by
   * the role they actually chose. Counted from who JOINED, not from who was
   * named in the referral modal — the link is generic, so people arrive
   * through it who were never named, and they still count.
   */
  referredAchieversCount: number;
  referredXosCount: number;
  /** Who referred this account (the other direction from the counts above). */
  referredByUserId?: string | null;
  referredByName?: string | null;
  /** The Getting Started LinkedIn carousel PDF has been uploaded for this user. */
  hasLinkedinCarousel: boolean;
  linkedinCarouselUploadedAt?: string | null;
  /** An admin has allowed this user to apply as an Expert. Experts are always allowed. */
  canApplyAsExpert: boolean;
}

export interface AdminLinkedinCarousel {
  userId: string;
  hasLinkedinCarousel: boolean;
  linkedinCarouselUploadedAt?: string | null;
}

export interface LinkedinCarouselStatus {
  available: boolean;
  uploadedAt?: string | null;
}

export interface AdminReferredUser {
  id: string;
  fullName: string;
  email: string;
  primaryRole: string;
  joinedAt?: string | null;
}

export interface AdminPendingReferral {
  name: string;
  email: string;
  linkedinUrl?: string | null;
  kind: string;
}

export interface AdminUserProgram {
  externalId: string;
  name: string;
  programType: AdminProgramType;
  status: AdminProgramStatus;
  membershipRole?: string | null;
}

export interface AdminUserDetail extends AdminUserRow {
  location?: string | null;
  bio?: string | null;
  linkedIn?: string | null;
  personalWebsite?: string | null;
  onboardingStep?: string | null;
  codeOfConductAccepted: boolean;
  authProvider?: string | null;
  /** null when the user never started an Expert application. */
  applicationStatus?: AdminApplicationStatus | null;
  programs: AdminUserProgram[];
  /** Accounts that joined through this person's link — what the counts count. */
  referredUsers: AdminReferredUser[];
  /** People they named who have not joined. A different fact from the above. */
  pendingReferrals: AdminPendingReferral[];
}

export interface AdminApplicationListParams {
  page?: number;
  size?: number;
  reviewStatus?: AdminApplicationStatus;
  source?: string;
}

export interface AdminApplicationRow {
  userId: string;
  fullName: string;
  email: string;
  currentTitle?: string | null;
  currentCompany?: string | null;
  reviewStatus: AdminApplicationStatus;
  /** The review step the application was at when rejected; null otherwise. */
  rejectedAtStatus?: AdminApplicationStatus | null;
  submittedAt?: string | null;
  reviewedAt?: string | null;
  reviewNotes?: string | null;
  /**
   * The admin who decided this application. Null while it is still awaiting a
   * decision, and on applications decided before the reviewer was being
   * recorded.
   */
  reviewedByUserId?: string | null;
  reviewedByName?: string | null;
  /** "Existing Achiever" or "New Sign-up" — derived, not stored. */
  source: string;
  contributionTypes: string[];
  coreIndustries: string[];
}

export interface AdminApplicationDetailOut extends AdminApplicationRow {
  linkedIn?: string | null;
  personalWebsite?: string | null;
  yearsOfExperience?: string | null;
  bio?: string | null;
  operatorThesis?: string | null;
  problemAndInsight?: string | null;
  advisoryPhilosophy?: string | null;
  engagementStructure?: string | null;
  rate?: string | null;
  advisoryAccomplishments?: string | null;
  /** Structured stories, or the free text written before the form had fields. */
  starPortfolio: StarPortfolioEntry[];
  coachingStrengths: string[];
  advisoryStyles: string[];
  functionsAdvised: string[];
  levelsMentored: string[];
  advisoryScenarios: { title: string; description: string }[];
  contributionGoals: string[];
  /** Reviewer verdict per Primary Goal; a goal with no entry is undecided. */
  contributionGoalDecisions: Partial<Record<string, ContributionGoalDecision>>;
  interestMultiAdvisorBoards?: boolean | null;
  wantsAdvisorMatching: boolean;
  /**
   * The contribution flags, raw. Each role is decided separately, so the
   * review drawer needs the individual booleans. A false flag was either never
   * applied for or has been rejected — both mean "not qualified".
   */
  createLeadPrograms: boolean;
  joinAsCoExpert: boolean;
  joinPabBoard: boolean;
  peerAdvisory: boolean;
  coFounding: boolean;
  createLeadPeerPrograms: boolean;
  createLeadOrganisationPrograms: boolean;
  infoAccuracyConfirmed: boolean;
  privacyPolicyAcknowledged: boolean;
  applicationReviewConsented: boolean;
  profileUseConsented: boolean;
  profileBuildConsented: boolean;
  consentsAt?: string | null;
}

/**
 * FORMING  -- fewer than three Experts have accepted their board seat
 * STALLED  -- board complete but no kickoff scheduled 5+ weeks on
 * AT_RISK  -- past the halfway week with goal progress under 40%
 * ON_TRACK -- otherwise
 */
export type AdminBoardHealth = "FORMING" | "STALLED" | "ON_TRACK" | "AT_RISK";

export interface AdminBoardAdvisor {
  userId?: string | null;
  fullName?: string | null;
  currentTitle?: string | null;
  currentCompany?: string | null;
  membershipStatus?: string | null;
}

export interface AdminAdvisoryBoardListParams {
  page?: number;
  size?: number;
  health?: AdminBoardHealth;
  search?: string;
}

/**
 * One PAB in the Advisory Boards tab. Almost everything past the identity
 * fields is aggregated server-side (sessions from meetings, goals from okrs,
 * satisfaction from feedback ratings) and `health` is derived from progress
 * against elapsed time — none of it is stored.
 */
export interface AdminAdvisoryBoardRow {
  externalId: string;
  name: string;
  status: AdminProgramStatus;
  achieverUserId?: string | null;
  achieverName?: string | null;
  achieverEmail?: string | null;
  formedOn?: string | null;
  endsOn?: string | null;
  durationWeeks?: number | null;
  currentWeek?: number | null;
  advisorCount: number;
  sessionsTotal: number;
  sessionsCompleted: number;
  goalsTotal: number;
  goalsAchieved: number;
  nextSessionAt?: string | null;
  lastActivityAt?: string | null;
  satisfactionScore?: number | null;
  /** Accepted board seats out of the three a PAB expects. */
  acceptedAdvisors: number;
  expectedAdvisors: number;
  /** First GROUP session with a time on it; null means no kickoff scheduled. */
  kickoffAt?: string | null;
  /** Mean OKR progress, falling back to the achieved share. Drives At Risk. */
  goalProgressPct?: number | null;
  health: AdminBoardHealth;
}

export interface AdminAdvisoryBoardDetail extends AdminAdvisoryBoardRow {
  advisors: AdminBoardAdvisor[];
}

export interface AdminSessionListParams {
  page?: number;
  size?: number;
  /** "upcoming" (SCHEDULED + ONGOING) | "completed" | "cancelled" */
  status?: AdminSessionStatusFilter;
  programType?: AdminProgramType;
  search?: string;
}

export type AdminSessionStatusFilter = "upcoming" | "completed" | "cancelled";

export interface AdminSessionRow {
  id: number;
  title: string;
  /** Raw MeetingStatus: SCHEDULED | ONGOING | COMPLETED | CANCELLED */
  status?: string | null;
  meetingFormat?: string | null;
  startTime?: string | null;
  endTime?: string | null;
  timezone?: string | null;
  programExternalId?: string | null;
  programName?: string | null;
  programType?: AdminProgramType | null;
  facilitatorName?: string | null;
  participantCount: number;
}

export interface AdminProgramListParams {
  page?: number;
  size?: number;
  /**
   * One type, or several. The program tabs ask for EXPERT and PEER_ADVISORY
   * together -- they share a list and are told apart by their label -- while
   * PABs keep their own tab. `listPrograms` joins an array with commas, which
   * is the shape the API parses; see the note there.
   */
  programType?: AdminProgramType | AdminProgramType[];
  status?: AdminProgramStatus;
  publishedOnly?: boolean;
  search?: string;
}

export interface AdminProgramRow {
  externalId: string;
  name: string;
  description?: string | null;
  programType: AdminProgramType;
  status: AdminProgramStatus;
  ownerUserId?: string | null;
  ownerName?: string | null;
  ownerEmail?: string | null;
  startsOn?: string | null;
  endsOn?: string | null;
  submittedAt?: string | null;
  reviewedAt?: string | null;
  rejectionReason?: string | null;
  /**
   * Who last decided this program.
   *
   * NOT always an admin: the lead expert's own publish endpoint stamps the
   * same column, so on a self-published program this is the owner. Compare
   * with `ownerUserId` before calling it an approval — `programReviewer()` in
   * adminFormat does exactly that.
   */
  reviewedByUserId?: string | null;
  reviewedByName?: string | null;
  /** Active achiever memberships. There is no capacity column to divide by. */
  enrolled: number;
  createdAt?: string | null;
}

export interface AdminProgramMember {
  membershipId: number;
  userId?: string | null;
  fullName?: string | null;
  email?: string | null;
  role?: string | null;
  status?: string | null;
}

export interface AdminProgramSession {
  id: number;
  title: string;
  description?: string | null;
  sessionFormat?: string | null;
  startTime?: string | null;
  isTemplate: boolean;
  /**
   * Which week of the program the session falls in, derived from its offset
   * from the program start — there is no week column. Null when either date is
   * missing, so the UI must fall back to plain ordering.
   */
  weekNumber?: number | null;
}

export interface AdminProgramDetail extends AdminProgramRow {
  members: AdminProgramMember[];
  sessionCount: number;
  sessions: AdminProgramSession[];

  /** The create-program wizard's Overview answers (on expert_programs). */
  durationWeeks?: number | null;
  /** Single session / 1-4 days; take precedence over durationWeeks when set. */
  durationDays?: number | null;
  sessionMode?: ExpertSessionMode | null;
  /** Enrolment cap (expert_programs.max_slots). */
  maxSlots?: number | null;
  weeklyTimeCommitment?: string | null;
  whoIsThisProgramFor?: string | null;
  programOutcomes?: string | null;
  prerequisites?: string | null;

  /** Active price plan, if one is set. */
  priceAmountCents?: number | null;
  priceCurrency?: string | null;
  priceDisplayName?: string | null;
}

/** A reviewer's verdict on one "Primary Goal for Joining". */
export type ContributionGoalDecision = "APPROVED" | "REJECTED";

export interface UserCapabilitiesOut {
  id: string;
  userId: string;
  contributionGoals?: string[] | null;
  wantsAdvisorMatching: boolean;
  problemAndInsight?: string | null;
  /**
   * 1-3 S.T.A.R. stories. Structured (four parts) since the signup and
   * application forms were split into fields; entries saved before that are
   * still plain strings and are never parsed -- see @/utils/starPortfolio.
   */
  starPortfolio?: StarPortfolioEntry[] | null;
  advisoryPhilosophy?: string | null;
  engagementStructure?: string | null;
  rate?: string | null;
  coachingStrengths?: string[] | null;
  advisoryStyles?: string[] | null;
  functionsAdvised?: string[] | null;
  levelsMentored?: string[] | null;
  advisoryScenarios?: { title: string; description: string }[] | null;
  advisoryAccomplishments?: string | null;
  interestMultiAdvisorBoards?: boolean | null;
  coreIndustries?: string[] | null;
  operatorThesis?: string | null;
  createLeadPrograms: boolean;
  joinAsCoExpert: boolean;
  joinPabBoard: boolean;
  peerAdvisory: boolean;
  coFounding: boolean;
  createLeadPeerPrograms: boolean;
  createLeadOrganisationPrograms: boolean;
  contributionGoalDecisions?: Partial<Record<string, ContributionGoalDecision>> | null;
  infoAccuracyConfirmed: boolean;
  privacyPolicyAcknowledged: boolean;
  applicationReviewConsented: boolean;
  profileUseConsented: boolean;
  profileBuildConsented: boolean;
  consentsAt?: string | null;
  reviewStatus:
    | "NOT_SUBMITTED"
    | "SUBMITTED"
    | "PENDING"
    | "COMMITTEE_REVIEW"
    | "APPROVED"
    | "REJECTED";
  rejectedAtStatus?: AdminApplicationStatus | null;
  submittedAt?: string | null;
  reviewedAt?: string | null;
  reviewNotes?: string | null;
}

/**
 * The XO Getting Started journey's whole server-side state.
 *
 * `completed` holds task keys, not indexes -- the step set changes shape
 * between releases (a step has already been removed and another made
 * optional), and positional state would silently re-point at the wrong step.
 */
export interface GettingStartedStateOut {
  completed: string[];
  welcomed: boolean;
}

/** A portfolio row from the `stars` table — what the Program Coach picker lists. */
export interface PortfolioStarOut {
  id: number;
  position: number | null;
  title: string | null;
  situation: string;
  task: string;
  action: string;
  result: string;
}

/** The Program Coach prompt's blanks, filled from one S.T.A.R. by the LLM. */
export interface ProgramCoachPromptOut {
  starId: number;
  problem: string;
  audience: string;
  whyMe: string;
  weeklyCommitment: string;
}

/**
 * Whether the calibration step can be booked.
 *
 * Three states the UI must keep apart: not configured, configured but the
 * host has no connected calendar, and ready. "Ready with no free slots" is a
 * fourth and is Nylas's to report.
 */
export interface CalibrationHostStatusOut {
  configured: boolean;
  hostConnected: boolean;
  hostUserId: string | null;
  hostName: string | null;
}

/** One person an expert has put forward by name. */
export interface ReferralOut {
  id: number;
  kind: "ACHIEVER" | "XO";
  name: string;
  email: string;
  linkedinUrl?: string | null;
  createdAt?: string | null;
  /** True once someone signed up with a matching address. */
  converted: boolean;
}

/**
 * The referral modal's whole state. One signup link per audience — achiever
 * signup and Expert signup — each carrying the referrer's `ref`.
 */
export interface ReferralsOut {
  achievers: ReferralOut[];
  xos: ReferralOut[];
  achieverShareLink: string;
  xoShareLink: string;
}

/** A booked calibration, stored as a Meeting with no programme. */
export interface CalibrationBookingOut {
  meetingId: number;
  startTime?: string | null;
  endTime?: string | null;
  meetingUrl?: string | null;
  bookingRef?: string | null;
}

/** Partial, in-progress save for the Expert Application wizard -- every field optional. */
export type UserCapabilitiesDraftUpdate = Partial<{
  contributionGoals: string[];
  problemAndInsight: string;
  /** Both shapes: re-sending a legacy string is how it survives a save. */
  starPortfolio: StarPortfolioEntry[];
  advisoryPhilosophy: string;
  engagementStructure: string;
  rate: string;
  coachingStrengths: string[];
  advisoryStyles: string[];
  functionsAdvised: string[];
  levelsMentored: string[];
  advisoryScenarios: { title: string; description: string }[];
  advisoryAccomplishments: string;
  interestMultiAdvisorBoards: boolean;
  coreIndustries: string[];
  operatorThesis: string;
  createLeadPrograms: boolean;
  joinAsCoExpert: boolean;
  joinPabBoard: boolean;
  peerAdvisory: boolean;
  coFounding: boolean;
  createLeadPeerPrograms: boolean;
  createLeadOrganisationPrograms: boolean;
}>;

/** Final "Review & Submit" payload -- PUT /capabilities/me. */
export interface UserCapabilitiesSubmit {
  contributionGoals?: string[];
  problemAndInsight: string;
  starPortfolio: StarPortfolioStory[];
  advisoryPhilosophy: string;
  engagementStructure: string;
  rate?: string;
  coachingStrengths?: string[];
  advisoryStyles?: string[];
  functionsAdvised?: string[];
  levelsMentored?: string[];
  advisoryScenarios?: { title: string; description: string }[];
  advisoryAccomplishments?: string;
  interestMultiAdvisorBoards?: boolean;
  infoAccuracyConfirmed: true;
  privacyPolicyAcknowledged: true;
  applicationReviewConsented: true;
  profileUseConsented: true;
  profileBuildConsented: true;
}

// Dashboard types
export interface DashboardStats {
  totalUsers: number;
  activeUsers: number;
  totalProjects: number;
  completedProjects: number;
}

export interface Project {
  id: string;
  name: string;
  status: "planning" | "active" | "completed" | "cancelled";
  progress: number;
  dueDate: string;
}

export interface Achiever {
  // v3: achiever identity IS the user. `id` is the SHA256 string user_id
  // returned by GET /achievers/me (AchieverAccountOut { id }). There is no
  // separate achievers-table int PK anymore.
  id: string;
  userId?: string;
}

export type ProgramStatus =
  | "DRAFT"
  | "PENDING_REVIEW"
  | "REJECTED"
  | "ACTIVE"
  | "LIVE"
  | "COMPLETED"
  | "ARCHIVED";
/**
 * LEAD is shared by every program type. CO_ADVISOR/ACHIEVER are the Expert
 * Program pair; FACILITATOR/PEER are the Peer Advisory pair. An Organisation
 * program reuses LEAD (founder) + FACILITATOR and adds MEMBER (its team pool).
 */
export type MembershipRole = "LEAD" | "CO_ADVISOR" | "ACHIEVER" | "FACILITATOR" | "PEER" | "MEMBER";
export type MembershipStatus = "PENDING" | "ACTIVE" | "REMOVED" | "COMPLETED";
export type OnboardingStep = "pending" | "intro_seen" | "goals_seen" | "complete";

/**
 * A single advisor as returned on a ProgramSummary row -- either a PAB board
 * advisor (selected via the matching funnel) or an Expert Program lead/
 * co-advisor.
 *
 * `status` carries whichever underlying status applies: for PAB advisors,
 * the invitation's approval status ("invited" | "accepted" | "rejected" |
 * "withdrawn" | "expired" | "suggested"); for Expert lead/co-advisors, the
 * membership status ("PENDING" | "ACTIVE") -- PENDING means invited but not
 * yet accepted.
 */
export interface ProgramAdvisorSummary {
  userId?: string | null;
  name: string;
  currentRole?: string | null;
  currentCompany?: string | null;
  profilePictureUrl?: string | null;
  status?: string | null;
  /** PAB advisors only: when the invite was sent. */
  invitedAt?: string | null;
}

export interface ProgramSummary {
  id: number;
  externalId: string;
  name: string;
  status: ProgramStatus;
  /**
   * PEER_ADVISORY belongs here too: GET /programs is type-agnostic and has
   * always returned peer memberships (program_composer has an explicit branch
   * for them), so omitting it silently dropped an accepted peer's own group
   * from every consumer that filters on this.
   */
  programType: "PAB" | "EXPERT" | "PEER_ADVISORY" | "ORGANISATION";
  role?: MembershipRole | null;
  membershipStatus?: MembershipStatus | null;
  onboardingStep?: OnboardingStep | null;
  lastViewedAt?: string | null;
  enrolledAt?: string | null;
  /** PAB: Program.starts_on/ends_on. EXPERT: ExpertProgram.starts_at/ends_at. */
  startsAt?: string | null;
  endsAt?: string | null;
  /** First / last scheduled GROUP session for this program ("kickoff" / "wrap-up"). */
  kickoffScheduledDate?: string | null;
  wrapupScheduledDate?: string | null;
  /** End time of the wrap-up (last group) session. Program has ended once this passes. */
  wrapupEndsAt?: string | null;
  /** PAB only: the achiever's selected board, each with their invitation status. */
  advisors?: ProgramAdvisorSummary[] | null;
  /**
   * PAB only, pre-derived server-side from `advisors` / `kickoffScheduledDate`
   * above (see program_composer.py). `isPabForming` is true until every
   * selected advisor has accepted (an empty board counts as forming);
   * `isKickoffScheduled` is true once the first GROUP session has a start_time.
   */
  isPabForming?: boolean | null;
  isKickoffScheduled?: boolean | null;
  /** EXPERT only. */
  leadAdvisor?: ProgramAdvisorSummary | null;
  coAdvisors?: ProgramAdvisorSummary[] | null;
  /**
   * Everything a program card renders, so a list of cards needs only this
   * response. The advisor home used to call getExpertProgram once per active
   * program purely for these — one request per card — even though the list was
   * already loaded. Counts match the detail endpoint exactly (verified), so a
   * card shows the same numbers whichever response fed it.
   */
  description?: string | null;
  slug?: string | null;
  durationWeeks?: number | null;
  membersCount?: number;
  sessionsCount?: number;
  okrsCount?: number;
}

export interface ProgramListResponse {
  programs: ProgramSummary[];
  total: number;
}

export interface AchieverFtuxStatus {
  /**
   * Bitmask of completed steps:
   *   bit 0 (1)  → Step 1: Code of Conduct
   *   bit 1 (2)  → Step 2: Review Goals
   *   bit 2 (4)  → Step 3: Improve Profile
   *   bit 3 (8)  → Step 4: Write Questions
   *   All complete = 15 (0b1111)
   *
   * Check a step: (ftuxStep & stepFlag) !== 0
   * Count done:   [1,2,4,8].filter(b => ftuxStep & b).length
   */
  ftuxStep: number;
  codeOfConductAccepted: boolean;
  kickoffQuestions: string | null;
}

/**
 * PAB Session type based on API response
 */
export interface PabSession {
  id: number;
  sessionIdentifier: string;
  /** The PAB's Program.name, e.g. a user-chosen name or "{FirstName}'s PAB". */
  name?: string | null;
  status: string;
  // v3: the achiever is identified by their user_id (SHA256 string), served
  // as `achieverUserId` by PabSessionOut. (Was the legacy int `achieverId`.)
  achieverUserId: string | null;
  companyId: number;
  activeFrom?: string;
  activeTill?: string;
  workingStatus: string;
}

export interface WeeklyPlanMeetingParticipant {
  name: string;
  profileUrl?: string | null;
}

export interface WeeklyPlanTask {
  updatedAt: undefined;
  id: number;
  okrId: number;
  okrTitle?: string | null;
  okrStatus?: string | null;
  title: string;
  status: string;
  dueDate: string | null;
}

export interface WeeklyPlanMeeting {
  id: number;
  title: string;
  groupSession?: boolean | null;
  meetingFormat?: string | null;
  status?: string | null;
  startTime: string;
  endTime: string;
  timezone?: string | null;
  meetingUrl?: string | null;
  participants?: WeeklyPlanMeetingParticipant[];
}

export interface WeeklyPlanWeekOKR {
  id: number;
  title: string;
  status: string;
  updatedAt: string;
  progressPercentage: number;
}

export interface WeeklyPlanWeek {
  weekNumber: number;
  startDate: string;
  endDate: string;
  tasks: WeeklyPlanTask[];
  meetings: WeeklyPlanMeeting[];
  okrs: WeeklyPlanWeekOKR[];
}

export interface WeeklyPlanResponse {
  sessionId: number;
  activeFrom: string;
  activeTill: string;
  kickoffDate: string | null;
  wrapupStatus: string | null;
  wrapupDate: string | null;
  weeks: WeeklyPlanWeek[];
}

export interface TimelineMilestone {
  meetingId: number;
  weekNumber: number;
  label: string;
  startTime: string;
}

export interface TimelineMilestonesResponse {
  sessionId: number;
  milestones: TimelineMilestone[];
}

export interface OnboardingStatus {
  currentStep: number; // 1, 2, 3, 4
  inviteCount: number; // Number of invites sent
  acceptedCount: number; // Number of advisors who accepted
  totalCount: number; // Total advisors selected/invited
  meetingsScheduled: number; // Number of group meetings scheduled
}

export interface Event {
  id: number;
  title: string;
  description: string;
  date: string;
  time: string;
  color: string;
}

export interface SessionSchedule {
  sessionId: number;
  date: string;
  times: string[];
  advisorIds: string[];
  meetingLink?: string;
}

export interface MeetingSlot {
  title: string;
  description: string;
  startTime: string;
  endTime: string;
  timezone: string;
  meetingFormat: string;
  advisorIds: string[];
}

export interface UpcomingMeeting {
  title: string;
  description: string;
  meetingType: string;
  meetingFormat: string;
  meetingUrl: string;
  meetingCode: string;
  startTime: string;
  endTime: string;
  timezone: string;
  platformMeetingId: string;
  calendarEventId: string;
  /** Set only for meetings booked via the Nylas Scheduler widget (the kickoff).
   *  Week 6 / week 12 are created through the Events API and have no booking,
   *  so these are null and the Reschedule action is hidden for them. */
  nylasBookingId: string | null;
  nylasBookingRef: string | null;
  recordingUrl: string;
  id: number;
  pabSessionId: number;
  status: string;
  participants: MeetingParticipant[];
  createdAt: string;
  updatedAt: string;
}

/** One of the three mandatory PAB group sessions, as returned by getGroupSessions. */
export interface PabGroupSessionMeeting {
  id: number;
  title: string;
  description: string | null;
  startTime: string | null;
  endTime: string | null;
  timezone: string | null;
  meetingUrl: string | null;
  calendarEventId: string | null;
  nylasBookingRef: string | null;
}

export interface PabGroupSessionSlot {
  /** 1 = Kick-off, 2 = Mid-PAB (week 6), 3 = Wrap-up (week 12). */
  sessionOrder: number;
  title: string;
  description: string | null;
  /** 1, 6 or 12 — the PAB week this session belongs to. */
  weekNumber: number;
  isScheduled: boolean;
  /** False until the kick-off anchors this slot's dates, or once its week has
   *  already passed — a picker over a past window offers nothing. */
  canSchedule: boolean;
  /** True when this slot's week is entirely in the past, so it can no longer be
   *  booked. Only possible on older PABs whose kick-off is far enough back. */
  windowHasPassed: boolean;
  /** False inside the cut-off before the session starts, or when it has no
   *  Nylas booking reference to move. */
  canReschedule: boolean;
  /** Hours before start after which rescheduling is locked. */
  rescheduleCutoffHours: number;
  /** True for the kick-off: moving it clears Mid-PAB and Wrap-up, whose weeks
   *  are measured from it. The UI warns before that happens. */
  rescheduleClearsLaterSessions: boolean;
  /** Bookable window; null for the kick-off, which is unrestricted. */
  windowStart: string | null;
  windowEnd: string | null;
  meeting: PabGroupSessionMeeting | null;
}

export interface PabGroupSessionsState {
  sessionId: number;
  /** Group sessions (excluding the kick-off) cleared if the kick-off moves. */
  dependentGroupCount: number;
  /** 1:1 sessions cleared if the kick-off moves — their bookable range runs
   *  kick-off → wrap-up, so they're invalidated too. */
  dependentOneOnOneCount: number;
  sessions: PabGroupSessionSlot[];
  scheduledCount: number;
  requiredCount: number;
  /** True once all three sessions exist. */
  allScheduled: boolean;
}

export interface UpcomingMeetingsResponse {
  items: UpcomingMeeting[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export interface PastMeeting {
  id: number;
  title: string;
  description: string;
  meetingType: string;
  meetingFormat: string;
  meetingUrl: string;
  meetingCode: string;
  startTime: string;
  endTime: string;
  timezone: string;
  platformMeetingId: string;
  calendarEventId: string;
  recordingUrl: string;
  pabSessionId: number;
  status: string;
  participants: MeetingParticipant[];
  summary: string;
  keyTakeaways: string;
  actionItems: string;
}

export interface PastMeetingsResponse {
  items: PastMeeting[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

/**
 * Combined meeting type for meetings by format endpoint
 */
export interface MeetingByFormat {
  id: number;
  title: string;
  description: string;
  meetingType: string;
  meetingFormat: string;
  meetingUrl: string;
  meetingCode: string;
  startTime: string;
  endTime: string;
  timezone: string;
  platformMeetingId: string;
  calendarEventId: string;
  recordingUrl: string;
  pabSessionId: number;
  status: string;
  participants: MeetingParticipant[];
  summary?: string;
  keyTakeaways?: string;
  actionItems?: string;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Response type for meetings by format
 */
export interface MeetingsByFormatResponse {
  items: MeetingByFormat[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export interface MeetingParticipant {
  profileUrl: string;
  role: "ACHIEVER" | "ADVISOR";
  email: string;
  fullName: string;
  isRequired: boolean;
  notes: string;
  id: number;
  meetingId: number;
  userId: string;
  status: "INVITED" | "ACCEPTED" | "DECLINED";
  invitedAt: string;
  respondedAt: string | null;
  attendedAt: string | null;
  leftAt: string | null;
  platformParticipantId: string | null;
  platformEmail: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdvisorMeeting {
  title: string;
  description: string;
  meetingType: "GOOGLE_MEET" | string;
  meetingUrl: string;
  meetingCode: string | null;
  startTime: string;
  endTime: string;
  timezone: string;
  platformMeetingId: string;
  calendarEventId: string | null;
  recordingUrl: string | null;
  id: number;
  /**
   * The program this meeting belongs to. In v3 a PAB *is* a program, so this is
   * what used to be called the "PAB session id" -- the advisor meeting
   * endpoints serialize MeetingOut, which sends `programId` and no
   * `pabSessionId`. Anything needing the PAB id should read this.
   */
  programId: number | null;
  status: "SCHEDULED" | "COMPLETED" | "CANCELLED";
  participants: MeetingParticipant[];
  participantSummary: string | null;
  createdAt: string;
  updatedAt: string;
  meetingFormat: string;
  meetingAgendas: MeetingAgenda[];
  sessionPrepGoals: string[];
}

export interface AdvisorMeetingUpdate {
  meetingAgendas: MeetingAgenda[];
  sessionPrepGoals: string[];
}

export interface MeetingMember {
  name: string;
  profilePictureUrl: string | null;
}

export interface AdvisorMeetingTodo {
  id: number;
  meetingId: number;
  assigneeUserId: string | null;
  content: string;
  isCompleted: boolean;
  createdAt: string;
  updatedAt: string;
  meetingTitle: string | null;
  meetingDate: string | null;
  pabSessionId: number | null;
  members: MeetingMember[];
}

export interface MeetingAgenda {
  description: string;
  duration: number;
}

/**
 * Meeting document type based on API response
 */
export interface MeetingDocument {
  fileUrl: string;
  meetingId: number;
  attachmentUrls: string[];
}
export interface MeetingRecommendation {
  startTime: string;
  endTime: string;
  dayOfWeek: string;
  timeSlot: string;
  availableAdvisorsCount: number;
  availableAdvisors: number[];
  conflictScore: number;
  recommendationScore: number;
}

export interface AdvisorAvailability {
  advisorId: number;
  advisorName: string;
  availableSlots: {
    startTime: string;
    endTime: string;
    dayOfWeek: string;
    timeSlot: string;
  }[];
}

export interface AchieverAvailability {
  startTime: string;
  endTime: string;
  dayOfWeek: string;
  timeSlot: string;
}

export interface MeetingRecommendationsResponse {
  sessionId: number;
  achieverAvailability: AchieverAvailability[];
  advisorAvailability: AdvisorAvailability[];
  recommendations: MeetingRecommendation[];
  totalAdvisors: number;
  availableAdvisorsCount: number;
  recommendationNotes: string;
}

export interface Meeting {
  title: string;
  description: string;
  meetingType: "GOOGLE_MEET";
  meetingUrl: string;
  meetingCode: string;
  startTime: string;
  endTime: string;
  timezone: string;
  platformMeetingId: string;
  calendarEventId: string;
  recordingUrl: string;
}

export interface SummaryCard {
  title: string;
  value: number;
  description: string;
}

/**
 * Profile-rich achiever info as embedded on a PAB card / dashboard card
 * (backend AchieverComprehensiveOut). Distinct from the `Achiever` identity
 * type: here `id` is the legacy int PK the card still carries, and `userId`
 * is the v3 SHA256 string identity. Prefer `userId` for any API call.
 */
export interface AchieverCardInfo {
  id: number;
  userId?: string | null;
  fullName: string;
  email: string;
  currentRole: string | null;
  currentCompany: string | null;
  location: string | null;
  currentRoleLevel: string | null;
  // Profile fields
  bio: string | null;
  profilePictureUrl: string | null;
  yearsOfExperience: string | null;
  linkedIn: string | null;
  personalWebsite: string | null;
  primaryFunction: string | null;
  organisationType: string[] | null;
  topGrowthAreas: string[] | null;
  goalsAndChallenges: string | null;
}

export interface AchieverQuestionOut {
  id: number;
  question: string;
  value: string | null;
  leftLabel: string;
  rightLabel: string;
}

export interface AchieverQuestionUpdate {
  value: string;
}

export interface Advisor {
  id: number;
  userId?: string | null;
  name: string;
  email: string;
  title: string;
  company: string;
  bio: string;
  isCurrentAdvisor: boolean;
  approvalStatus: "suggested" | "approved" | "declined";
  isSelected: boolean;
}

export interface PABCard {
  pabSessionId: number;
  sessionIdentifier: string;
  /** The PAB's Program.name, e.g. a user-chosen name or "{FirstName}'s PAB". */
  name?: string | null;
  status: string;
  createdAt: string;
  achiever: AchieverCardInfo;
  advisors: Advisor[];
  hasGoals: boolean;
  hasMessages: boolean;
  totalAdvisors: number;
  pabApprovalStatus: string;
  inviteMessage?: string | null;
  /** When this expert's invitation arrived, and when it lapses. */
  invitedAt?: string | null;
  expiresAt?: string | null;
  /** Goal categories the matcher picked this expert for ("Why you"). */
  matchedGoals?: string[];
}

export interface GoalRow {
  // v3: the achiever is identified by their user_id string (DashboardGoalRow
  // sends `achieverUserId`); was the legacy int `achieverId`.
  achieverUserId: string | null;
  achieverName: string;
  goalTitle: string;
  dueDate: string;
  progress: number;
  tasksCompleted: number;
  totalTasks: number;
  actions: string[];
}

export interface DashboardSummaryResponse {
  summaryCards: SummaryCard[];
  pabCards: PABCard[];
  goalRows: GoalRow[];
  lastUpdated: string;
}

export interface DashboardValueStats {
  summaryCards: Record<string, number>;
}

export interface AdvisorSummaryCard {
  title: string;
  value: number;
  description: string;
}

export interface AdvisorDashboardSummaryResponse {
  summaryCards: AdvisorSummaryCard[];
  pabCards: PABCard[];
  goalRows: GoalRow[];
  lastUpdated: string;
}

export interface AdvisorDashboardValueStats {
  summaryCards: Record<string, number>;
}

export interface OKTStatusSummaryForAdvisor {
  total: number;
  completed: number;
}

export interface OKRSummaryForAdvisor {
  id: number;
  title: string;
  status: string;
  oktSummary: OKTStatusSummaryForAdvisor;
}

export interface AdvisorStarMoment {
  pabSessionId: number;
  okrId: number;
  goalTitle: string;
  achievedAt: string | null;
  achieverUserId: string;
  achieverName: string;
  achieverProfilePictureUrl: string | null;
}

export interface AdvisorPabOverview {
  pabSessionId: number;
  sessionIdentifier: string;
  status: string;
  workingStatus: string | null;
  activeFrom: string | null;
  activeTill: string | null;
  achiever: {
    id: number;
    fullName: string;
    currentRole: string | null;
    currentCompany: string | null;
    location: string | null;
    currentRoleLevel: string | null;
    profilePictureUrl: string | null;
  };
  totalAdvisors: number;
  okrs: OKRSummaryForAdvisor[];
  lastTouch: string | null;
  kickoffDate: string | null;
  /** Every goal except dropped/archived, unlike `okrs` (top two in flight). */
  goals: AdvisorPabGoal[];
  coAdvisors: AdvisorPabPerson[];
  /** Seats on a board; fewer accepted advisors means it is still forming. */
  expectedAdvisors: number;
  wrapupDate: string | null;
  nextSession: AdvisorPabSessionRef | null;
  /** The achiever's average rating of this expert, if they gave one. */
  myRating: number | null;
  starsCaptured: number;
}

export type AdvisorPabGoalHealth = "achieved" | "on_track" | "at_risk" | "not_started";

export interface AdvisorPabGoal {
  id: number;
  title: string;
  status: string;
  health: AdvisorPabGoalHealth;
  progress: number;
  tasksTotal: number;
  tasksCompleted: number;
}

export interface AdvisorPabPerson {
  userId: string;
  fullName: string;
  profilePictureUrl: string | null;
}

export interface AdvisorPabSessionRef {
  meetingId: number;
  title: string;
  startTime: string | null;
  endTime: string | null;
}

/** An upcoming PAB session whose achiever marked their prep ready. */
export interface AdvisorPrepReady {
  meetingId: number;
  pabSessionId: number;
  title: string;
  startTime: string | null;
  achieverName: string;
  notificationIds: number[];
  isRead: boolean;
}

export type ProgressGoalStatus = "Behind" | "On Track" | "Ahead";

export interface AdvisorProgressGoal {
  title: string;
  tasksDone: number;
  totalTasks: number;
  statusLabel?: ProgressGoalStatus;
  deltaLabel?: string;
}

export interface AdvisorProgressItem {
  id: string;
  sessionId: number;
  achieverName: string;
  roleLabel: string;
  lastTouch: string;
  currentWeek: number;
  goals: AdvisorProgressGoal[];
}

export interface StarStory {
  situation: string;
  task: string;
  action: string;
  result: string;
  title?: string | null;
}

export interface StarStoryRequest {
  situation: string;
  task: string;
  action: string;
  result: string;
  title?: string | null;
}

export interface PaginatedStarStoriesResponse {
  items: StarStory[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export interface AchieverFeedbackRequest {
  // v3: backend FeedbackAchieverCreate expects advisor_user_id (SHA256 string).
  advisorUserId: string;
  rateScore: number;
  comments: string;
}

export interface AchieverFeedbackResponse {
  success: boolean;
  message: string;
}

export interface AdvisorFeedbackResponse {
  id: number;
  advisorId: number;
  pabSessionId: number;
  appreciateAboutAdvisor: string[] | null;
  writtenTestimonial: string | null;
  isRecommended: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AdvisorProgramFeedbackResponse {
  id: number;
  advisorId: number;
  programId: number;
  appreciateAboutAdvisor: string[] | null;
  writtenTestimonial: string | null;
  isRecommended: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface OverallPabFeedbackRequest {
  rateScore: number;
  comments: string;
}

export interface OverallPabFeedbackResponse {
  success: boolean;
  message: string;
}

export interface AdvisorFeedbackItem {
  // v3: backed by the unified feedback table (UUID id) -- backend field is
  // advisor_user_id (SHA256 string), not the legacy int advisorId.
  id: string;
  advisorUserId: string;
  rateScore: number;
  comments: string;
}

export type GetAllAdvisorFeedbackResponse = AdvisorFeedbackItem[];

/** One rating being submitted: the session itself when targetUserId is null. */
export interface SessionFeedbackItem {
  targetUserId: string | null;
  rating: number;
  comment?: string | null;
}

/** A stored feedback row, as returned to its author. */
export interface SessionFeedbackEntry {
  id: string;
  programId: number;
  meetingId: number | null;
  targetUserId: string | null;
  rating: number | null;
  comment: string | null;
  createdAt: string;
}

/** A person the caller is expected to rate for a session. */
export interface SessionFeedbackTarget {
  userId: string;
  fullName: string | null;
  /** Program membership role: ADVISOR (PAB) or LEAD / CO_ADVISOR (Expert Program). */
  role: string;
  submitted: boolean;
  profilePictureUrl: string | null;
  /** Subtitle under the name, e.g. "CPO @ Notion"; null when the profile has neither. */
  roleTitle: string | null;
  /** The caller's own previous rating/comment, so the form can prefill. */
  rating: number | null;
  comment: string | null;
}

/**
 * What the caller still owes for a session. `complete` is the flag to prompt on
 * -- it accounts for both the session rating and every peer rating.
 */
export interface SessionFeedbackPending {
  meetingId: number;
  programId: number;
  sessionTitle: string | null;
  sessionStartTime: string | null;
  sessionSubmitted: boolean;
  sessionRating: number | null;
  sessionComment: string | null;
  targets: SessionFeedbackTarget[];
  complete: boolean;
}

export interface GetOverallPabFeedbackResponse {
  rateScore: number;
  comments: string;
}

export interface GrowthArea {
  competencyName: string;
  id: number;
  competencyDescription: string;
}

export interface FetchGrowthAreasResponse {
  primaryFunction: string;
  roleLevel: string;
  growthAreas: GrowthArea[];
  totalCount: number;
}

export interface MarkSessionPrepReadyPayload {
  sessionOverview: string;
  sessionPrepGoals: string[];
  meetingAgendas: MeetingAgenda[];
  sessionPrepLink: string;
  sessionCallLink: string;
}

export type FetchRoleLevelsResponse = string[];

export interface OKTStatusSummaryForAdvisor {
  total: number;
  completed: number;
  onTrack: number;
  ahead: number;
}

export interface OKRSummaryForAdvisor {
  id: number;
  title: string;
  status: string;
  oktSummary: OKTStatusSummaryForAdvisor;
}

/** One member of a PAB's selected board, as returned by PabSessionDetailedOut.advisors
 * (app/schemas/pab_session.py's AdvisorDetailOut) and by getSelectedAdvisors. */
export interface PabBoardAdvisor {
  id: number;
  userId?: string | null;
  name: string;
  email: string;
  title?: string | null;
  company?: string | null;
  isSelected: boolean;
  approvalStatus: string;
  matchScore: number;
  matchedGoals: string[];
  profileUrl?: string | null;
}

// PAB Session detailed response types
export interface PABSessionDetailed {
  session: {
    id: number;
    sessionIdentifier: string;
    status: string;
    createdAt: string;
    activeFrom: string | null;
    activeTill: string | null;
    prerequisites: {
      organization_goal: string[];
      team_goal: string[];
      profile: {
        current_role: string;
        current_company: string;
        location: string;
        years_of_experience: number;
        primary_function: string;
        organisation_type: string[];
        current_role_level: string;
        top_growth_areas: string[];
        goals_and_challenges: string;
        bio: string;
      };
      preference: {
        preferred_advisor_level: string;
        advisory_style: string[];
        session_preference: string;
        session_duration_mins: number;
        meeting_frequency: string;
      };
    };
  };
  achiever: {
    id: number;
    userId?: string | null;
    name: string;
    email: string;
    currentRole: string;
    currentCompany: string;
    profileUrl: string | null;
  };
  /** Every ADVISOR-role invitation ever created for this PAB -- the full
   * matching-funnel candidate history (suggested/withdrawn/rejected included),
   * NOT just the achiever's final board (see PabSessionService.get_detailed_session,
   * which queries invitations with no `is_selected` filter). Filter on
   * `isSelected` to get the actual board, same as `getSelectedAdvisors` does
   * server-side. */
  advisors: PabBoardAdvisor[];
  okrs: {
    id: number;
    title: string;
    description: string;
    status: string;
    isFinalized: boolean;
    progressPercentage: number;
    dueDate: string;
    oktSummary?: OKTStatusSummaryForAdvisor;
    advisors?: OKRAdvisor[];
    tasks: {
      id: number;
      title: string;
      description: string;
      status: string;
      isFinalized: boolean;
      startDate: string | null;
      dueDate: string | null;
      notesCount?: number;
    }[];
  }[];
}

export interface OKTsSummary {
  total: number;
  completed: number;
  inProgress: number;
  notStarted: number;
  progressPercentage: number;
}

export interface OKRAlignment {
  company: number;
  team: number;
  personal: number;
}

export interface SelectedOKRWithOKTs {
  id: number;
  title: string;
  description: string;
  status: GoalStatus;
  dueDate: string | null;
  progressPercentage: number;
  category: string;
  smartGoalScore: number;
  alignment: OKRAlignment;
  isSuggested: boolean;
  isFinalized: boolean;
  visibility: "PRIVATE" | "PUBLIC";
  evaluationComment: string | null;
  okts: OKT[];
  oktsSummary: OKTsSummary;
  advisors?: OKRAdvisor[];
  /** Expert Program goals only — set when this goal was synced down from an advisor's template. */
  clonedFromId?: number | null;
}

export interface SelectedOKRsWithOKTsResponse {
  items: SelectedOKRWithOKTs[];
}

export interface StarStoryWithMeta extends StarStory {
  id: number;
  okrId: number;
  pabSessionId: number;
  createdAt?: string;
  updatedAt?: string;
}

// ── Wrapup types ──────────────────────────────────────────────────────────────

export interface WrapupMetrics {
  totalSessions: number;
  totalOkrsAchieved: number;
  totalStarsCaptured: number;
  totalTasksCompleted: number;
}

export interface JourneyEvent {
  weekNumber: number;
  weekStart: string;
  weekEnd: string;
  eventType: string;
  title: string;
  date: string;
  meetingAgendas?: { description: string; duration: number }[];
}

export interface WrapupWeeksJourney {
  sessionId: number;
  totalWeeks: number;
  journey: JourneyEvent[];
}

export interface ProgramWeeksJourneyEvent {
  weekNumber: number;
  weekStart: string;
  weekEnd: string;
  eventType: "goal_achieved" | "goals_finalized" | "session_completed";
  title: string | null;
  eventDate: string | null;
  meetingAgendas: unknown[] | null;
}

export interface ProgramWeeksJourneyResponse {
  programId: number;
  totalWeeks: number;
  journey: ProgramWeeksJourneyEvent[];
}

export interface TasksMessageProps {
  pabSession: PabSession | undefined;
  selectedTasks: TaskItem[];
  handleSetSelectedTask: (task: TaskItem, isSelected: boolean) => void;
  handleContinue: () => void;
  handleProceedToAdvisors?: () => void;
  onBack?: () => void;
  disabled?: boolean;
  onContentLoaded?: () => void;
}

// API methods
// ── Batched PAB timelines ────────────────────────────────────────────────────
// Shapes for GET /pab-sessions/timelines. Only the counts the horizontal
// timeline draws; isCompleted/isCurrent stay client-side because they depend on
// the viewer's own today.

export interface TimelineWeekCounts {
  weekNumber: number;
  startDate: string;
  endDate: string;
  taskCount: number;
  sessionCount: number;
  groupSessionCount: number;
  oneOnOneSessionCount: number;
  upcomingSessionCount: number;
  goalAchieved: boolean;
}

export interface PabTimeline {
  sessionId: number;
  activeFrom: string | null;
  activeTill: string | null;
  kickoffDate: string | null;
  /** Drives the edit-access/archived check on the advisor home and PABs list. */
  wrapupDate: string | null;
  wrapupStatus: string | null;
  /** Empty when the board has no active dates yet — the UI renders a blank
   *  week track for that, so it is a normal state and not an error. */
  weeks: TimelineWeekCounts[];
}

export interface PabTimelinesResponse {
  timelines: PabTimeline[];
}

// ── Messages inbox ───────────────────────────────────────────────────────────
// Shapes for GET /messages/inbox, which backs the whole messages list in one
// request. Note a PAB session id IS a program id, so PAB and Expert rows share
// these types and are told apart by `programType`.

export interface InboxConversationPreview {
  conversationId: number;
  lastMessageBody: string | null;
  lastMessageSenderId: string | null;
  lastMessageSenderName: string | null;
  lastMessageAt: string | null;
  unreadCount: number;
}

export interface InboxParticipant {
  userId: string;
  fullName: string | null;
  profilePictureUrl: string | null;
  currentRole: string | null;
  company: string | null;
  membershipRole: string | null;
  /** null when the two have never exchanged a message — the row still renders,
   *  as "No messages yet", so the roster must come back regardless. */
  directConversation: InboxConversationPreview | null;
}

export interface InboxProgram {
  programId: number;
  externalId: string | null;
  name: string | null;
  programType: "PAB" | "EXPERT" | "PEER_ADVISORY";
  groupConversation: InboxConversationPreview | null;
  participants: InboxParticipant[];
}

export interface MessagesInbox {
  programs: InboxProgram[];
  totalUnreadCount: number;
}

export const api = {
  // User methods
  users: {
    getCurrentUser: async (token?: string): Promise<User> => {
      const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
      const response = await authAxios.get(`${API_BASE}/users/me`, { headers });
      return response.data;
    },
    /** Add an additional role to the current user (e.g. advisor becoming achiever too). Idempotent. */
    grantRole: async (role: string): Promise<User> => {
      const response = await authAxios.post(`${API_BASE}/users/me/roles`, { role });
      return response.data;
    },
    /** Switch which role's workspace the current user is viewing the app as. Must already hold that role. */
    setActiveWorkspace: async (workspace: string): Promise<User> => {
      const response = await authAxios.patch(`${API_BASE}/users/me/active-workspace`, {
        workspace,
      });
      return response.data;
    },
    updateProfile: async (userData: Partial<User>): Promise<User> => {
      const response = await authAxios.put(`${API_BASE}/users/profile`, userData);
      return response.data;
    },
    deleteMe: async (): Promise<void> => {
      await authAxios.delete(`${API_BASE}/users/me`);
    },
    getAiUsage: async (): Promise<{
      daily: { requests: number; limit: number; remaining: number };
    }> => {
      const response = await authAxios.get(`${API_BASE}/users/me/ai-usage`);
      return response.data;
    },
    getOnboardingStep: async (token?: string): Promise<{ step: string | null }> => {
      const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
      // Called during login routing (getOnboardingRedirectRoute), which catches
      // failures and falls back to "/home". Without _skipForbiddenRedirect a 403
      // here would hard-redirect to /unauthorized via the axios interceptor,
      // bypassing that fallback — so a user whose onboarding-step check 403s on
      // login would land on the unauthorized page instead of home.
      const response = await authAxios.get(`${API_BASE}/users/onboarding/step`, {
        headers,
        _skipForbiddenRedirect: true,
      } as never);
      return response.data;
    },
    updateOnboardingStep: async (step: string): Promise<User> => {
      const response = await authAxios.put(`${API_BASE}/users/onboarding/step`, { step });
      return response.data;
    },
    // DISABLED (multi-account): the /users/me/connected-accounts endpoints are
    // commented out on the backend, so these clients would 404.
    //
    // getConnectedAccounts: async (token?: string): Promise<{ items: ConnectedAccount[] }> => {
    //   const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    //   const response = await authAxios.get(`${API_BASE}/users/me/connected-accounts`, { headers });
    //   return response.data;
    // },
    // connectAccount: async (targetUserId: string): Promise<ConnectedAccount> => {
    //   const response = await authAxios.post(`${API_BASE}/users/me/connected-accounts`, {
    //     targetUserId,
    //   });
    //   return response.data;
    // },
    // disconnectAccount: async (targetUserId: string): Promise<void> => {
    //   await authAxios.delete(`${API_BASE}/users/me/connected-accounts/${targetUserId}`);
    // },
    updateWrapupStep: async (step: number): Promise<WrapupStep> => {
      const response = await authAxios.patch(`${API_BASE}/users/wrapup/step`, { step });
      return response.data;
    },
    updateBuildPabStep: async (step: number): Promise<BuildPabStep> => {
      const response = await authAxios.patch(`${API_BASE}/users/build-pab/step`, { step });
      return response.data;
    },
    updateBuildExpertStep: async (step: number): Promise<BuildExpertStep> => {
      const response = await authAxios.patch(`${API_BASE}/users/build-expert/step`, { step });
      return response.data;
    },
    getProgramTypes: async (): Promise<{
      hasPab: boolean;
      hasExpert: boolean;
      hasOrganisation?: boolean;
    }> => {
      const response = await authAxios.get(`${API_BASE}/users/me/program-types`);
      return response.data;
    },
    patchLastLogin: async (): Promise<void> => {
      await authAxios.patch(`${API_BASE}/users/me/last-login`, {
        last_logged_in: new Date().toISOString(),
      });
    },
    getUserLastLogin: async (
      userId: string,
    ): Promise<{ last_logged_in: string | null; user_created_date: string | null }> => {
      try {
        const response = await authAxios.get(`${API_BASE}/users/${userId}/last-login`, {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          _skipForbiddenRedirect: true,
        } as any);
        return response.data;
      } catch {
        return { last_logged_in: null, user_created_date: null };
      }
    },
  },

  messages: {
    /** Pass `workspace` to count only that workspace's conversations — the
     *  ones the messages list actually shows. */
    unreadCount: async (workspace?: "advisor" | "achiever"): Promise<{ unreadCount: number }> => {
      const response = await authAxios.get(`${API_BASE}/messages/unread-count`, {
        params: workspace ? { workspace } : undefined,
      });
      return response.data;
    },
    /** Every conversation preview for the messages list in ONE request.
     *
     *  The drawer used to build this itself by fetching each conversation's full
     *  history — one request per program, per PAB, and per (program × member)
     *  pair, ~89 for a user with 14 programs — then keeping only the last
     *  message of each. Prefer this over adding another per-row fetch. */
    inbox: async (): Promise<MessagesInbox> => {
      const response = await authAxios.get(`${API_BASE}/messages/inbox`);
      return response.data;
    },
    /** Clears a whole conversation's unread state in one write. Replaces the
     *  old per-message PATCH fan-out over an unreadIds array. */
    markConversationRead: async (conversationId: number): Promise<{ unreadCount: number }> => {
      const response = await authAxios.patch(
        `${API_BASE}/messages/conversations/${conversationId}/read`,
      );
      return response.data;
    },
  },

  notifications: {
    list: async (page: number = 1, size: number = 10): Promise<NotificationListResponse> => {
      const response = await authAxios.get(`${API_BASE}/notifications`, {
        params: { page, size },
      });
      return response.data;
    },
    unreadCount: async (): Promise<{ unreadCount: number }> => {
      const response = await authAxios.get(`${API_BASE}/notifications/unread-count`);
      return response.data;
    },
    markAllRead: async (): Promise<{ unreadCount: number }> => {
      const response = await authAxios.patch(`${API_BASE}/notifications/read-all`);
      return response.data;
    },
    markIdsRead: async (notificationIds: number[]): Promise<{ unreadCount: number }> => {
      const response = await authAxios.patch(`${API_BASE}/notifications/mark-read`, {
        notificationIds,
      });
      return response.data;
    },
    markOneRead: async (notificationId: number): Promise<{ unreadCount: number }> => {
      const response = await authAxios.patch(`${API_BASE}/notifications/${notificationId}/read`);
      return response.data;
    },
  },

  // Auth methods
  auth: {
    impersonate: async (data: {
      email: string;
      secretKey?: string;
    }): Promise<{ loginUrl: string; email: string }> => {
      const response = await authAxios.post(`${API_BASE}/auth/impersonate`, data);
      return response.data;
    },
  },

  // Dashboard methods
  dashboard: {
    getStats: async (): Promise<DashboardStats> => {
      const response = await authAxios.get(`${API_BASE}/dashboard/stats`);
      return response.data;
    },
    getProjects: async (): Promise<Project[]> => {
      const response = await authAxios.get(`${API_BASE}/dashboard/projects`);
      return response.data;
    },
    getAchieverSummary: async (): Promise<DashboardSummaryResponse> => {
      const response = await authAxios.get(`${API_BASE}/dashboard/achievers/summary`);
      return response.data;
    },
    getAchieverMetrics: async (): Promise<AchieverMetricsResponse> => {
      const response = await authAxios.get(`${API_BASE}/dashboard/achievers/metrics`);
      return response.data;
    },
    getValueStats: async (): Promise<DashboardValueStats> => {
      const summaryResponse = await authAxios.get(`${API_BASE}/dashboard/achievers/summary`);
      const summaryCards: Record<string, number> = {};

      // Create a map with title as key and value as value
      summaryResponse.data.summaryCards.forEach((card: SummaryCard) => {
        summaryCards[card.title] = card.value;
      });

      return { summaryCards };
    },
    getAdvisorValueStats: async (): Promise<AdvisorDashboardValueStats> => {
      const response = await authAxios.get<AdvisorDashboardSummaryResponse>(
        `${API_BASE}/dashboard/advisors/summary`,
      );
      const summaryCards: Record<string, number> = {};

      // Create a map with title as key and value as value
      response.data.summaryCards.forEach((card: AdvisorSummaryCard) => {
        summaryCards[card.title] = card.value;
      });

      return { summaryCards };
    },
    getAdvisorDashboardSummary: async (): Promise<AdvisorDashboardSummaryResponse> => {
      const response = await authAxios.get(`${API_BASE}/dashboard/advisors/summary`);
      return response.data;
    },
    getAdvisorPABs: async (): Promise<PABCard[]> => {
      const response = await authAxios.get<AdvisorDashboardSummaryResponse>(
        `${API_BASE}/dashboard/advisors/summary`,
      );
      return response.data.pabCards;
    },
    getAdvisorGoals: async (): Promise<GoalRow[]> => {
      const response = await authAxios.get<AdvisorDashboardSummaryResponse>(
        `${API_BASE}/dashboard/advisors/summary`,
      );
      return response.data.goalRows;
    },
    getAdvisorProgressItems: async (
      sessionLimit?: number,
      goalsLimit: number = 2,
    ): Promise<AdvisorProgressItem[]> => {
      const params: Record<string, number> = {
        goals_limit: goalsLimit,
      };

      if (typeof sessionLimit === "number") {
        params.session_limit = sessionLimit;
      }

      const response = await authAxios.get<AdvisorProgressItem[]>(
        `${API_BASE}/dashboard/advisors/progress-items`,
        {
          params,
        },
      );
      return response.data;
    },

    /** Upcoming PAB sessions (next 7 days) whose achiever marked prep ready. */
    getAdvisorPrepReady: async (): Promise<AdvisorPrepReady[]> => {
      const response = await authAxios.get<AdvisorPrepReady[]>(
        `${API_BASE}/dashboard/advisors/prep-ready`,
      );
      return response.data;
    },

    /** Achieved PAB goals with no S.T.A.R. yet, newest first (Home's S.T.A.R. Moment). */
    getAdvisorStarMoments: async (): Promise<AdvisorStarMoment[]> => {
      const response = await authAxios.get<AdvisorStarMoment[]>(
        `${API_BASE}/dashboard/advisors/star-moments`,
      );
      return response.data;
    },

    getAdvisorPabOverviews: async (): Promise<AdvisorPabOverview[]> => {
      const response = await authAxios.get<AdvisorPabOverview[]>(
        `${API_BASE}/dashboard/advisors/pab-overviews`,
      );
      return response.data;
    },
  },
  master: {
    /**
     * Every option in a scope, in dropdown order. Pages are capped at 100 by
     * the API, and a list can be longer (Industry has 148), so this keeps
     * fetching until it has them all.
     */
    getMasterData: async <T>(scope: MasterScope, size: number = 100): Promise<T[]> => {
      const encoded = encodeURIComponent(scope);
      const items: T[] = [];
      for (let page = 1; ; page++) {
        const response = await authAxios.get(
          `${API_BASE}/master-data/${encoded}?size=${size}&page=${page}`,
        );
        const pageItems = (response.data.items ?? []) as T[];
        items.push(...pageItems);
        const total: number = response.data.total ?? items.length;
        if (pageItems.length === 0 || items.length >= total) return items;
      }
    },
    /** Type-ahead for State / Region: countries, states and cities ("Houston, TX, USA"). */
    searchLocations: async (q: string, limit: number = 20): Promise<LocationOption[]> => {
      const response = await authAxios.get(`${API_BASE}/master-data/locations/search`, {
        params: { q, limit },
      });
      return response.data;
    },
  },
  achievers: {
    getAchiever: async (): Promise<Achiever> => {
      const response = await authAxios.get(`${API_BASE}/achievers/me`);
      return response.data;
    },
    getAchieverMe: async (): Promise<Achiever> => {
      const response = await authAxios.get(`${API_BASE}/achievers/me`);
      return response.data;
    },
    getProfile: async (achieverId: string): Promise<Record<string, unknown>> => {
      const response = await authAxios.get(`${API_BASE}/achievers/${achieverId}/profile`);
      return response.data;
    },
    updateProfile: async (
      achieverId: string,
      profile: Record<string, unknown>,
    ): Promise<Record<string, unknown>> => {
      const response = await authAxios.put(`${API_BASE}/achievers/${achieverId}/profile`, profile);
      return response.data;
    },
    updateProfileWithPreferences: async (
      profile: Record<string, unknown>,
      file?: File,
    ): Promise<Record<string, unknown>> => {
      const formData = new FormData();

      Object.entries(profile || {}).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        const serialized = typeof value === "object" ? JSON.stringify(value) : String(value);
        formData.append(key, serialized);
      });

      if (file) {
        formData.append("avatar", file);
      }

      const response = await authAxios.put(
        `${API_BASE}/achievers/me/profile-with-preferences`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      return response.data;
    },
    updateSocialLinks: async (
      achieverId: string,
      socialLinks: { linkedIn?: string; personalWebsite?: string },
    ): Promise<Record<string, unknown>> => {
      const response = await authAxios.patch(
        `${API_BASE}/achievers/${achieverId}/profile/social-links`,
        socialLinks,
      );
      return response.data;
    },
    createProfile: async (
      achieverId: string,
      profile: {
        // v3: backend AchieverProfileCreate requires `currentTitle`
        currentTitle: string;
        currentCompany: string;
        location: string;
        yearsOfExperience: ExperienceEnum;
        primaryFunction: string;
        organisationType: string[];
        currentRoleLevel: string;
        topGrowthAreas: string[];
        goalsAndChallenges: string;
        bio: string;
        linkedIn?: string;
        personalWebsite?: string;
        experienceContext?: string[];
        jobRole?: string;
        industry?: string;
      },
    ): Promise<AxiosResponse> => {
      const response = await authAxios.post(`${API_BASE}/achievers/${achieverId}/profile`, profile);
      return response.data;
    },
    getPreference: async (achieverId: string): Promise<Record<string, unknown>> => {
      const response = await authAxios.get(`${API_BASE}/achievers/${achieverId}/preference`);
      return response.data;
    },
    createPreference: async (
      achieverId: string,
      preference: {
        preferredAdvisorLevel: string;
        advisoryStyle: string[];
        sessionPreference?: string;
        sessionDuration?: number;
        meetingFrequency?: string;
        days?: string[];
        timeSlots?: string[];
        timezone?: string;
        questionsForAdvisors?: string[];
      },
    ): Promise<AxiosResponse> => {
      const response = await authAxios.post(
        `${API_BASE}/achievers/${achieverId}/preference`,
        preference,
      );
      return response.data;
    },
    /**
     * Update achiever preference
     * PUT /api/v1/achievers/{achiever_id}/preference
     * @param achieverId - The achiever id
     * @param preference - Partial preference data to update
     * @returns {Promise<AxiosResponse>}
     */
    updateAchieverPreference: async (
      achieverId: string,
      preference: Partial<{
        preferredAdvisorLevel: string;
        advisoryStyle: string[];
        sessionPreference: string;
        sessionDuration: number;
        meetingFrequency: string;
        days: string[];
        timeSlots: string[];
        timezone: string;
        questionsForAdvisors: string[];
      }>,
    ): Promise<AxiosResponse> => {
      const response = await authAxios.put(
        `${API_BASE}/achievers/${achieverId}/preference`,
        preference,
      );
      return response.data;
    },
    getQuestions: async (achieverId: string): Promise<AchieverQuestionOut[]> => {
      const response = await authAxios.get(`${API_BASE}/achievers/${achieverId}/questions`);
      return response.data;
    },
    updateQuestion: async (
      achieverId: string,
      questionId: number,
      data: AchieverQuestionUpdate,
    ): Promise<AchieverQuestionOut> => {
      const response = await authAxios.put(
        `${API_BASE}/achievers/${achieverId}/questions/${questionId}`,
        data,
      );
      return response.data;
    },
    generateAdvisorInviteSummaries: async (
      achieverId: string,
      payload: AchieverInviteSummaryRequest,
    ): Promise<AchieverInviteSummaryResponse> => {
      const response = await authAxios.post(
        `${API_BASE}/achievers/${achieverId}/advisor-invite-summaries`,
        payload,
      );
      return response.data;
    },
    /**
     * Get FTUX checklist status for the current achiever
     * GET /api/v1/achievers/me/ftux-status
     */
    getAchieverFtuxStatus: async (): Promise<AchieverFtuxStatus> => {
      const response = await authAxios.get(`${API_BASE}/achievers/me/ftux-status`);
      return response.data;
    },
    /**
     * Mark a specific FTUX step as complete (bitmask — any order allowed)
     * POST /api/v1/achievers/me/complete-ftux-step
     * @param stepNumber 1–4
     */
    completeAchieverFtuxStep: async (stepNumber: number): Promise<AchieverFtuxStatus> => {
      const response = await authAxios.post(`${API_BASE}/achievers/me/complete-ftux-step`, {
        step_number: stepNumber,
      });
      return response.data;
    },
    /**
     * Save kick-off questions and mark step 4 complete
     * POST /api/v1/achievers/me/kickoff-questions
     */
    saveKickoffQuestions: async (questions: string): Promise<AchieverFtuxStatus> => {
      const response = await authAxios.post(`${API_BASE}/achievers/me/kickoff-questions`, {
        questions,
      });
      return response.data;
    },
  },
  advisors: {
    getAdvisor: async (): Promise<{
      userId: string;
      ftuxStep: number;
      codeOfConductAccepted: boolean;
    }> => {
      // v3: GET /advisors/me returns { userId, ftuxStep, codeOfConductAccepted }.
      // Advisor identity IS the user_id (SHA256 string); there is no advisors int PK.
      const response = await authAxios.get(`${API_BASE}/advisors/me`);
      return response.data;
    },
    /**
     * Mark a specific advisor FTUX step as complete (bitmask — any order allowed)
     * POST /api/v1/advisors/me/complete-ftux-step
     * @param stepNumber 1–3
     */
    completeFtuxStep: async (
      stepNumber: number,
    ): Promise<{ userId: string; ftuxStep: number; codeOfConductAccepted: boolean }> => {
      const response = await authAxios.post(`${API_BASE}/advisors/me/complete-ftux-step`, {
        step_number: stepNumber,
      });
      return response.data;
    },
    /**
     * Get advisor meetings
     * GET /api/v1/advisors/me/meetings
     * @returns {Promise<AdvisorMeeting[]>}
     */
    getMeetings: async (): Promise<AdvisorMeeting[]> => {
      const response = await authAxios.get(`${API_BASE}/advisors/me/meetings/upcoming`);
      return response.data;
    },
    /**
     * Get advisor past meetings
     * GET /api/v1/advisors/me/meetings/past
     * @returns {Promise<AdvisorMeeting[]>}
     */
    getPastMeetings: async (): Promise<AdvisorMeeting[]> => {
      const response = await authAxios.get(`${API_BASE}/advisors/me/meetings/past`);
      return response.data;
    },
    /**
     * Get all incomplete meeting todos across every meeting the advisor participates in.
     * GET /api/v1/advisors/me/meeting-todos
     */
    getMeetingTodos: async (): Promise<AdvisorMeetingTodo[]> => {
      const response = await authAxios.get(`${API_BASE}/advisors/me/meeting-todos`);
      return response.data;
    },
    /**
     * Get advisor profile by ID
     * GET /api/v1/advisors/{id}/profile
     * @param advisorId - The advisor id
     * @returns {Promise<AdvisorProfile>}
     */
    getProfile: async (advisorId: string): Promise<AdvisorProfile> => {
      const response = await authAxios.get(`${API_BASE}/advisors/${advisorId}/profile`);
      return response.data;
    },
    getPublicProfile: async (slug: string): Promise<PublicAdvisorProfile> => {
      const response = await publicAxios.get(
        `${API_BASE}/public/advisors/profile/${encodeURIComponent(slug)}`,
      );
      return response.data;
    },
    /**
     * Get advisor platform stats by advisor ID
     * GET /api/v1/advisors/{advisor_id}/platform-stats
     * @param advisorId - The advisor id
     * @returns {Promise<AdvisorPlatformStats>}
     */
    getPlatformStats: async (advisorId: string): Promise<AdvisorPlatformStats> => {
      const response = await authAxios.get(`${API_BASE}/advisors/${advisorId}/platform-stats`);
      const data = response.data ?? {};
      return {
        totalSessions: data.totalSessions ?? data.total_sessions ?? 0,
        acceptedPabs: data.acceptedPabs ?? data.accepted_pabs ?? 0,
        platformExperience: data.platformExperience ?? data.platform_experience ?? 0,
      };
    },
    updateProfileWithPreferences: async (
      profile: Record<string, unknown>,
      file?: File,
    ): Promise<Record<string, unknown>> => {
      const formData = new FormData();

      Object.entries(profile || {}).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        const serialized = typeof value === "object" ? JSON.stringify(value) : String(value);
        formData.append(key, serialized);
      });

      if (file) {
        formData.append("avatar", file);
      }

      const response = await authAxios.put(
        `${API_BASE}/advisors/me/profile-with-preferences`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      return response.data;
    },
    createProfile: async (
      advisorId: string,
      profile: {
        currentTitle: string;
        currentCompany: string;
        location: string;
        yearsOfExperience: ExperienceEnum;
        primaryFunction: string;
        currentRoleLevel?: string;
        topGrowthAreas?: string[];
        experienceContext: string[];
        jobRole?: string;
        industry?: string;
        bio: string;
        organisationType: string[];
        linkedIn?: string;
        personalWebsite?: string;
      },
    ): Promise<AxiosResponse> => {
      const response = await authAxios.post(`${API_BASE}/advisors/${advisorId}/profile`, profile);
      return response.data;
    },
    updateProfile: async (
      advisorId: string,
      profile: {
        currentTitle: string;
        currentCompany: string;
        location: string;
        yearsOfExperience: ExperienceEnum;
        primaryFunction: string;
        currentRoleLevel?: string;
        topGrowthAreas?: string[];
        experienceContext: string[];
        jobRole?: string;
        industry?: string;
        bio: string;
        organisationType: string[];
        linkedIn?: string;
        personalWebsite?: string;
      },
    ): Promise<AxiosResponse> => {
      const response = await authAxios.put(`${API_BASE}/advisors/${advisorId}/profile`, profile);
      return response.data;
    },
    getPreference: async (advisorId: string): Promise<Record<string, unknown>> => {
      const response = await authAxios.get(`${API_BASE}/advisors/${advisorId}/preference`);
      return response.data;
    },
    createPreference: async (
      advisorId: string,
      preference: {
        advisoryAreas: string[];
        advisoryStyles: string[];
        sessionPreference?: string;
        sessionDuration?: number;
        meetingFrequency?: string;
        days: string[];
        timeSlots: string[];
        timezone: string;
        functionsAdvised: string[];
        levelsMentored: string[];
        advisoryScenarios?: { title: string; description: string }[];
        interestMultiAdvisorBoards?: boolean;
        advisoryAccomplishments?: string;
        createLeadPrograms?: boolean;
        joinAsCoExpert?: boolean;
        joinPabBoard?: boolean;
        peerAdvisory?: boolean;
        coFounding?: boolean;
        coreIndustries?: string[];
        operatorThesis?: string;
        problemAndInsight?: string;
        starPortfolio?: StarPortfolioStory[];
        infoAccuracyConfirmed?: boolean;
        privacyPolicyAcknowledged?: boolean;
        applicationReviewConsented?: boolean;
        profileUseConsented?: boolean;
        profileBuildConsented?: boolean;
        advisoryPhilosophy?: string;
        engagementStructure?: string;
        rate?: string;
      },
    ): Promise<AxiosResponse> => {
      // Transform advisoryAreas to coachingStrength for API compatibility
      const { advisoryAreas, ...restPreference } = preference;
      const apiPayload = {
        ...restPreference,
        coachingStrengths: advisoryAreas, //TODO update the API to use advisoryAreas
      };

      const response = await authAxios.post(
        `${API_BASE}/advisors/${advisorId}/preference`,
        apiPayload,
      );
      return response.data;
    },
    updatePreference: async (
      advisorId: string,
      preference: Partial<{
        advisoryAreas: string[];
        advisoryStyles: string[];
        sessionPreference: string;
        sessionDuration: number;
        meetingFrequency: string;
        functionsAdvised: string[];
        levelsMentored: string[];
        interestMultiAdvisorBoards: boolean;
        advisoryAccomplishments: string;
        advisoryScenarios: { title: string; description: string }[];
        createLeadPrograms: boolean;
        joinAsCoExpert: boolean;
        joinPabBoard: boolean;
        peerAdvisory: boolean;
        coFounding: boolean;
        coreIndustries: string[];
        operatorThesis: string;
        problemAndInsight: string;
        starPortfolio: StarPortfolioStory[];
        infoAccuracyConfirmed: boolean;
        privacyPolicyAcknowledged: boolean;
        applicationReviewConsented: boolean;
        profileUseConsented: boolean;
        profileBuildConsented: boolean;
        advisoryPhilosophy: string;
        engagementStructure: string;
        rate: string;
      }>,
    ): Promise<AxiosResponse> => {
      const { advisoryAreas, ...restPreference } = preference;
      const apiPayload: Record<string, unknown> = {
        ...restPreference,
      };
      if (advisoryAreas !== undefined) {
        apiPayload.coachingStrengths = advisoryAreas;
      }

      const response = await authAxios.put(
        `${API_BASE}/advisors/${advisorId}/preference`,
        apiPayload,
      );
      return response.data;
    },
  },
  // Expert Application wizard (achiever applying to become an Expert/advisor).
  // Self-service, role-agnostic -- not gated on already holding the advisor role.
  capabilities: {
    /** Current user's questionnaire, if any progress has been saved yet. Throws 404 if none. */
    getMine: async (): Promise<UserCapabilitiesOut> => {
      const response = await authAxios.get(`${API_BASE}/capabilities/me`);
      return response.data;
    },
    /**
     * The caller's own Expert application in the admin review drawer's shape,
     * so "View Application" shows the applicant the reviewer's preview.
     * GET /api/v1/capabilities/me/application (404 when nothing submitted).
     */
    getMyApplication: async (): Promise<AdminApplicationDetailOut> => {
      const response = await authAxios.get(`${API_BASE}/capabilities/me/application`);
      return response.data;
    },
    /** Partial, in-progress save for wizard steps 1-5. Never changes reviewStatus/submittedAt. */
    saveDraft: async (payload: UserCapabilitiesDraftUpdate): Promise<UserCapabilitiesOut> => {
      const response = await authAxios.patch(`${API_BASE}/capabilities/me/draft`, payload);
      return response.data;
    },
    /** Final "Review & Submit" step -- requires all fields + 5 consents, sets reviewStatus=SUBMITTED. */
    submit: async (payload: UserCapabilitiesSubmit): Promise<UserCapabilitiesOut> => {
      const response = await authAxios.put(`${API_BASE}/capabilities/me`, payload);
      return response.data;
    },
    /**
     * Send a rejected application back for review as it stands now, and alert
     * the super admins. POST /api/v1/capabilities/me/resubmit (400 unless REJECTED).
     */
    resubmit: async (): Promise<UserCapabilitiesOut> => {
      const response = await authAxios.post(`${API_BASE}/capabilities/me/resubmit`);
      return response.data;
    },
  },
  // The XO "Getting Started" launch journey.
  //
  // The server stores WHICH steps are done, never what they say -- titles and
  // guidance live in src/constants/gettingStarted.ts. Every mutation returns
  // the whole state, so an optimistic update can be reconciled without a
  // follow-up read.
  gettingStarted: {
    getMine: async (): Promise<GettingStartedStateOut> => {
      const response = await authAxios.get(`${API_BASE}/getting-started/me`);
      return response.data;
    },
    /** Idempotent -- ticking twice is the same as once. */
    complete: async (taskKey: string): Promise<GettingStartedStateOut> => {
      const response = await authAxios.post(`${API_BASE}/getting-started/me/complete`, {
        taskKey,
      });
      return response.data;
    },
    /** Un-tick. A step that was never ticked is a no-op, not an error. */
    uncomplete: async (taskKey: string): Promise<GettingStartedStateOut> => {
      const response = await authAxios.delete(
        `${API_BASE}/getting-started/me/complete/${encodeURIComponent(taskKey)}`,
      );
      return response.data;
    },
    /** Dismiss the first-login welcome screen, for good and on every device. */
    markWelcomed: async (): Promise<GettingStartedStateOut> => {
      const response = await authAxios.post(`${API_BASE}/getting-started/me/welcomed`);
      return response.data;
    },
    /** Clear every tick and re-arm the welcome screen. Caller's own state only. */
    reset: async (): Promise<GettingStartedStateOut> => {
      const response = await authAxios.delete(`${API_BASE}/getting-started/me`);
      return response.data;
    },
    /**
     * Whether the calibration step can be booked at all. Three distinct
     * answers the UI must not collapse: not configured, configured but the
     * host has no connected calendar, and ready.
     */
    getCalibrationHostStatus: async (): Promise<CalibrationHostStatusOut> => {
      const response = await authAxios.get(`${API_BASE}/getting-started/calibration/host-status`);
      return response.data;
    },
    /**
     * This expert's existing calibration booking, or null. `bookingRef` is
     * what puts the Nylas widget into reschedule mode, so picking a new slot
     * moves the same event instead of creating a second one.
     */
    getCalibrationBooking: async (): Promise<CalibrationBookingOut | null> => {
      const response = await authAxios.get(`${API_BASE}/getting-started/calibration/booking`);
      return response.data ?? null;
    },
    /**
     * Achiever "explanatory session" (My Journey: Start small) -- the
     * calibration's host and booking flow, 15 minutes, ticking no step.
     */
    getExplanatorySessionHostStatus: async (): Promise<CalibrationHostStatusOut> => {
      const response = await authAxios.get(
        `${API_BASE}/getting-started/explanatory-session/host-status`,
      );
      return response.data;
    },
    getExplanatorySessionBooking: async (): Promise<CalibrationBookingOut | null> => {
      const response = await authAxios.get(
        `${API_BASE}/getting-started/explanatory-session/booking`,
      );
      return response.data ?? null;
    },
    createExplanatorySessionScheduler: async (): Promise<SchedulerConfigResult> => {
      const response = await authAxios.post(
        `${API_BASE}/getting-started/explanatory-session/scheduler`,
      );
      return response.data;
    },
    confirmExplanatorySessionBooking: async (payload: {
      configurationId: string;
      eventId?: string;
      bookingId?: string;
      bookingRef?: string;
    }): Promise<CalibrationBookingOut | null> => {
      const response = await authAxios.post(
        `${API_BASE}/getting-started/explanatory-session/confirm-booking`,
        payload,
      );
      return response.data ?? null;
    },
    /** The Nylas Scheduler config the booking widget renders. */
    createCalibrationScheduler: async (): Promise<SchedulerConfigResult> => {
      const response = await authAxios.post(`${API_BASE}/getting-started/calibration/scheduler`);
      return response.data;
    },
    confirmCalibrationBooking: async (payload: {
      configurationId: string;
      eventId?: string;
      bookingId?: string;
      bookingRef?: string;
    }): Promise<GettingStartedStateOut> => {
      const response = await authAxios.post(
        `${API_BASE}/getting-started/calibration/confirm-booking`,
        payload,
      );
      return response.data;
    },

    // ── Program Coach prompt (xo.launch.design) ───────────────────────────

    /** The caller's portfolio S.T.A.R. rows, in author order. */
    listProgramCoachStars: async (): Promise<PortfolioStarOut[]> => {
      const response = await authAxios.get(`${API_BASE}/getting-started/program-coach/stars`);
      return response.data;
    },
    /**
     * Fill the prompt's blanks from one S.T.A.R. via the LLM. Counts against
     * the daily AI limit — 429 once it is used up.
     */
    generateProgramCoachPrompt: async (starId: number): Promise<ProgramCoachPromptOut> => {
      const response = await authAxios.post(`${API_BASE}/getting-started/program-coach/prompt`, {
        starId,
      });
      return response.data;
    },

    // ── Referrals (xo.connect.refer-achievers / refer-xos) ───────────────
    //
    // Every mutation returns the whole list, as the other Getting Started
    // routes do, so the client never needs a follow-up read.

    /** Whether the team has shared the expert's LinkedIn carousel PDF yet. */
    getCarousel: async (): Promise<LinkedinCarouselStatus> => {
      const response = await authAxios.get(`${API_BASE}/getting-started/carousel`);
      return response.data;
    },
    /** The carousel PDF's bytes — for the page-by-page preview and the download. */
    getCarouselFile: async (): Promise<ArrayBuffer> => {
      const response = await authAxios.get(`${API_BASE}/getting-started/carousel/file`, {
        responseType: "arraybuffer",
      });
      return response.data;
    },
    getReferrals: async (): Promise<ReferralsOut> => {
      const response = await authAxios.get(`${API_BASE}/getting-started/referrals`);
      return response.data;
    },
    /** 409 if this person has already been referred as this kind. */
    addReferral: async (payload: {
      kind: "ACHIEVER" | "XO";
      name: string;
      email: string;
      linkedinUrl?: string;
    }): Promise<ReferralsOut> => {
      const response = await authAxios.post(`${API_BASE}/getting-started/referrals`, payload);
      return response.data;
    },
    removeReferral: async (referralId: number): Promise<ReferralsOut> => {
      const response = await authAxios.delete(
        `${API_BASE}/getting-started/referrals/${referralId}`,
      );
      return response.data;
    },

    // ── Peer 1:1 intro (xo.connect.peer) ─────────────────────────────────────
    //
    // Same machinery as the calibration, but the other party is an expert the
    // user picked from the Directory, so every call is scoped by userId.

    /** Whether THIS expert has a calendar connected. `false` is an ordinary
     *  answer — most experts have not connected one. */
    getPeerIntroStatus: async (userId: string): Promise<CalibrationHostStatusOut> => {
      const response = await authAxios.get(`${API_BASE}/getting-started/intro/peer-status`, {
        params: { userId },
      });
      return response.data;
    },
    /** 409 when that expert has no calendar; the caller renders it, not throws. */
    createPeerIntroScheduler: async (userId: string): Promise<SchedulerConfigResult> => {
      const response = await authAxios.post(`${API_BASE}/getting-started/intro/scheduler`, null, {
        params: { userId },
      });
      return response.data;
    },
    confirmPeerIntroBooking: async (payload: {
      peerUserId: string;
      configurationId?: string;
      eventId?: string;
      bookingId?: string;
      bookingRef?: string;
    }): Promise<GettingStartedStateOut> => {
      const response = await authAxios.post(
        `${API_BASE}/getting-started/intro/confirm-booking`,
        payload,
      );
      return response.data;
    },
  },
  pabSessions: {
    /**
     * Initialize a new PAB session for the logged-in achiever user.
     * POST /api/v1/pab-sessions/initialize
     * @param name - Optional name for the PAB, becomes the underlying Program's name.
     * @returns {Promise<PabSession>}
     */
    initialize: async (name?: string): Promise<PabSession> => {
      const response = await authAxios.post(
        `${API_BASE}/pab-sessions/initialize`,
        name ? { name } : {},
      );
      return response.data;
    },
    /**
     * Get detailed information for a PAB session
     * GET /api/v1/pab-sessions/{id}/detailed
     * @param sessionId - The PAB session id
     * @returns {Promise<PABSessionDetailed>}
     */
    getDetailed: async (sessionId: number): Promise<PABSessionDetailed> => {
      const response = await authAxios.get(`${API_BASE}/pab-sessions/${sessionId}/detailed`);
      return response.data;
    },

    /**
     * Get weekly tasks and sessions for a PAB session
     * GET /api/v1/pab-sessions/{id}/weekly-plan
     * @param sessionId - The PAB session id
     * @param weeks - Number of weeks to return (default 12)
     * @returns {Promise<WeeklyPlanResponse>}
     */
    getWeeklyPlan: async (sessionId: number, weeks: number = 12): Promise<WeeklyPlanResponse> => {
      const response = await authAxios.get(`${API_BASE}/pab-sessions/${sessionId}/weekly-plan`, {
        params: { weeks },
      });
      return response.data;
    },
    /**
     * Compact horizontal-timeline counts for EVERY PAB the user is on, in one
     * request. GET /api/v1/pab-sessions/timelines
     *
     * Use this instead of calling getWeeklyPlan per board just to draw a
     * timeline: one HorizontalTimeline is rendered per row on the advisor home
     * and PABs routes, so that pattern cost one request per board (10 boards =
     * 10 requests) and shipped every task, meeting and OKR to display six
     * integers per week. getWeeklyPlan is still the right call when a week's
     * actual contents are needed, e.g. the expanded week view.
     */
    getTimelines: async (weeks: number = 12): Promise<PabTimelinesResponse> => {
      const response = await authAxios.get(`${API_BASE}/pab-sessions/timelines`, {
        params: { weeks },
      });
      return response.data;
    },
    /**
     * Get compact timeline milestones for a PAB session.
     * GET /api/v1/pab-sessions/{id}/timeline-milestones
     * @param sessionId - The PAB session id
     * @param weeks - Number of weeks in timeline (default 12)
     */
    getTimelineMilestones: async (
      sessionId: number,
      weeks: number = 12,
    ): Promise<TimelineMilestonesResponse> => {
      const response = await authAxios.get(
        `${API_BASE}/pab-sessions/${sessionId}/timeline-milestones`,
        { params: { weeks } },
      );
      return response.data;
    },
    /**
     * Get goals dashboard metrics for a PAB session
     * GET /api/v1/pab-sessions/{session_id}/okrs/metrics
     * @param sessionId - The PAB session id
     * @returns {Promise<GoalsMetrics>}
     */
    getGoalsMetrics: async (sessionId: number): Promise<GoalsMetrics> => {
      const response = await authAxios.get(`${API_BASE}/pab-sessions/${sessionId}/okrs/metrics`);
      return response.data;
    },
    /**
     * Get AI OKR recommendations for a PAB session
     * GET /api/v1/pab-sessions/{session_id}/okrs/ai-recommendations
     * @param sessionId - The PAB session id
     * @param force - Optional boolean to force refresh
     * @returns {Promise<GoalsGroup[]>}
     */
    getOkrAiRecommendations: async (sessionId: number, force?: boolean): Promise<GoalsGroup[]> => {
      const params = force !== undefined ? { force } : {};
      const response = await authAxios.get(
        `${API_BASE}/pab-sessions/${sessionId}/okrs/ai-recommendations`,
        { params },
      );
      return response.data.items;
    },

    getAdvisorPabInvites: async (): Promise<PABCard[]> => {
      const response = await authAxios.get<PABCard[]>(`${API_BASE}/pab-sessions/invited/advisor`);
      return response.data;
    },

    /**
     * Save selected OKRs for a PAB session
     * POST /api/v1/pab-sessions/{session_id}/okrs/selected
     * @param sessionId - The PAB session id
     * @param goals - Array of goal objects with id and title to save
     * @returns {Promise<any>} (response data)
     */
    saveSelectedOkrs: async (sessionId: number, goals: GoalItem[]): Promise<GoalItem[]> => {
      const payload = goals.map((goal) => ({
        id: goal.id,
        title: goal.title,
        description: goal.description,
      }));
      const response = await authAxios.post(
        `${API_BASE}/pab-sessions/${sessionId}/okrs/selected`,
        payload,
      );
      return response.data;
    },
    /**
     * Add the achiever's OWN goal while choosing goals (Build-PAB / goal setting).
     * POST /api/v1/pab-sessions/{session_id}/okrs
     *
     * Created as a candidate exactly like an AI suggestion -- not finalized,
     * `isSuggested` so the recommendations list keeps returning it when the
     * step remounts, and with a category so it groups ("Your Goals") instead
     * of breaking the server's grouping, which requires one. "Confirm My
     * Goals" then finalizes it through saveSelectedOkrs like any other pick.
     */
    addManualOkr: async (
      sessionId: number,
      goal: { title: string; description?: string },
    ): Promise<GoalItem> => {
      const response = await authAxios.post(`${API_BASE}/pab-sessions/${sessionId}/okrs`, {
        title: goal.title,
        description: goal.description || null,
        category: { group: MANUAL_GOALS_GROUP, description: "Goals you added yourself" },
        isSuggested: true,
        isFinalized: false,
        status: "NOT_STARTED",
        visibility: "PRIVATE",
      });
      return {
        id: response.data.id,
        title: response.data.title,
        description: response.data.description ?? undefined,
      };
    },
    /**
     * Add the achiever's OWN task under a goal while choosing tasks.
     * POST /api/v1/pab-sessions/{session_id}/okrs/{okr_id}/okts
     *
     * Not `isSuggested`: the task list returns every task on the goal either
     * way, and a force-regenerate of AI tasks deletes only suggested ones, so
     * this keeps the achiever's own task safe from it. "Find My Advisors"
     * finalizes it through selectOktsWithTitles like any other pick.
     */
    addManualOkt: async (sessionId: number, okrId: number, title: string): Promise<TaskItem> => {
      const response = await authAxios.post(
        `${API_BASE}/pab-sessions/${sessionId}/okrs/${okrId}/okts`,
        { title, isSuggested: false, isFinalized: false, status: "NOT_STARTED" },
      );
      return response.data;
    },
    /**
     * Get AI-generated and saved tasks for an OKR or all finalized OKRs in a session
     * GET /api/v1/pab-sessions/{session_id}/okrs/{okr_id}/okts/ai-recommendations
     * @param sessionId - The PAB session id
     * @param okrId - The OKR id or 'all'
     * @returns {Promise<TaskGroup[]>}
     */
    getOktAiRecommendations: async (
      sessionId: number,
      okrId: string,
      force: boolean = false,
    ): Promise<TaskGroup[]> => {
      const response = await authAxios.get(
        `${API_BASE}/pab-sessions/${sessionId}/okrs/${okrId}/okts/ai-recommendations`,
        { params: { force } },
      );
      return response.data.items;
    },

    /**
     * List existing OKTs for an OKR (no AI generation)
     * GET /api/v1/pab-sessions/{session_id}/okrs/{okr_id}/okts
     * @param sessionId - The PAB session id
     * @param okrId - The OKR id
     * @returns {Promise<OKT[]>}
     */
    listOkts: async (sessionId: number, okrId: number): Promise<OKT[]> => {
      const response = await authAxios.get(
        `${API_BASE}/pab-sessions/${sessionId}/okrs/${okrId}/okts`,
      );
      return response.data;
    },

    /**
     * Save selected OKTs for a PAB session
     * POST /api/v1/pab-sessions/{session_id}/okrs/{okr_id}/okts/selected
     * @param sessionId - The PAB session id
     * @param okrId - The OKR id (or 'all' for all OKRs in session)
     * @param tasks - Array of task objects with id and title to save
     * @returns {Promise<TaskItem[]>} (response data)
     */
    /**
     * Select OKTs and update titles in single operation
     * POST /api/v1/pab-sessions/{session_id}/okrs/{okr_id}/okts/selection
     * @param sessionId - The PAB session id
     * @param okrId - The OKR id or "all"
     * @param finalizeData - Array of tasks to select with their updated titles
     * @returns {Promise<TaskItem[]>}
     */
    selectOktsWithTitles: async (
      sessionId: number,
      okrId: string,
      finalizeData: TaskItem[],
    ): Promise<TaskItem[]> => {
      const response = await authAxios.post(
        `${API_BASE}/pab-sessions/${sessionId}/okrs/${okrId}/okts/selection`,
        { finalize_data: finalizeData },
      );
      return response.data;
    },
    /**
     * Get selected OKTs for a PAB session
     * GET /api/v1/pab-sessions/{session_id}/okrs/{okr_id}/okts/selected
     * @param sessionId - The PAB session id
     * @param okrId - The OKR id (or 'all' for all OKRs in session)
     * @returns {Promise<TaskItem[]>} (response data)
     */
    getSelectedOkts: async (sessionId: number, okrId: string): Promise<TaskItem[]> => {
      const response = await authAxios.get(
        `${API_BASE}/pab-sessions/${sessionId}/okrs/${okrId}/okts/selected`,
      );
      return response.data;
    },

    // OKR Advisors
    /**
     * Get all advisors with assignment status for an OKR
     */
    getOkrAdvisors: async (sessionId: number, okrId: number): Promise<OKRAdvisor[]> => {
      const response = await authAxios.get(
        `${API_BASE}/pab-sessions/${sessionId}/okrs/${okrId}/advisors`,
      );
      return response.data;
    },

    /**
     * Update advisor assignment for an OKR
     *
     * This endpoint toggles the advisor assignment on/off server-side and
     * returns the resulting `is_assigned` state for the given PAB board.
     */
    updateOkrAdvisorAssignment: async (
      sessionId: number,
      okrId: number,
      membershipId: number,
    ): Promise<{ is_assigned: boolean }> => {
      const response = await authAxios.post(
        `${API_BASE}/pab-sessions/${sessionId}/okrs/${okrId}/advisors/assignment`,
        { membership_id: membershipId },
      );
      return response.data;
    },

    /**
     * Sync all advisor assignments for an OKR in a single request.
     * Called when the advisor dropdown is closed to persist all changes at once.
     */
    syncOkrAdvisorAssignments: async (
      sessionId: number,
      okrId: number,
      // v3: membership_id (program_members.id), not the legacy pab_board_id --
      // the backend OKRAdvisorBulkItem schema requires this field, and its
      // absence (still named pab_board_id here) produced a 422.
      advisors: { membership_id: number; is_assigned: boolean }[],
    ): Promise<OKRAdvisor[]> => {
      const response = await authAxios.patch(
        `${API_BASE}/pab-sessions/${sessionId}/okrs/${okrId}/advisors`,
        { advisors },
      );
      return response.data;
    },

    /**
     * Get only assigned advisors for an OKR
     */
    getAssignedAdvisors: async (sessionId: number, okrId: number): Promise<OKRAdvisor[]> => {
      const response = await authAxios.get(
        `${API_BASE}/pab-sessions/${sessionId}/okrs/${okrId}/advisors/assigned`,
      );
      return response.data;
    },

    // OKT Notes
    /**
     * List all notes for all OKTs under an OKR in one query
     */
    listOkrNotes: async (sessionId: number, okrId: number): Promise<OKTNote[]> => {
      const response = await authAxios.get(
        `${API_BASE}/pab-sessions/${sessionId}/okrs/${okrId}/notes`,
      );
      return response.data;
    },

    /**
     * List all notes for an OKT
     */
    listOktNotes: async (sessionId: number, okrId: number, oktId: number): Promise<OKTNote[]> => {
      const response = await authAxios.get(
        `${API_BASE}/pab-sessions/${sessionId}/okrs/${okrId}/okts/${oktId}/notes`,
      );
      return response.data;
    },

    /**
     * Create a note for an OKT
     */
    createOktNote: async (
      sessionId: number,
      okrId: number,
      oktId: number,
      content: string,
    ): Promise<OKTNote> => {
      const response = await authAxios.post(
        `${API_BASE}/pab-sessions/${sessionId}/okrs/${okrId}/okts/${oktId}/notes`,
        { content },
      );
      return response.data;
    },

    /**
     * Delete a note
     */
    deleteOktNote: async (
      sessionId: number,
      okrId: number,
      oktId: number,
      noteId: number,
    ): Promise<void> => {
      await authAxios.delete(
        `${API_BASE}/pab-sessions/${sessionId}/okrs/${okrId}/okts/${oktId}/notes/${noteId}`,
      );
    },

    // Meeting Todos
    /**
     * Get the current user's action items across every meeting in a session.
     *
     * These are the to-dos the Notetaker extracted from the session's own
     * recording, plus anything added manually. Only INCOMPLETE items assigned
     * to the caller are returned — the backend filters by assignee, so this is
     * "my to-dos", not the whole board's.
     *
     * GET /api/v1/pab-sessions/{session_id}/meeting-todos
     * @param sessionId - The PAB session id
     * @returns {Promise<AdvisorMeetingTodo[]>}
     */
    getSessionMeetingTodos: async (sessionId: number): Promise<AdvisorMeetingTodo[]> => {
      const response = await authAxios.get(`${API_BASE}/pab-sessions/${sessionId}/meeting-todos`);
      return response.data;
    },

    /**
     * Mark a meeting todo as completed
     * PATCH /api/v1/pab-sessions/{session_id}/meeting-todos/{todo_id}/complete
     */
    completeMeetingTodo: async (sessionId: number, todoId: number): Promise<AdvisorMeetingTodo> => {
      const response = await authAxios.patch(
        `${API_BASE}/pab-sessions/${sessionId}/meeting-todos/${todoId}/complete`,
      );
      return response.data;
    },

    /**
     * Delete a meeting todo
     * DELETE /api/v1/pab-sessions/{session_id}/meeting-todos/{todo_id}
     */
    deleteMeetingTodo: async (sessionId: number, todoId: number): Promise<void> => {
      await authAxios.delete(`${API_BASE}/pab-sessions/${sessionId}/meeting-todos/${todoId}`);
    },

    /**
     * Get matching advisors for a PAB session
     * GET /api/v1/pab-sessions/{session_id}/advisors/matching
     * @param sessionId - The PAB session id
     * @returns {Promise<PaginatedAdvisorsResponse>}
     */
    getMatchingAdvisors: async (sessionId: number): Promise<PaginatedAdvisorsResponse> => {
      const response = await authAxios.get(
        `${API_BASE}/pab-sessions/${sessionId}/advisors/ai-recommendations`,
      );
      return response.data;
    },
    /**
     * Get available AI advisor recommendations, excluding accepted/rejected advisors
     * GET /api/v1/pab-sessions/{session_id}/advisors/ai-recommendations/available
     * @param sessionId - The PAB session id
     * @returns {Promise<PaginatedAdvisorsResponse>}
     */
    getAvailableAdvisors: async (sessionId: number): Promise<PaginatedAdvisorsResponse> => {
      const response = await authAxios.get(
        `${API_BASE}/pab-sessions/${sessionId}/advisors/ai-recommendations/available`,
      );
      return response.data;
    },
    /**
     * Save selected advisors for a PAB session
     * POST /api/v1/pab-sessions/{session_id}/advisors/selected
     * @param sessionId - The PAB session id
     * @param advisorIds - Array of advisor ids to save
     * @returns {Promise<any>} (response data)
     */
    saveSelectedAdvisors: async (
      sessionId: number,
      advisorIds: { id: number }[],
    ): Promise<SuggestedAdvisor[]> => {
      const payload = advisorIds.map((id) => ({ id }));
      const response = await authAxios.post(
        `${API_BASE}/pab-sessions/${sessionId}/advisors/selected`,
        payload,
      );
      return response.data;
    },
    sendInvitations: async (
      sessionId: number,
      // v3: id is the advisor's user_id (SHA256 string) — AdvisorSelection.id.
      advisors: { id: string; inviteMessage: string }[],
    ): Promise<AdvisorInviteResponse> => {
      const payload = { advisors: advisors };
      const response = await authAxios.post(
        `${API_BASE}/pab-sessions/${sessionId}/advisors/send-invite`,
        payload,
      );
      return response.data;
    },

    updateAdvisorInviteStatus: async (
      sessionId: number,
      approvalStatus: "accepted" | "rejected",
      declineReason?: string,
    ): Promise<{ approval_status: string; decline_reason?: string }> => {
      const payload: {
        approval_status: "accepted" | "rejected";
        decline_reason?: string;
      } = {
        approval_status: approvalStatus,
      };

      // Only send decline_reason when rejecting
      if (approvalStatus === "rejected" && declineReason) {
        payload.decline_reason = declineReason;
      }

      const response = await authAxios.patch(
        `${API_BASE}/pab-sessions/${sessionId}/advisors/invite`,
        payload,
      );

      return response.data;
    },

    /**
     * List every PAB session belonging to the current achiever (pending,
     * forming, active, and completed alike) — unlike getActiveSession below,
     * this doesn't narrow down to a single session.
     * GET /api/v1/pab-sessions/me
     * Used by the Journey Portfolio page, which lists all of an achiever's
     * PABs (not just the current one) alongside their Expert Programs.
     * @returns {Promise<PabSession[]>}
     */
    listMine: async (): Promise<PabSession[]> => {
      try {
        const response = await authAxios.get<PaginatedPabSessionsResponse>(
          `${API_BASE}/pab-sessions/me`,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          { _skipForbiddenRedirect: true } as any,
        );
        return response.data.items;
      } catch (error) {
        if ((error as { response?: { status?: number } })?.response?.status === 403) {
          return [];
        }
        throw error;
      }
    },
    /**
     * Get active PAB session for the current achiever
     * GET /api/v1/pab-sessions/me
     * @returns {Promise<PabSession | null>}
     */
    getActiveSession: async (): Promise<PabSession | null> => {
      try {
        const response = await authAxios.get<PaginatedPabSessionsResponse>(
          `${API_BASE}/pab-sessions/me`,
          // This is an optional "do I have a PAB?" probe used on shared pages
          // (PabSessionContext, UnauthorizedPage, etc.). A pure advisor lacks the
          // PAB_SESSIONS_READ_ME permission and gets a 403 — which is expected, not
          // an access failure. Skip the global /unauthorized redirect and treat it
          // as "no session" so it can't bounce non-achievers to /unauthorized.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          { _skipForbiddenRedirect: true } as any,
        );
        return (
          response.data.items.find(
            (session) => session.status === "ACTIVE" || session.status === "COMPLETED",
          ) ?? null
        );
      } catch (error) {
        if ((error as { response?: { status?: number } })?.response?.status === 403) {
          return null;
        }
        throw error;
      }
    },
    /**
     * Get selected OKRs for a PAB session
     * GET /api/v1/pab-sessions/{session_id}/okrs/selected
     * @param sessionId - The PAB session id
     * @returns {Promise<SelectedOkr[]>}
     */
    getSelectedOkrs: async (sessionId: number): Promise<SelectedOkr[]> => {
      const response = await authAxios.get<PaginatedSelectedOkrsResponse>(
        `${API_BASE}/pab-sessions/${sessionId}/okrs/selected`,
      );
      return response.data.items;
    },

    /**
     * Get selected OKRs with OKTs for a PAB session
     * GET /api/v1/pab-sessions/{session_id}/okrs/selected-with-okts
     * @param sessionId - The PAB session id
     * @returns {Promise<SelectedOKRWithOKTs[]>}
     */
    getSelectedOkrsWithOkts: async (sessionId: number): Promise<SelectedOKRWithOKTs[]> => {
      const response = await authAxios.get(
        `${API_BASE}/pab-sessions/${sessionId}/okrs/selected-with-okts`,
      );
      return response.data;
    },
    /**
     * Get selected advisors for a PAB session
     * GET /api/v1/pab-sessions/{session_id}/advisors/selected
     * @param sessionId - The PAB session id
     * @returns {Promise<SelectedAdvisor[]>}
     */
    getSelectedAdvisors: async (sessionId: number): Promise<SelectedAdvisor[]> => {
      const response = await authAxios.get<SelectedAdvisorsResponse>(
        `${API_BASE}/pab-sessions/${sessionId}/advisors/selected`,
      );
      return response.data.selectedAdvisors;
    },
    /**
     * Get advisors with assignment status for all finalized OKRs in a PAB session
     * GET /api/v1/pab-sessions/{session_id}/okrs/advisors/assignments
     */
    getSessionOkrAdvisors: async (sessionId: number): Promise<SessionOkrAdvisors[]> => {
      const response = await authAxios.get(
        `${API_BASE}/pab-sessions/${sessionId}/okrs/advisors/assignments`,
      );
      return response.data.okrs;
    },
    /**
     * Get advisor availability for a PAB session
     * GET /api/v1/pab-sessions/{session_id}/advisors/availability
     * @param sessionId - The PAB session id
     * @returns {Promise<AdvisorsAvailabilityResponse>}
     */
    getAdvisorsAvailability: async (sessionId: number): Promise<AdvisorsAvailabilityResponse> => {
      const response = await authAxios.get<AdvisorsAvailabilityResponse>(
        `${API_BASE}/pab-sessions/${sessionId}/advisors/availability`,
      );
      return response.data;
    },
    /**
     * Get individual advisor availability for a PAB session
     * GET /api/v1/pab-sessions/{session_id}/advisors/{advisor_id}/availability
     * @param sessionId - The PAB session id
     * @param advisorId - The advisor id
     * @returns {Promise<IndividualAdvisorAvailabilityResponse>}
     */
    getIndividualAdvisorAvailability: async (
      sessionId: number,
      advisorId: number,
    ): Promise<IndividualAdvisorAvailabilityResponse> => {
      const response = await authAxios.get<IndividualAdvisorAvailabilityResponse>(
        `${API_BASE}/pab-sessions/${sessionId}/advisors/${advisorId}/availability`,
      );
      return response.data;
    },
    /**
     * Save session schedule for a PAB session
     * POST /api/v1/pab-sessions/{session_id}/schedule
     * @param sessionId - The PAB session id
     * @param schedule - The session schedule data
     * @returns {Promise<any>} (response data)
     */
    saveSessionSchedule: async (
      sessionId: number,
      schedule: Omit<SessionSchedule, "sessionId">,
    ): Promise<Meeting> => {
      const response = await authAxios.post(
        `${API_BASE}/pab-sessions/${sessionId}/meetings`,
        schedule,
      );
      return response.data;
    },
    /**
     * Save meeting slots for a PAB session
     * POST /api/v1/pab-sessions/{session_id}/meetings
     * @param sessionId - The PAB session id
     * @param slot - Meeting slot to save
     * @returns {Promise<any>} (response data)
     */
    saveMeetingSlot: async (sessionId: number, slot: MeetingSlot): Promise<Meeting> => {
      const response = await authAxios.post(`${API_BASE}/pab-sessions/${sessionId}/meetings`, slot);
      return response.data;
    },
    /**
     * Get AI meeting recommendations for a PAB session
     * GET /api/v1/pab-sessions/{session_id}/meetings/ai-recommendations
     * @param sessionId - The PAB session id
     * @returns {Promise<MeetingRecommendationsResponse>}
     */
    getMeetingRecommendations: async (
      sessionId: number,
    ): Promise<MeetingRecommendationsResponse> => {
      const response = await authAxios.get(
        `${API_BASE}/pab-sessions/${sessionId}/meetings/ai-recommendations`,
      );
      return response.data;
    },

    getUpcomingMeetings: async (sessionId: number): Promise<UpcomingMeetingsResponse> => {
      const response = await authAxios.get(
        `${API_BASE}/pab-sessions/${sessionId}/meetings/upcoming`,
      );
      return response.data;
    },

    /**
     * Get Nylas live calendar availability for all connected participants.
     * Slots are 60 minutes and constrained to declared open hours.
     * GET /api/v1/pab-sessions/{session_id}/nylas-availability
     */
    getNylasAvailability: async (
      sessionId: number,
      startTime: string,
      endTime: string,
    ): Promise<{
      timeSlots: { start_time: number; end_time: number }[];
      order: string[];
      openHours: { days: number[]; timezone: string; start: string; end: string }[];
      connectedCount: number;
      totalCount: number;
    }> => {
      const response = await authAxios.get(
        `${API_BASE}/pab-sessions/${sessionId}/nylas-availability`,
        { params: { start_time: startTime, end_time: endTime } },
      );
      return response.data;
    },

    /**
     * Create (or return existing) Nylas Scheduler config for a GROUP PAB meeting.
     * POST /api/v1/pab-sessions/{session_id}/scheduler
     */
    createGroupScheduler: async (
      sessionId: number,
    ): Promise<{
      id: number;
      nylasConfigurationId: string;
      slug: string;
      name: string;
      configType: string;
      durationMinutes: number;
      isNew: boolean;
    }> => {
      const response = await authAxios.post(`${API_BASE}/pab-sessions/${sessionId}/scheduler`);
      return response.data;
    },

    /**
     * Create (or return existing) Nylas Scheduler config for a 1:1 PAB meeting.
     * POST /api/v1/pab-sessions/{session_id}/advisors/{advisor_board_id}/scheduler
     */
    createOneOnOneScheduler: async (
      sessionId: number,
      advisorBoardId: number,
    ): Promise<{
      id: number;
      nylasConfigurationId: string;
      slug: string;
      name: string;
      configType: string;
      durationMinutes: number;
      isNew: boolean;
    }> => {
      const response = await authAxios.post(
        `${API_BASE}/pab-sessions/${sessionId}/advisors/${advisorBoardId}/scheduler`,
      );
      return response.data;
    },
    /**
     * Confirm a Nylas booking and persist meeting data in pab_meetings.
     * POST /api/v1/pab-sessions/{session_id}/meetings/confirm-nylas-booking
     * Idempotent — safe to call even if the webhook already created the meeting.
     */
    confirmNylasBooking: async (
      sessionId: number,
      schedulerConfigId: number,
      eventId: string,
      bookingId?: string,
      bookingRef?: string,
      /** Achiever-supplied name/description; omitted falls back to the canonical copy. */
      title?: string,
      description?: string,
    ): Promise<{
      meetingId: number;
      title: string;
      description: string | null;
      calendarEventId: string | null;
      meetingUrl: string | null;
      startTime: string | null;
      endTime: string | null;
      status: string;
      bookingRef: string | null;
    }> => {
      const response = await authAxios.post(
        `${API_BASE}/pab-sessions/${sessionId}/meetings/confirm-nylas-booking`,
        { schedulerConfigId, eventId, bookingId, bookingRef, title, description },
      );
      return response.data;
    },

    /**
     * State of the three mandatory PAB group sessions (Kick-off, Mid-PAB, Wrap-up).
     * GET /api/v1/pab-sessions/{session_id}/group-sessions
     *
     * Drives the Sessions tab's three slots: scheduled sessions render as cards,
     * unscheduled ones as "Create ..." placeholders. `canSchedule` is false for
     * Mid-PAB/Wrap-up until the kick-off exists, since it anchors their windows.
     */
    getGroupSessions: async (sessionId: number): Promise<PabGroupSessionsState> => {
      const response = await authAxios.get(`${API_BASE}/pab-sessions/${sessionId}/group-sessions`);
      return response.data;
    },

    /**
     * Nylas scheduler config for ONE group session, so it can be booked with the
     * NylasScheduling widget.
     * POST /api/v1/pab-sessions/{session_id}/group-sessions/{session_order}/scheduler
     *
     * sessionOrder: 1 = Kick-off (any date), 2 = Mid-PAB (week 6), 3 = Wrap-up (week 12).
     * Each session has its own config because the bookable window is a property of
     * the config. Idempotent — returns the existing config when there is one.
     */
    createGroupSessionScheduler: async (
      sessionId: number,
      sessionOrder: number,
      /**
       * Session name/description entered before picking a slot. These are baked
       * into the Nylas configuration, so Nylas stamps them onto the calendar
       * event and the booking webhook reads them back — no title race.
       */
      payload?: { title?: string; description?: string },
    ): Promise<{
      id: number;
      nylasConfigurationId: string | null;
      slug: string | null;
      name: string;
      configType: string;
      durationMinutes: number;
      sessionOrder: number;
      isNew: boolean;
    }> => {
      const response = await authAxios.post(
        `${API_BASE}/pab-sessions/${sessionId}/group-sessions/${sessionOrder}/scheduler`,
        payload ?? {},
      );
      return response.data;
    },

    /**
     * Clear everything anchored to the kick-off after it has been moved:
     * Mid-PAB, Wrap-up, and any 1:1 sessions.
     * POST /api/v1/pab-sessions/{session_id}/group-sessions/clear-later
     *
     * Their windows are measured from the kick-off, so once it shifts they sit in
     * the wrong weeks. Calendar events are deleted without notifying anyone —
     * the replacement bookings send their own invites.
     */
    clearLaterGroupSessions: async (
      sessionId: number,
    ): Promise<{
      removed: { id: number; title: string; sessionType: "GROUP" | "ONE_ON_ONE" }[];
      removedCount: number;
      groupCount: number;
      oneOnOneCount: number;
    }> => {
      const response = await authAxios.post(
        `${API_BASE}/pab-sessions/${sessionId}/group-sessions/clear-later`,
      );
      return response.data;
    },

    /**
     * Rename a scheduled group session / rewrite its description.
     * PATCH /api/v1/pab-sessions/{session_id}/group-sessions/{meeting_id}
     *
     * Title/description only — the calendar event is patched in place so invitees
     * keep their entry. Date/time changes go through the reschedule flow.
     */
    updateGroupSession: async (
      sessionId: number,
      meetingId: number,
      payload: { title?: string; description?: string },
    ): Promise<PabGroupSessionMeeting> => {
      const response = await authAxios.patch(
        `${API_BASE}/pab-sessions/${sessionId}/group-sessions/${meetingId}`,
        payload,
      );
      return response.data;
    },

    /**
     * Who the caller still owes feedback about for a session.
     * GET /api/v1/pab-sessions/{session_id}/meetings/{meeting_id}/feedback/pending
     *
     * The target list is decided server-side and differs by program type: in an
     * Expert Program an achiever rates the experts while an expert rates nobody,
     * whereas in a PAB everyone rates everyone. Clients should render whatever
     * comes back rather than deriving it from roles.
     */
    getSessionFeedbackPending: async (
      sessionId: number,
      meetingId: number,
    ): Promise<SessionFeedbackPending> => {
      const response = await authAxios.get(
        `${API_BASE}/pab-sessions/${sessionId}/meetings/${meetingId}/feedback/pending`,
      );
      return response.data;
    },

    /**
     * Submit the session rating plus every peer rating in one request.
     * POST /api/v1/pab-sessions/{session_id}/meetings/{meeting_id}/feedback
     *
     * Re-submitting updates the caller's existing rows instead of duplicating
     * them, so a retry is safe.
     */
    submitSessionFeedback: async (
      sessionId: number,
      meetingId: number,
      items: SessionFeedbackItem[],
    ): Promise<SessionFeedbackEntry[]> => {
      const response = await authAxios.post(
        `${API_BASE}/pab-sessions/${sessionId}/meetings/${meetingId}/feedback`,
        { items },
      );
      return response.data;
    },

    /**
     * Feedback the caller submitted for a session.
     * GET /api/v1/pab-sessions/{session_id}/meetings/{meeting_id}/feedback/mine
     *
     * Own submissions only -- feedback written about you is admin-only and has
     * no client-facing endpoint.
     */
    getMySessionFeedback: async (
      sessionId: number,
      meetingId: number,
    ): Promise<SessionFeedbackEntry[]> => {
      const response = await authAxios.get(
        `${API_BASE}/pab-sessions/${sessionId}/meetings/${meetingId}/feedback/mine`,
      );
      return response.data;
    },

    /**
     * Get achiever feedback for a meeting
     * GET /api/v1/pab-sessions/{session_id}/meetings/{meeting_id}/achiver_feedback
     */
    getMeetingFeedback: async (
      sessionId: number,
      meetingId: number,
    ): Promise<{
      id: number;
      meetingId: number;
      pabSessionId: number;
      achiverId: number;
      rateScore: number;
      comments: string | null;
      createdAt: string;
      updatedAt: string;
    }> => {
      const response = await authAxios.get(
        `${API_BASE}/pab-sessions/${sessionId}/meetings/${meetingId}/achiver_feedback`,
      );
      return response.data;
    },

    /**
     * Create achiever feedback for a meeting
     * POST /api/v1/pab-sessions/{session_id}/meetings/{meeting_id}/achiver_feedback
     */
    createMeetingFeedback: async (
      sessionId: number,
      meetingId: number,
      data: {
        rateScore: number;
        comments?: string | null;
      },
    ): Promise<{
      id: number;
      meetingId: number;
      pabSessionId: number;
      achiverId: number;
      rateScore: number;
      comments: string | null;
      createdAt: string;
      updatedAt: string;
    }> => {
      const response = await authAxios.post(
        `${API_BASE}/pab-sessions/${sessionId}/meetings/${meetingId}/achiver_feedback`,
        data,
      );
      return response.data;
    },

    /**
     * Update achiever feedback for a meeting
     * PUT /api/v1/pab-sessions/{session_id}/meetings/{meeting_id}/achiver_feedback
     */
    updateMeetingFeedback: async (
      sessionId: number,
      meetingId: number,
      data: {
        rateScore?: number;
        comments?: string | null;
      },
    ): Promise<{
      id: number;
      meetingId: number;
      pabSessionId: number;
      achiverId: number;
      rateScore: number;
      comments: string | null;
      createdAt: string;
      updatedAt: string;
    }> => {
      const response = await authAxios.put(
        `${API_BASE}/pab-sessions/${sessionId}/meetings/${meetingId}/achiver_feedback`,
        data,
      );
      return response.data;
    },

    /**
     * Create a new OKR for a PAB session
     * POST /api/v1/pab-sessions/{session_id}/okrs
     * @param sessionId - The PAB session id
     * @param okrData - The OKR create data
     * @returns {Promise<SelectedOKRWithOKTs>}
     */
    createOkr: async (
      sessionId: number,
      okrData: {
        title: string;
        description: string;
        dueDate?: string;
      },
    ): Promise<SelectedOKRWithOKTs> => {
      const response = await authAxios.post(`${API_BASE}/pab-sessions/${sessionId}/okrs`, {
        title: okrData.title,
        description: okrData.description,
        pabSessionId: sessionId,
        dueDate: okrData.dueDate || null,
        isSuggested: false,
        isFinalized: true,
        status: "NOT_STARTED",
        visibility: "PRIVATE",
      });
      return response.data;
    },
    /**
     * Update an OKR for a PAB session
     * PUT /api/v1/pab-sessions/{session_id}/okrs/{id}
     * @param sessionId - The PAB session id
     * @param okrId - The OKR id
     * @param okrData - The OKR update data
     * @returns {Promise<SelectedOKRWithOKTs>}
     */
    updateOkr: async (
      sessionId: number,
      okrId: number,
      okrData: {
        title?: string;
        description?: string;
        category?: string;
        smartGoalScore?: number;
        alignment?: OKRAlignment;
        isSuggested?: boolean;
        isFinalized?: boolean;
        dueDate?: string | null;
        progressPercentage?: number;
        evaluationComment?: string | null;
        visibility?: "PRIVATE" | "PUBLIC";
        status?: string | null;
        isSelectedForSessionPrep?: boolean;
      },
    ): Promise<SelectedOKRWithOKTs> => {
      const response = await authAxios.put(
        `${API_BASE}/pab-sessions/${sessionId}/okrs/${okrId}`,
        okrData,
      );
      return response.data;
    },

    /**
     * Mark an OKR as achieved and complete all its OKTs in a single request.
     * POST /api/v1/pab-sessions/{session_id}/okrs/{id}/mark-achieved
     */
    markOkrAsAchieved: async (sessionId: number, okrId: number): Promise<OKR> => {
      const response = await authAxios.post(
        `${API_BASE}/pab-sessions/${sessionId}/okrs/${okrId}/mark-achieved`,
      );
      return response.data;
    },

    /**
     * Delete (soft delete) an OKR for a PAB session
     * DELETE /api/v1/pab-sessions/{session_id}/okrs/{id}
     * @param sessionId - The PAB session id
     * @param okrId - The OKR id
     * @returns {Promise<OKR>}
     */
    deleteOkr: async (sessionId: number, okrId: number): Promise<OKR> => {
      const response = await authAxios.delete(
        `${API_BASE}/pab-sessions/${sessionId}/okrs/${okrId}`,
      );
      return response.data;
    },

    /**
     * Create a new OKT task for an OKR
     * POST /api/v1/pab-sessions/{session_id}/okrs/{okr_id}/okts
     * @param sessionId - The PAB session id
     * @param okrId - The OKR id
     * @param oktData - The OKT task data
     * @returns {Promise<OKT>}
     */
    createOkt: async (
      sessionId: number,
      okrId: number,
      oktData: {
        title: string;
        description: string;
        status: "NOT_STARTED" | "IN_PROGRESS" | "COMPLETED" | "NOT_REQUIRED" | "MISSED";
        isSuggested: boolean;
        isFinalized: boolean;
        startDate: string;
        dueDate: string;
        evaluationComment: string;
      },
    ): Promise<OKT> => {
      const response = await authAxios.post(
        `${API_BASE}/pab-sessions/${sessionId}/okrs/${okrId}/okts`,
        oktData,
      );
      return response.data;
    },

    /**
     * Update an OKT task for an OKR
     * PUT /api/v1/pab-sessions/{session_id}/okrs/{okr_id}/okts/{okt_id}
     * @param sessionId - The PAB session id
     * @param okrId - The OKR id
     * @param oktId - The OKT task id
     * @param oktData - The OKT task update data
     * @returns {Promise<OKT>}
     */
    updateOkt: async (
      sessionId: number,
      okrId: number,
      oktId: number,
      oktData: {
        title: string;
        description: string;
        status: "NOT_STARTED" | "IN_PROGRESS" | "COMPLETED" | "NOT_REQUIRED" | "MISSED";
        isSuggested: boolean;
        isFinalized?: boolean; // Optional - handled by saveSelectedOkts()
        startDate: string;
        dueDate: string;
        evaluationComment: string;
      },
    ): Promise<OKT> => {
      const response = await authAxios.put(
        `${API_BASE}/pab-sessions/${sessionId}/okrs/${okrId}/okts/${oktId}`,
        oktData,
      );
      return response.data;
    },
    /**
     * Get a specific meeting by ID for a PAB session
     * GET /api/v1/pab-sessions/{session_id}/meetings/{meeting_id}
     * @param sessionId - The PAB session id
     * @param meetingId - The meeting id
     * @returns {Promise<AdvisorMeeting>}
     */
    getMeeting: async (sessionId: number, meetingId: number): Promise<AdvisorMeeting> => {
      const response = await authAxios.get(
        `${API_BASE}/pab-sessions/${sessionId}/meetings/${meetingId}`,
      );
      return response.data;
    },
    /**
     * Get meeting documents for a specific meeting
     * GET /api/v1/pab-sessions/{session_id}/meetings/{meeting_id}/documents
     * @param sessionId - The PAB session id
     * @param meetingId - The meeting id
     * @returns {Promise<MeetingDocument>}
     */
    getMeetingDocuments: async (sessionId: number, meetingId: number): Promise<MeetingDocument> => {
      const response = await authAxios.get(
        `${API_BASE}/pab-sessions/${sessionId}/meetings/${meetingId}/documents`,
      );
      return response.data;
    },
    /**
     * Update meeting prep (goals and agendas) for a meeting
     * PUT /api/v1/pab-sessions/{session_id}/meetings/{meeting_id}/prep
     * @param sessionId - The PAB session id
     * @param meetingId - The meeting id
     * @param prepData - The meeting prep data
     * @returns {Promise<UpcomingMeeting | PastMeeting | MeetingByFormat>}
     */
    updateMeeting: async (
      sessionId: number,
      meetingId: number,
      meetingData: AdvisorMeetingUpdate,
    ): Promise<UpcomingMeeting | PastMeeting | MeetingByFormat> => {
      const response = await authAxios.put(
        `${API_BASE}/pab-sessions/${sessionId}/meetings/${meetingId}`,
        meetingData,
      );
      return response.data;
    },
    getPastMeetings: async (sessionId: number): Promise<PastMeetingsResponse> => {
      const response = await authAxios.get(`${API_BASE}/pab-sessions/${sessionId}/meetings/past`);
      return response.data;
    },
    /**
     * Get meetings by format for a PAB session
     * GET /api/v1/sessions/{session_id}/meetings/by-format?meeting_format={format}&page={page}&size={size}
     * @param sessionId - The PAB session id
     * @param meetingFormat - The meeting format (GROUP or ONE_ON_ONE)
     * @returns {Promise<MeetingsByFormatResponse>}
     */
    getMeetingsByFormat: async (
      sessionId: number,
      meetingFormat: string,
    ): Promise<MeetingsByFormatResponse> => {
      const response = await authAxios.get(
        `${API_BASE}/pab-sessions/${sessionId}/meetings/format/${meetingFormat}`,
      );
      return response.data;
    },

    /**
     * Mark session prep as ready for a meeting
     * POST /api/v1/pab-sessions/{session_id}/meetings/{meeting_id}/session-prep/ready
     */
    markSessionPrepReady: async (
      sessionId: number,
      meetingId: number,
      payload: {
        sessionOverview: string;
        sessionPrepGoals: string[];
        meetingAgendas: { description: string; duration: number }[];
        sessionPrepLink: string;
        sessionCallLink: string;
      },
    ): Promise<MarkSessionPrepReadyPayload> => {
      const response = await authAxios.post(
        `${API_BASE}/pab-sessions/${sessionId}/meetings/${meetingId}/session-prep/ready`,
        payload,
      );
      return response.data;
    },

    /**
     * Get messages for a PAB session
     * GET /api/v1/pab-sessions/{session_id}/messages
     * @param sessionId - The PAB session id
     * @returns {Promise<PabMessagesResponse>}
     */
    getMessages: async (
      sessionId: number,
      opts?: { skipForbiddenRedirect?: boolean },
    ): Promise<PabMessagesResponse> => {
      const response = await authAxios.get(
        `${API_BASE}/pab-sessions/${sessionId}/messages`,
        opts?.skipForbiddenRedirect ? ({ _skipForbiddenRedirect: true } as never) : undefined,
      );
      return response.data;
    },
    /**
     * Get direct messages between the current user and a specific advisor
     * GET /api/v1/pab-sessions/{session_id}/messages/direct?advisor_id={advisor_id}
     * @param sessionId - The PAB session id
     * @param advisorId - The advisor's user ID
     * @returns {Promise<PabMessagesResponse>}
     */
    getDirectMessages: async (
      sessionId: number,
      // v3: advisor identity is the user_id (SHA256 string), not an int PK.
      advisorId: string,
    ): Promise<PabMessagesResponse> => {
      const response = await authAxios.get(
        `${API_BASE}/pab-sessions/${sessionId}/messages/direct`,
        { params: { advisor_id: advisorId } },
      );
      return response.data;
    },
    /**
     * Mark a PAB session message as read
     * PATCH /api/v1/pab-sessions/{pab_session_id}/messages/{message_id}
     */
    markMessageAsRead: async (sessionId: number, messageId: number): Promise<PabMessage> => {
      const response = await authAxios.patch(
        `${API_BASE}/pab-sessions/${sessionId}/messages/${messageId}`,
        { isRead: true },
      );
      return response.data;
    },
    /**
     * Send a message to a PAB session
     * POST /api/v1/pab-sessions/{session_id}/messages
     * @param sessionId - The PAB session id
     * @param body - The message body
     * @param advisorId - Optional advisor ID to send message to a specific advisor
     * @returns {Promise<PabMessage>}
     */
    sendMessage: async (
      sessionId: number,
      body: string,
      // v3: advisor identity is the user_id (SHA256 string), not an int PK.
      advisorId?: string,
      /** Ids of resources ALREADY uploaded via resourcesApi.upload with
       *  source="MESSAGE". The send call links them; it never carries bytes. */
      attachmentIds?: number[],
    ): Promise<PabMessage> => {
      const payload: { body: string; advisorId?: string; attachmentIds?: number[] } = { body };
      if (advisorId !== undefined) {
        payload.advisorId = advisorId;
      }
      if (attachmentIds?.length) {
        payload.attachmentIds = attachmentIds;
      }
      const response = await authAxios.post(
        `${API_BASE}/pab-sessions/${sessionId}/messages`,
        payload,
      );
      return response.data;
    },
    /**
     * Send a 1:1 direct message to any PAB session member by user ID
     * POST /api/v1/pab-sessions/{session_id}/messages/direct
     */
    sendDirectMessageByUserId: async (
      sessionId: number,
      recipientUserId: string,
      body: string,
      /** Ids of resources already uploaded with source="MESSAGE". A 1:1
       *  attachment is private to the two participants — the server keeps it
       *  out of the shared Resources tab. */
      attachmentIds?: number[],
    ): Promise<PabMessage> => {
      const response = await authAxios.post(
        `${API_BASE}/pab-sessions/${sessionId}/messages/direct`,
        {
          recipient_user_id: recipientUserId,
          body,
          ...(attachmentIds?.length ? { attachmentIds } : {}),
        },
      );
      return response.data;
    },
    /**
     * Get 1:1 direct messages between the current user and another PAB session member
     * GET /api/v1/pab-sessions/{session_id}/messages/direct/{other_user_id}
     */
    getDirectMessagesByUserId: async (
      sessionId: number,
      otherUserId: string,
      opts?: { skipForbiddenRedirect?: boolean },
    ): Promise<PabMessage[]> => {
      const response = await authAxios.get(
        `${API_BASE}/pab-sessions/${sessionId}/messages/direct/${otherUserId}`,
        opts?.skipForbiddenRedirect ? ({ _skipForbiddenRedirect: true } as never) : undefined,
      );
      return response.data;
    },
    /**
     * Update OKT status and details
     * PUT /api/v1/pab-sessions/{session_id}/okrs/{okr_id}/okts/{okt_id}
     * @param sessionId - The PAB session id
     * @param okrId - The OKR id
     * @param oktId - The OKT id
     * @param data - The updated OKT data
     * @returns {Promise<OKT>}
     */
    updateOKT: async (
      sessionId: number,
      okrId: number,
      oktId: number,
      data: UpdateOKTRequest,
    ): Promise<OKT> => {
      const response = await authAxios.put(
        `${API_BASE}/pab-sessions/${sessionId}/okrs/${okrId}/okts/${oktId}`,
        data,
      );
      return response.data;
    },
    /**
     * Update OKR status and details
     * PUT /api/v1/pab-sessions/{session_id}/okrs/{id}
     * @param sessionId - The PAB session id
     * @param okrId - The OKR id
     * @param data - The updated OKR data
     * @returns {Promise<OKR>}
     */
    updateOKR: async (sessionId: number, okrId: number, data: UpdateOKRRequest): Promise<OKR> => {
      const response = await authAxios.put(
        `${API_BASE}/pab-sessions/${sessionId}/okrs/${okrId}`,
        data,
      );
      return response.data;
    },

    /**
     * Get AI-generated STAR stories for a specific OKR
     * POST /api/v1/pab-sessions/{session_id}/okrs/{okr_id}/stars/ai-recommendations
     * @param sessionId - The PAB session id
     * @param okrId - The OKR id
     * @returns {Promise<StarStory[]>}
     */
    getStarRecommendations: async (sessionId: number, okrId: number): Promise<StarStory[]> => {
      const response = await authAxios.post<PaginatedStarStoriesResponse>(
        `${API_BASE}/pab-sessions/${sessionId}/okrs/${okrId}/stars/ai-recommendations`,
      );
      return response.data.items;
    },

    /**
     * Save accepted STAR story for a specific OKR
     * POST /api/v1/pab-sessions/{session_id}/okrs/{okr_id}/stars
     * @param sessionId - The PAB session id
     * @param okrId - The OKR id
     * @param starStory - The STAR story to save
     * @returns {Promise<StarStory>}
     */
    saveStarStory: async (
      sessionId: number,
      okrId: number,
      starStory: StarStoryRequest,
    ): Promise<StarStoryWithMeta> => {
      const response = await authAxios.post<StarStoryWithMeta>(
        `${API_BASE}/pab-sessions/${sessionId}/okrs/${okrId}/stars`,
        {
          situation: starStory.situation,
          task: starStory.task,
          action: starStory.action,
          result: starStory.result,
          title: starStory.title ?? null,
        },
      );
      return response.data;
    },

    /** GET /pab-sessions/{sessionId}/okrs/{okrId}/stars — list all STARs for an OKR */
    getStarsForOkr: async (sessionId: number, okrId: number): Promise<StarStoryWithMeta[]> => {
      const response = await authAxios.get(
        `${API_BASE}/pab-sessions/${sessionId}/okrs/${okrId}/stars`,
      );
      return response.data;
    },

    /** GET /pab-sessions/{sessionId}/okrs/{okrId}/stars/{starId} — get one STAR */
    getStarById: async (
      sessionId: number,
      okrId: number,
      starId: number,
    ): Promise<StarStoryWithMeta> => {
      const response = await authAxios.get(
        `${API_BASE}/pab-sessions/${sessionId}/okrs/${okrId}/stars/${starId}`,
      );
      return response.data;
    },

    /** DELETE /pab-sessions/{sessionId}/okrs/{okrId}/stars/{starId} */
    deleteStarStory: async (sessionId: number, okrId: number, starId: number): Promise<void> => {
      await authAxios.delete(`${API_BASE}/pab-sessions/${sessionId}/okrs/${okrId}/stars/${starId}`);
    },

    /** PATCH /pab-sessions/{sessionId}/okrs/{okrId}/stars/{starId} — update one or more fields */
    updateStarStory: async (
      sessionId: number,
      okrId: number,
      starId: number,
      updates: Partial<Pick<StarStory, "situation" | "task" | "action" | "result" | "title">>,
    ): Promise<StarStoryWithMeta> => {
      const response = await authAxios.patch(
        `${API_BASE}/pab-sessions/${sessionId}/okrs/${okrId}/stars/${starId}`,
        updates,
      );
      return response.data;
    },

    /**
     * Send follow-up email to advisor for pending invite
     * POST /api/v1/pab-sessions/{session_id}/advisors/{advisor_id}/follow-up
     * @param sessionId - The PAB session id
     * @param advisorId - The advisor id
     * @returns {Promise<{ success: boolean; message?: string }>}
     */
    sendAdvisorFollowUp: async (
      sessionId: number,
      // v3: advisor identity is the user_id (SHA256 string), not an int PK.
      advisorId: string,
    ): Promise<{ success: boolean; message?: string }> => {
      const response = await authAxios.post(
        `${API_BASE}/pab-sessions/${sessionId}/advisors/${advisorId}/follow-up`,
        {},
      );
      return response.data;
    },
    /**
     * Submit achiever feedback for advisor or overall PAB experience
     * POST /api/v1/pab-sessions/{session_id}/feedback/achiever
     * @param sessionId - The PAB session id
     * @param feedbackData - The feedback data
     * @returns {Promise<AchieverFeedbackResponse>}
     */
    submitAchieverFeedback: async (
      sessionId: number,
      feedbackData: AchieverFeedbackRequest,
    ): Promise<AchieverFeedbackResponse> => {
      const response = await authAxios.post(
        `${API_BASE}/pab-sessions/${sessionId}/feedback/achiever`,
        feedbackData,
      );
      return response.data;
    },
    /**
     * Submit overall PAB feedback
     * POST /api/v1/pab-sessions/{pab_session_id}/feedback/overall
     * @param sessionId - The PAB session id
     * @param feedbackData - The overall feedback data
     * @returns {Promise<OverallPabFeedbackResponse>}
     */
    submitOverallPabFeedback: async (
      sessionId: number,
      feedbackData: OverallPabFeedbackRequest,
    ): Promise<OverallPabFeedbackResponse> => {
      const response = await authAxios.post(
        `${API_BASE}/pab-sessions/${sessionId}/feedback/overall`,
        feedbackData,
      );
      return response.data;
    },
    /**
     * Get all advisor feedback for a PAB session
     * GET /api/v1/pab-sessions/{pab_session_id}/feedback/achiever
     * @param sessionId - The PAB session id
     * @returns {Promise<GetAllAdvisorFeedbackResponse>}
     */
    getAllAdvisorFeedback: async (sessionId: number): Promise<GetAllAdvisorFeedbackResponse> => {
      const response = await authAxios.get(
        `${API_BASE}/pab-sessions/${sessionId}/feedback/achiever`,
      );
      return response.data;
    },
    /**
     * Get overall PAB feedback
     * GET /api/v1/pab-sessions/{session_id}/feedback/overall
     * @param sessionId - The PAB session id
     * @returns {Promise<GetOverallPabFeedbackResponse>}
     */
    getOverallPabFeedback: async (sessionId: number): Promise<GetOverallPabFeedbackResponse> => {
      const response = await authAxios.get(
        `${API_BASE}/pab-sessions/${sessionId}/feedback/overall`,
      );
      return response.data;
    },
    /**
     * Update overall PAB feedback
     * PUT /api/v1/pab-sessions/{pab_session_id}/feedback/overall
     * @param sessionId - The PAB session id
     * @param feedbackData - The updated feedback data
     * @returns {Promise<GetOverallPabFeedbackResponse>}
     */
    updateOverallPabFeedback: async (
      sessionId: number,
      feedbackData: OverallPabFeedbackRequest,
    ): Promise<GetOverallPabFeedbackResponse> => {
      const response = await authAxios.put(
        `${API_BASE}/pab-sessions/${sessionId}/feedback/overall`,
        feedbackData,
      );
      return response.data;
    },
    /**
     * Archive a PAB session and store the edit-access grace period end date.
     * POST /api/v1/pab-sessions/{pab_session_id}/archive
     * @param sessionId - The PAB session id
     * @param lockedAt - ISO-8601 datetime (last group meeting + 10 days), or null
     */
    archivePabSession: async (
      sessionId: number,
      lockedAt: string | null,
    ): Promise<{ id: number; status: string; lockedAt: string | null }> => {
      const response = await authAxios.post(`${API_BASE}/pab-sessions/${sessionId}/archive`, {
        lockedAt,
      });
      return response.data;
    },
    /**
     * Submit (create or update) advisor feedback for a specific advisor in a PAB session.
     * POST /api/v1/pab-sessions/{session_id}/advisors/{advisor_id}/feedback
     */
    submitAdvisorFeedback: async (
      sessionId: number,
      // v3: advisor identity is the user_id (SHA256 string), not an int PK.
      advisorId: string,
      feedbackData: {
        appreciateAboutAdvisor: string[];
        writtenTestimonial: string;
        isRecommended: boolean;
      },
    ): Promise<AdvisorFeedbackResponse> => {
      const response = await authAxios.post(
        `${API_BASE}/pab-sessions/${sessionId}/advisors/${advisorId}/feedback`,
        feedbackData,
      );
      return response.data;
    },

    /**
     * Get existing advisor feedback for a specific advisor in a PAB session.
     * GET /api/v1/pab-sessions/{session_id}/advisors/{advisor_id}/feedback
     */
    getAdvisorFeedback: async (
      sessionId: number,
      advisorId: string,
    ): Promise<AdvisorFeedbackResponse | null> => {
      try {
        const response = await authAxios.get(
          `${API_BASE}/pab-sessions/${sessionId}/advisors/${advisorId}/feedback`,
        );
        return response.data;
      } catch {
        return null;
      }
    },

    /**
     * Generate AI profile tags for an advisor based on their bio, role,
     * coaching strengths, and experience context.
     * POST /api/v1/pab-sessions/{session_id}/advisors/{advisor_id}/generate-tags
     */
    generateAdvisorTags: async (sessionId: number, advisorId: string): Promise<string[]> => {
      const response = await authAxios.post<{ advisorId: string; tags: string[] }>(
        `${API_BASE}/pab-sessions/${sessionId}/advisors/${advisorId}/generate-tags`,
      );
      return response.data.tags;
    },

    /**
     * Get all STAR stories across all OKRs for a session
     * GET /api/v1/pab-sessions/{session_id}/okrs/all/stars
     */
    getAllStars: async (sessionId: number): Promise<StarStoryWithMeta[]> => {
      const response = await authAxios.get(`${API_BASE}/pab-sessions/${sessionId}/okrs/all/stars`);
      return response.data.map((star: StarStoryWithMeta) => ({
        ...star,
        id: star.id,
        okrId: star.okrId,
        pabSessionId: star.pabSessionId,
      }));
    },
    /**
     * Get minimal onboarding status for achiever
     * GET /api/v1/pab-sessions/onboarding-status
     * @returns {Promise<OnboardingStatus>}
     */
    getOnboardingStatus: async (): Promise<OnboardingStatus> => {
      const response = await authAxios.get(`${API_BASE}/pab-sessions/onboarding-status`);
      return response.data;
    },

    /** GET /pab-sessions/{sessionId}/wrapup/metrics */
    getWrapupMetrics: async (sessionId: number): Promise<WrapupMetrics> => {
      const response = await authAxios.get(`${API_BASE}/pab-sessions/${sessionId}/wrapup/metrics`);
      return response.data;
    },

    /** GET /pab-sessions/{sessionId}/wrapup/weeks-journey */
    getWrapupWeeksJourney: async (sessionId: number): Promise<WrapupWeeksJourney> => {
      const response = await authAxios.get(
        `${API_BASE}/pab-sessions/${sessionId}/wrapup/weeks-journey`,
      );
      return response.data;
    },
  },
  growthAreas: {
    /**
     * Fetch growth areas based on primary function and role level
     * GET /api/v1/growth-areas/?primary_function={primaryFunction}&role_level={roleLevel}
     * @param primaryFunction - The primary function
     * @param roleLevel - The role level
     * @param signal - Optional AbortSignal to cancel the request
     * @returns {Promise<FetchGrowthAreasResponse>}
     */
    fetchGrowthAreas: async (
      primaryFunction: string,
      roleLevel: string,
      signal?: AbortSignal,
    ): Promise<FetchGrowthAreasResponse> => {
      const params = new URLSearchParams({
        primary_function: primaryFunction,
        role_level: roleLevel,
      });
      const response = await authAxios.get(`${API_BASE}/growth-areas/?${params.toString()}`, {
        signal, // Pass the signal to axios
      });
      return response.data;
    },
    /**
     * Fetch role levels based on primary function
     * GET /api/v1/growth-areas/role-levels?primary_function={primaryFunction}
     * @param primaryFunction - The primary function
     * @param signal - Optional AbortSignal to cancel the request
     * @returns {Promise<FetchRoleLevelsResponse>}
     */
    fetchRoleLevels: async (
      primaryFunction: string,
      signal?: AbortSignal,
    ): Promise<FetchRoleLevelsResponse> => {
      const params = new URLSearchParams({
        primary_function: primaryFunction,
      });
      const response = await authAxios.get(
        `${API_BASE}/growth-areas/role-levels?${params.toString()}`,
        { signal }, // Pass the signal to axios
      );
      return response.data;
    },
    /**
     * Fetch all growth areas for a primary function (across all role levels)
     * GET /api/v1/growth-areas/all?primary_function={primaryFunction}
     * @param primaryFunction - The primary function
     * @param signal - Optional AbortSignal to cancel the request
     * @returns {Promise<FetchGrowthAreasResponse>}
     */
    fetchAllGrowthAreas: async (
      primaryFunction: string,
      signal?: AbortSignal,
    ): Promise<FetchGrowthAreasResponse> => {
      const params = new URLSearchParams({
        primary_function: primaryFunction,
      });
      const response = await authAxios.get(`${API_BASE}/growth-areas/all?${params.toString()}`, {
        signal,
      });
      return response.data;
    },
  },

  // CRM Leads methods
  crmLeads: {
    /**
     * Get advisor form prefill data by email from CRM leads
     * GET /api/v1/crm-leads/lookup?email={email}&entity_type={entityType}
     * @param email - Email address to lookup
     * @param entityType - Entity type (advisor or achiever), defaults to advisor
     * @returns {Promise<AdvisorLeadLookupResponse>}
     */
    getPrefillData: async (
      email: string,
      entityType: string = "advisor",
    ): Promise<AdvisorLeadLookupResponse> => {
      const response = await authAxios.get(
        `${API_BASE}/crm-leads/lookup?email=${encodeURIComponent(email)}&entity_type=${entityType}`,
      );
      return response.data;
    },
    /**
     * The signed-in user's own Expert CRM record, shaped for Expert signup
     * steps 2-4. `null` when the CRM has none for their email.
     * GET /api/v1/crm-leads/me/expert-prefill
     */
    getMyExpertPrefill: async (): Promise<ExpertSignupPrefill | null> => {
      const response = await authAxios.get(`${API_BASE}/crm-leads/me/expert-prefill`);
      return response.data ?? null;
    },
  },

  // Lead methods for HubSpot integration
  leads: {
    /**
     * Sign up a lead from HubSpot form
     * POST /api/v1/leads/signup
     * @param data - Lead signup data
     * @returns {Promise<LeadSignupResponse>}
     */
    signup: async (data: LeadSignupRequest): Promise<LeadSignupResponse> => {
      const response = await publicAxios.post<LeadSignupResponse>(`${API_BASE}/leads/signup`, data);
      return response.data;
    },

    /**
     * Verify lead email token from email link
     * GET /api/v1/leads/verify?token={token}
     * @param token - Verification token from email
     * @returns {Promise<LeadVerificationResponse>}
     */
    verifyEmail: async (token: string): Promise<LeadVerificationResponse> => {
      const response = await publicAxios.get<LeadVerificationResponse>(`${API_BASE}/leads/verify`, {
        params: { token },
      });
      return response.data;
    },

    /**
     * Match lead with advisors using AI
     * POST /api/v1/leads/match/{leadId}?max_advisors={maxAdvisors}
     * @param leadId - Lead ID from leads table
     * @param maxAdvisors - Maximum number of advisors to return (default: 9)
     * @returns {Promise<LeadMatchingResponse>}
     */
    matchWithAdvisors: async (
      leadId: number,
      maxAdvisors: number = 9,
    ): Promise<LeadMatchingResponse> => {
      const response = await publicAxios.post<LeadMatchingResponse>(
        `${API_BASE}/leads/match/${leadId}`,
        null,
        {
          params: { max_advisors: maxAdvisors },
        },
      );
      return response.data;
    },

    /**
     * Track demo call booking and get HubSpot booking URL
     * POST /api/v1/leads/book-demo/{leadId}
     * @param leadId - Lead ID from leads table
     * @param data - Optional booking data
     * @returns {Promise<DemoCallBookingResponse>}
     */
    bookDemoCall: async (
      leadId: number,
      data?: DemoCallBookingRequest,
    ): Promise<DemoCallBookingResponse> => {
      const response = await publicAxios.post<DemoCallBookingResponse>(
        `${API_BASE}/leads/book-demo/${leadId}`,
        data || {},
      );
      return response.data;
    },

    /**
     * Convert lead to full user (achiever or advisor)
     * POST /api/v1/leads/convert/{leadId}
     * 🔒 Requires Admin Authentication
     * @param leadId - Lead ID from leads table (e.g., 42)
     * @param data - Conversion data (target role, create PAB session)
     * @returns {Promise<LeadConversionResponse>}
     */
    convertToUser: async (
      leadId: number,
      data: LeadConversionRequest,
    ): Promise<LeadConversionResponse> => {
      const response = await authAxios.post<LeadConversionResponse>(
        `${API_BASE}/leads/convert/${leadId}`,
        data,
      );
      return response.data;
    },
  },

  /**
   * Admin panel (/api/v1/admin). Every endpoint requires the `super_admin`
   * role server-side (require_super_admin) in all environments, so a non-admin
   * calling these gets a 403 regardless of what the UI shows.
   */
  admin: {
    getStats: async (): Promise<AdminStats> => {
      const response = await authAxios.get(`${API_BASE}/admin/stats`);
      return response.data;
    },

    /**
     * Revenue tab: every program and PAB with its cohort's payments nested.
     *
     * One call rather than a list plus a fetch-per-expanded-row — admin row
     * counts are in the tens, and the server batches every lookup, so nesting
     * is cheaper than the request fan-out an expand handler would cause.
     */
    getRevenue: async (): Promise<AdminRevenue> => {
      const response = await authAxios.get(`${API_BASE}/admin/revenue`);
      return response.data;
    },

    listUsers: async (params: AdminUserListParams = {}): Promise<Paginated<AdminUserRow>> => {
      const response = await authAxios.get(`${API_BASE}/admin/users`, { params });
      return response.data;
    },
    getUser: async (userId: string): Promise<AdminUserDetail> => {
      const response = await authAxios.get(`${API_BASE}/admin/users/${userId}`);
      return response.data;
    },
    setUserStatus: async (
      userId: string,
      isActive: boolean,
      notes?: string,
    ): Promise<AdminUserDetail> => {
      const response = await authAxios.post(`${API_BASE}/admin/users/${userId}/status`, {
        isActive,
        notes,
      });
      return response.data;
    },
    /** Allow or disallow a user from starting the Expert application. */
    setExpertAccess: async (userId: string, allowed: boolean): Promise<AdminUserDetail> => {
      const response = await authAxios.post(`${API_BASE}/admin/users/${userId}/expert-access`, {
        allowed,
      });
      return response.data;
    },
    /** Upload or replace the user's Getting Started LinkedIn carousel PDF. */
    uploadLinkedinCarousel: async (userId: string, file: File): Promise<AdminLinkedinCarousel> => {
      const formData = new FormData();
      formData.append("file", file);
      const response = await authAxios.post(
        `${API_BASE}/admin/users/${userId}/linkedin-carousel`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return response.data;
    },
    deleteLinkedinCarousel: async (userId: string): Promise<AdminLinkedinCarousel> => {
      const response = await authAxios.delete(
        `${API_BASE}/admin/users/${userId}/linkedin-carousel`,
      );
      return response.data;
    },

    /** Reported MindShare posts and comments — the moderation queue. */
    listReports: async (
      params: {
        status?: string;
        targetType?: string;
        reason?: string;
        page?: number;
        size?: number;
      } = {},
    ): Promise<Paginated<AdminReport>> => {
      const response = await authAxios.get(`${API_BASE}/admin/reports`, { params });
      return response.data;
    },
    /** `dismiss` the report, or `remove` the content (closes all its reports). */
    decideReport: async (reportId: number, action: "dismiss" | "remove"): Promise<AdminReport> => {
      const response = await authAxios.post(`${API_BASE}/admin/reports/${reportId}/decision`, {
        action,
      });
      return response.data;
    },

    listApplications: async (
      params: AdminApplicationListParams = {},
    ): Promise<Paginated<AdminApplicationRow>> => {
      const response = await authAxios.get(`${API_BASE}/admin/applications`, { params });
      return response.data;
    },
    getApplication: async (userId: string): Promise<AdminApplicationDetailOut> => {
      const response = await authAxios.get(`${API_BASE}/admin/applications/${userId}`);
      return response.data;
    },
    decideApplication: async (
      userId: string,
      decision: AdminApplicationDecision,
      notes?: string,
    ): Promise<AdminApplicationDetailOut> => {
      const response = await authAxios.post(`${API_BASE}/admin/applications/${userId}/decision`, {
        decision,
        notes,
      });
      return response.data;
    },

    /**
     * Approve or reject one applied role. Approving is a no-op server-side —
     * the flag is already true — while rejecting clears it, dropping the role
     * from the applicant's qualified capabilities.
     */
    decideApplicationRole: async (
      userId: string,
      role: string,
      approved: boolean,
    ): Promise<AdminApplicationDetailOut> => {
      const response = await authAxios.post(`${API_BASE}/admin/applications/${userId}/roles`, {
        role,
        approved,
      });
      return response.data;
    },

    /**
     * Approve or reject one "Primary Goal for Joining". Recorded as a verdict
     * beside the applicant's choice — their selection itself is never changed.
     */
    decideApplicationGoal: async (
      userId: string,
      goal: string,
      approved: boolean,
    ): Promise<AdminApplicationDetailOut> => {
      const response = await authAxios.post(`${API_BASE}/admin/applications/${userId}/goals`, {
        goal,
        approved,
      });
      return response.data;
    },

    listPrograms: async (
      params: AdminProgramListParams = {},
    ): Promise<Paginated<AdminProgramRow>> => {
      // programType is joined with commas rather than passed as an array:
      // axios serialises `["EXPERT","PEER_ADVISORY"]` as `programType[]=...`,
      // a key FastAPI will not bind to `programType`, so the filter would be
      // silently dropped and every type would come back -- on an approval
      // queue, that means showing a reviewer rows they must not act on.
      const { programType, ...rest } = params;
      const query = {
        ...rest,
        ...(programType
          ? { programType: Array.isArray(programType) ? programType.join(",") : programType }
          : {}),
      };
      const response = await authAxios.get(`${API_BASE}/admin/programs`, { params: query });
      return response.data;
    },
    getProgram: async (externalId: string): Promise<AdminProgramDetail> => {
      const response = await authAxios.get(`${API_BASE}/admin/programs/${externalId}`);
      return response.data;
    },
    decideProgram: async (
      externalId: string,
      approve: boolean,
      reason?: string,
    ): Promise<AdminProgramDetail> => {
      const response = await authAxios.post(`${API_BASE}/admin/programs/${externalId}/decision`, {
        approve,
        reason,
      });
      return response.data;
    },
    listAdvisoryBoards: async (
      params: AdminAdvisoryBoardListParams = {},
    ): Promise<Paginated<AdminAdvisoryBoardRow>> => {
      const response = await authAxios.get(`${API_BASE}/admin/advisory-boards`, { params });
      return response.data;
    },
    getAdvisoryBoard: async (externalId: string): Promise<AdminAdvisoryBoardDetail> => {
      const response = await authAxios.get(`${API_BASE}/admin/advisory-boards/${externalId}`);
      return response.data;
    },

    listSessions: async (
      params: AdminSessionListParams = {},
    ): Promise<Paginated<AdminSessionRow>> => {
      const response = await authAxios.get(`${API_BASE}/admin/sessions`, { params });
      return response.data;
    },

    setProgramStatus: async (
      externalId: string,
      programStatus: AdminSettableProgramStatus,
      reason?: string,
    ): Promise<AdminProgramDetail> => {
      const response = await authAxios.post(`${API_BASE}/admin/programs/${externalId}/status`, {
        status: programStatus,
        reason,
      });
      return response.data;
    },

    /**
     * Coupons tab. Status and category are derived and filtered server-side;
     * the list comes back already paged.
     */
    coupons: {
      list: async (params: AdminCouponListParams = {}): Promise<Paginated<AdminCouponRow>> => {
        const response = await authAxios.get(`${API_BASE}/admin/coupons`, { params });
        return response.data;
      },
      stats: async (): Promise<AdminCouponStats> => {
        const response = await authAxios.get(`${API_BASE}/admin/coupons/stats`);
        return response.data;
      },
      /**
       * ACTIVE Expert and Peer Advisory programs a coupon can go on, each with
       * its creator. `search` needs 2+ characters.
       */
      programOptions: async (search: string): Promise<AdminCouponProgramOption[]> => {
        const response = await authAxios.get(`${API_BASE}/admin/coupons/programs`, {
          params: { search },
        });
        return response.data;
      },
      pricePreview: async (
        params: AdminCouponPricePreviewParams,
      ): Promise<AdminCouponPricePreview> => {
        // Nulls dropped: FastAPI reads `percentOff=` as an invalid integer.
        const query = Object.fromEntries(
          Object.entries(params).filter(([, value]) => value != null),
        );
        const response = await authAxios.get(`${API_BASE}/admin/coupons/price-preview`, {
          params: query,
        });
        return response.data;
      },
      checkCode: async (code: string, excludeId?: number): Promise<AdminCouponCodeCheck> => {
        const response = await authAxios.get(`${API_BASE}/admin/coupons/code-check`, {
          params: { code, ...(excludeId ? { excludeId } : {}) },
        });
        return response.data;
      },
      get: async (couponId: number): Promise<AdminCouponDetail> => {
        const response = await authAxios.get(`${API_BASE}/admin/coupons/${couponId}`);
        return response.data;
      },
      redemptions: async (
        couponId: number,
        params: { page?: number; size?: number } = {},
      ): Promise<Paginated<AdminCouponRedemptionRow>> => {
        const response = await authAxios.get(`${API_BASE}/admin/coupons/${couponId}/redemptions`, {
          params,
        });
        return response.data;
      },
      create: async (payload: AdminCouponInput): Promise<AdminCouponDetail> => {
        const response = await authAxios.post(`${API_BASE}/admin/coupons`, payload);
        return response.data;
      },
      /** Partial: a key left out is untouched, an explicit null clears it. */
      update: async (
        couponId: number,
        payload: Partial<AdminCouponInput>,
      ): Promise<AdminCouponDetail> => {
        const response = await authAxios.patch(`${API_BASE}/admin/coupons/${couponId}`, payload);
        return response.data;
      },
      remove: async (couponId: number): Promise<void> => {
        await authAxios.delete(`${API_BASE}/admin/coupons/${couponId}`);
      },
      createBulk: async (
        payload: AdminBulkCouponInput,
      ): Promise<{ created: AdminBulkCouponRecipient[] }> => {
        const response = await authAxios.post(`${API_BASE}/admin/coupons/bulk`, payload);
        return response.data;
      },
    },
  },
};

/**
 * Get dashboard summary including OKRs for achievers
 * GET /api/v1/dashboard/achievers/summary
 * @returns {Promise<DashboardSummaryResponse>}
 */
export const getDashboardSummary = async (): Promise<DashboardSummaryResponse> => {
  const response = await authAxios.get(`${API_BASE}/dashboard/achievers/summary`);
  return response.data;
};

// ==================== NYLAS CALENDAR INTEGRATION ====================

import type {
  CalendarInfo,
  NylasAuthUrlResponse,
  NylasCallbackResponse,
  NylasConnectionStatus,
  SchedulerConfig,
  SchedulerConfigCreate,
  BookingInfo,
  AvailabilityCheckRequest,
  AvailabilityResponse,
  WorkingHoursPayload,
  WorkingHoursResponse,
} from "@/types/nylas";

export const nylasApi = {
  /**
   * Get Nylas OAuth URL for calendar connection
   * GET /api/v1/nylas/auth/url
   */
  getAuthUrl: async (returnPath?: string): Promise<NylasAuthUrlResponse> => {
    const params = returnPath ? { return_path: returnPath } : undefined;
    const response = await authAxios.get(`${API_BASE}/nylas/auth/url`, { params });
    return response.data;
  },

  /**
   * Handle OAuth callback (exchange code for grant)
   * POST /api/v1/nylas/auth/callback?code={code}&state={state}
   */
  handleCallback: async (code: string, state: string): Promise<NylasCallbackResponse> => {
    const response = await publicAxios.post(
      `${API_BASE}/nylas/auth/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`,
    );
    return response.data;
  },

  /**
   * Get current user's calendar connection status
   * GET /api/v1/nylas/connection-status
   */
  getConnectionStatus: async (): Promise<NylasConnectionStatus> => {
    const response = await authAxios.get(`${API_BASE}/nylas/connection-status`);
    return response.data;
  },

  /**
   * Get a specific user's calendar connection status
   * GET /api/v1/nylas/connection-status/{userId}
   */
  getUserConnectionStatus: async (userId: string): Promise<NylasConnectionStatus> => {
    const response = await authAxios.get(`${API_BASE}/nylas/connection-status/${userId}`);
    return response.data;
  },

  /**
   * Re-sync calendars for a grant whose initial sync failed (e.g. provider rate limit)
   * POST /api/v1/nylas/grants/{grantId}/sync-calendars
   */
  syncGrantCalendars: async (
    grantId: string,
  ): Promise<{ grantId: string; calendarIds: string[]; synced: boolean }> => {
    const response = await authAxios.post(`${API_BASE}/nylas/grants/${grantId}/sync-calendars`);
    return response.data;
  },

  /**
   * Disconnect a calendar account
   * DELETE /api/v1/nylas/grants/{grantId}
   */
  disconnectCalendar: async (grantId: string): Promise<void> => {
    await authAxios.delete(`${API_BASE}/nylas/grants/${grantId}`);
  },

  /**
   * Create a scheduler configuration for booking meetings
   * POST /api/v1/nylas/scheduler/configs
   */
  createSchedulerConfig: async (payload: SchedulerConfigCreate): Promise<SchedulerConfig> => {
    const response = await authAxios.post(`${API_BASE}/nylas/scheduler/configs`, payload);
    return response.data;
  },

  /**
   * List scheduler configurations
   * GET /api/v1/nylas/scheduler/configs
   */
  getSchedulerConfigs: async (params?: {
    organizerUserId?: string;
    pabSessionId?: number;
    configType?: string;
  }): Promise<SchedulerConfig[]> => {
    const response = await authAxios.get(`${API_BASE}/nylas/scheduler/configs`, { params });
    return response.data;
  },

  /**
   * Get a specific scheduler configuration
   * GET /api/v1/nylas/scheduler/configs/{configId}
   */
  getSchedulerConfig: async (configId: number): Promise<SchedulerConfig> => {
    const response = await authAxios.get(`${API_BASE}/nylas/scheduler/configs/${configId}`);
    return response.data;
  },

  /**
   * Get booking info for NylasScheduling component
   * GET /api/v1/nylas/scheduler/configs/{configId}/booking-info
   */
  getBookingInfo: async (configId: number): Promise<BookingInfo> => {
    const response = await authAxios.get(
      `${API_BASE}/nylas/scheduler/configs/${configId}/booking-info`,
    );
    return response.data;
  },

  /**
   * Delete a scheduler configuration
   * DELETE /api/v1/nylas/scheduler/configs/{configId}
   */
  deleteSchedulerConfig: async (configId: number): Promise<void> => {
    await authAxios.delete(`${API_BASE}/nylas/scheduler/configs/${configId}`);
  },

  /**
   * Check mutual availability for participants
   * POST /api/v1/nylas/availability
   */
  checkAvailability: async (payload: AvailabilityCheckRequest): Promise<AvailabilityResponse> => {
    const response = await authAxios.post(`${API_BASE}/nylas/availability`, payload);
    return response.data;
  },

  /**
   * List all calendars available in a grant, each annotated with isSelected.
   * GET /api/v1/nylas/grants/{grantId}/calendars
   */
  getGrantCalendars: async (grantId: string): Promise<CalendarInfo[]> => {
    const response = await authAxios.get(`${API_BASE}/nylas/grants/${grantId}/calendars`);
    return response.data;
  },

  /**
   * Save the user's calendar selection for a grant.
   * PUT /api/v1/nylas/grants/{grantId}/calendars
   */
  updateGrantCalendars: async (grantId: string, calendarIds: string[]): Promise<void> => {
    await authAxios.put(`${API_BASE}/nylas/grants/${grantId}/calendars`, { calendarIds });
  },

  /**
   * Get the current user's working hours (stored availability slots as Nylas OpenHours[]).
   * GET /api/v1/nylas/working-hours
   */
  getWorkingHours: async (): Promise<WorkingHoursResponse> => {
    const response = await authAxios.get(`${API_BASE}/nylas/working-hours`);
    return response.data;
  },

  /**
   * Save the current user's working hours.
   * PUT /api/v1/nylas/working-hours
   */
  saveWorkingHours: async (payload: WorkingHoursPayload): Promise<WorkingHoursResponse> => {
    const response = await authAxios.put(`${API_BASE}/nylas/working-hours`, payload);
    return response.data;
  },
};

export const programsApi = {
  /**
   * Session feedback for an Expert Program session.
   *
   * Same contract as the PAB equivalents on `api.pabSessions`, addressed by the
   * program's external id. The server applies the Expert Program rule: an
   * achiever rates the experts, an expert rates only the session.
   */
  getSessionFeedbackPending: async (
    externalId: string,
    meetingId: number,
  ): Promise<SessionFeedbackPending> => {
    const response = await authAxios.get(
      `${API_BASE}/expert-programs/${externalId}/sessions/${meetingId}/feedback/pending`,
    );
    return response.data;
  },

  submitSessionFeedback: async (
    externalId: string,
    meetingId: number,
    items: SessionFeedbackItem[],
  ): Promise<SessionFeedbackEntry[]> => {
    const response = await authAxios.post(
      `${API_BASE}/expert-programs/${externalId}/sessions/${meetingId}/feedback`,
      { items },
    );
    return response.data;
  },

  getMySessionFeedback: async (
    externalId: string,
    meetingId: number,
  ): Promise<SessionFeedbackEntry[]> => {
    const response = await authAxios.get(
      `${API_BASE}/expert-programs/${externalId}/sessions/${meetingId}/feedback/mine`,
    );
    return response.data;
  },

  list: async (): Promise<ProgramListResponse> => {
    const response = await authAxios.get(`${API_BASE}/programs`);
    return response.data;
  },
  getAdvisorStats: async (): Promise<AdvisorProgramStats> => {
    const response = await authAxios.get(`${API_BASE}/expert-programs/stats`);
    return response.data;
  },
  /** Newest enrollees across the expert's running programs, newest first. */
  getRecentEnrollments: async (limit = 3): Promise<RecentEnrollment[]> => {
    const response = await authAxios.get(`${API_BASE}/expert-programs/recent-enrollments`, {
      params: { limit },
    });
    return response.data;
  },
  /** The expert's next group session across their running programs, or null. */
  getNextSession: async (): Promise<NextProgramSession | null> => {
    const response = await authAxios.get(`${API_BASE}/expert-programs/next-session`);
    return response.data ?? null;
  },
  getGroupedAdvisorPrograms: async (): Promise<GroupedAdvisorPrograms> => {
    const response = await authAxios.get(`${API_BASE}/expert-programs/grouped`);
    return response.data;
  },
  join: async (externalId: string, urlRole: "achiever" | "advisor"): Promise<void> => {
    await authAxios.post(`${API_BASE}/expert-programs/${externalId}/join?url_role=${urlRole}`);
  },
  createExpertProgram: async (data: {
    name: string;
    description?: string;
    who_is_this_program_for?: string;
    program_outcomes?: string;
    duration_weeks?: number;
    duration_days?: number;
    session_mode?: ExpertSessionMode;
    max_slots?: number;
    weekly_time_commitment?: string;
    prerequisites?: string;
    price_cents?: number;
  }): Promise<ExpertProgramDetail> => {
    const response = await authAxios.post(`${API_BASE}/expert-programs`, data);
    return response.data;
  },
  updateExpertProgram: async (
    externalId: string,
    data: {
      name?: string;
      description?: string;
      who_is_this_program_for?: string;
      program_outcomes?: string;
      /** Setting either duration clears the other on the server. */
      duration_weeks?: number;
      duration_days?: number;
      session_mode?: ExpertSessionMode;
      max_slots?: number;
      weekly_time_commitment?: string;
      prerequisites?: string;
      /** Enrollment price in integer cents. Omit to leave the saved price untouched. */
      price_cents?: number;
    },
  ): Promise<ExpertProgramDetail> => {
    const response = await authAxios.patch(`${API_BASE}/expert-programs/${externalId}`, data);
    return response.data;
  },
  getExpertProgram: async (externalId: string): Promise<ExpertProgramDetail> => {
    const response = await authAxios.get(`${API_BASE}/expert-programs/${externalId}`);
    return response.data;
  },
  getMembers: async (externalId: string): Promise<ProgramMembersGrouped> => {
    const response = await authAxios.get(`${API_BASE}/expert-programs/${externalId}/members`);
    const data: ProgramMembersGrouped = response.data;
    return {
      ...data,
      lead_advisors: data.lead_advisors.map(withProfilePictureUrl),
      co_advisors: data.co_advisors.map(withProfilePictureUrl),
      achievers: data.achievers.map(withProfilePictureUrl),
    };
  },
  // Expert OKR/OKT responses are camelCase (ExpertOKROut / ExpertOKTOut extend
  // CamelModel), matching the ExpertOKR/ExpertOKT types and the rest of the app.
  // GET and every mutation now return the payload directly so cache patches and
  // render code read the same field names — edits reflect without a refresh.
  /**
   * A program's goals.
   *
   * `templateOnly` forces the PROGRAM's own goals (`is_template`) regardless of
   * who is asking. Without it the endpoint branches on the caller's role —
   * staff get the program's goals, a participant gets their personal ones —
   * which is right for the default view but useless when a screen needs to show
   * both side by side, as the peer Goals tab does.
   */
  getOkrs: async (externalId: string, templateOnly?: boolean): Promise<ExpertOKR[]> => {
    const response = await authAxios.get(`${API_BASE}/expert-programs/${externalId}/okrs`, {
      params: templateOnly ? { template_only: true } : undefined,
    });
    return response.data;
  },
  getMemberOkrs: async (externalId: string, membershipId: number): Promise<ExpertOKR[]> => {
    const response = await authAxios.get(
      `${API_BASE}/expert-programs/${externalId}/members/${membershipId}/okrs`,
    );
    return response.data;
  },
  getMemberOkrsProgress: async (
    externalId: string,
    membershipId: number,
  ): Promise<MemberOkrsProgress> => {
    const response = await authAxios.get(
      `${API_BASE}/expert-programs/${externalId}/members/${membershipId}/okrs/progress`,
    );
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const d = response.data as any;
    return {
      progress_percentage: d.progress_percentage ?? d.progressPercentage ?? 0,
      total_goals: d.total_goals ?? d.totalGoals,
      completed_goals: d.completed_goals ?? d.completedGoals,
      total_tasks: d.total_tasks ?? d.totalTasks,
      completed_tasks: d.completed_tasks ?? d.completedTasks,
    };
  },
  createMemberOkr: async (
    externalId: string,
    membershipId: number,
    data: { title: string; description?: string },
  ): Promise<ExpertOKR> => {
    const response = await authAxios.post(
      `${API_BASE}/expert-programs/${externalId}/members/${membershipId}/okrs`,
      data,
    );
    return response.data;
  },
  updateMemberOkr: async (
    externalId: string,
    membershipId: number,
    okrId: number,
    data: {
      title?: string;
      description?: string | null;
      status?: string;
      due_date?: string | null;
    },
  ): Promise<ExpertOKR> => {
    const response = await authAxios.put(
      `${API_BASE}/expert-programs/${externalId}/members/${membershipId}/okrs/${okrId}`,
      data,
    );
    return response.data;
  },
  achieveMemberOkr: async (
    externalId: string,
    membershipId: number,
    okrId: number,
  ): Promise<ExpertOKR> => {
    const response = await authAxios.put(
      `${API_BASE}/expert-programs/${externalId}/members/${membershipId}/okrs/${okrId}`,
      { status: "ACHIEVED" },
    );
    return response.data;
  },
  deleteMemberOkr: async (
    externalId: string,
    membershipId: number,
    okrId: number,
  ): Promise<void> => {
    await authAxios.delete(
      `${API_BASE}/expert-programs/${externalId}/members/${membershipId}/okrs/${okrId}`,
    );
  },
  updateExpertOkt: async (
    okrId: number,
    oktId: number,
    data: { title?: string; start_date?: string | null; due_date?: string | null; status?: string },
  ): Promise<ExpertOKT> => {
    const response = await authAxios.put(`${API_BASE}/okrs/${okrId}/okts/${oktId}`, data);
    return response.data;
  },
  createExpertOkt: async (
    okrId: number,
    data: { title: string; start_date?: string; due_date?: string; status?: string },
  ): Promise<ExpertOKT> => {
    const response = await authAxios.post(`${API_BASE}/okrs/${okrId}/okts`, data);
    return response.data;
  },
  getSessions: async (externalId: string): Promise<ExpertSession[]> => {
    const response = await authAxios.get(`${API_BASE}/expert-programs/${externalId}/sessions`);
    return response.data;
  },
  createOkr: async (
    externalId: string,
    data: { title: string; description?: string },
  ): Promise<ExpertOKR> => {
    const response = await authAxios.post(`${API_BASE}/expert-programs/${externalId}/okrs`, data);
    return response.data;
  },
  createOkt: async (
    externalId: string,
    okrId: number,
    data: {
      title: string;
      description?: string;
      start_date?: string;
      due_date?: string;
      status?: string;
    },
  ): Promise<ExpertOKT> => {
    const response = await authAxios.post(
      `${API_BASE}/expert-programs/${externalId}/okrs/${okrId}/okts`,
      data,
    );
    return response.data;
  },
  updateOkr: async (
    externalId: string,
    okrId: number,
    data: {
      title?: string;
      description?: string | null;
      due_date?: string | null;
      status?: string;
    },
  ): Promise<ExpertOKR> => {
    const response = await authAxios.patch(
      `${API_BASE}/expert-programs/${externalId}/okrs/${okrId}`,
      data,
    );
    return response.data;
  },
  updateOkt: async (
    externalId: string,
    okrId: number,
    oktId: number,
    data: {
      status?: string;
      title?: string;
      start_date?: string | null;
      due_date?: string | null;
      description?: string | null;
    },
  ): Promise<ExpertOKT> => {
    const response = await authAxios.patch(
      `${API_BASE}/expert-programs/${externalId}/okrs/${okrId}/okts/${oktId}`,
      data,
    );
    return response.data;
  },
  getOkrAiSuggestions: async (
    externalId: string,
    force = false,
  ): Promise<ExpertOKRSuggestion[]> => {
    const response = await authAxios.post(
      `${API_BASE}/expert-programs/${externalId}/okrs/ai-suggestions`,
      null,
      {
        params: { force },
      },
    );
    return response.data.items;
  },
  selectOkrAiSuggestion: async (
    externalId: string,
    okrId: number,
    title?: string,
  ): Promise<ExpertOKR> => {
    const response = await authAxios.post(
      `${API_BASE}/expert-programs/${externalId}/okrs/ai-suggestions/select`,
      {
        okrId,
        title,
      },
    );
    return response.data;
  },
  getOktAiSuggestions: async (
    externalId: string,
    okrId: number,
    force = false,
  ): Promise<ExpertOKTSuggestion[]> => {
    const response = await authAxios.post(
      `${API_BASE}/expert-programs/${externalId}/okrs/${okrId}/okts/ai-suggestions`,
      null,
      { params: { force } },
    );
    return response.data.items;
  },
  selectOktAiSuggestions: async (
    externalId: string,
    okrId: number,
    finalizeData: { id: number; title: string }[],
  ): Promise<ExpertOKT[]> => {
    const response = await authAxios.post(
      `${API_BASE}/expert-programs/${externalId}/okrs/${okrId}/okts/selection`,
      { finalize_data: finalizeData },
    );
    return response.data;
  },
  createSession: async (
    externalId: string,
    data: {
      title: string;
      description?: string;
      session_type: string;
      start_time?: string;
      end_time?: string;
      duration_minutes?: number;
      /** Organisation programs only: KICKOFF / CHECKPOINT / RECAP / NORTH_STAR / OKR / OKI. */
      org_kind?: string;
      /** Organisation programs only: the North Star a check-in belongs to. */
      north_star_id?: number;
      /** Organisation programs only: the objective an OKR meeting belongs to. */
      okr_id?: number;
      /** Organisation programs only: the initiative an OKI meeting belongs to. */
      oki_id?: number;
      /** Organisation programs only: the IANA zone the date was picked in --
       *  the server checks the date against the cycle there. */
      timezone?: string;
    },
  ): Promise<ExpertSession> => {
    const response = await authAxios.post(
      `${API_BASE}/expert-programs/${externalId}/sessions`,
      data,
    );
    return response.data;
  },
  createSessionScheduler: async (
    externalId: string,
    durationMinutes = 60,
    title?: string,
    description?: string,
    signal?: AbortSignal,
  ): Promise<{
    id: number;
    nylasConfigurationId: string | null;
    slug: string | null;
    name: string;
    configType: string;
    durationMinutes: number;
    isNew: boolean;
  }> => {
    const response = await authAxios.post(
      `${API_BASE}/expert-programs/${externalId}/sessions/scheduler`,
      null,
      { params: { duration_minutes: durationMinutes, title, description }, signal },
    );
    return response.data;
  },
  confirmSessionBooking: async (
    externalId: string,
    data: {
      title: string;
      description?: string;
      start_time?: string;
      end_time?: string;
      calendar_event_id?: string;
      meeting_url?: string;
      booking_id?: string;
      booking_ref?: string;
      session_type?: string;
    },
  ): Promise<ExpertSession> => {
    const response = await authAxios.post(
      `${API_BASE}/expert-programs/${externalId}/sessions/confirm-booking`,
      data,
    );
    return response.data;
  },
  deleteOkr: async (externalId: string, okrId: number): Promise<void> => {
    await authAxios.delete(`${API_BASE}/expert-programs/${externalId}/okrs/${okrId}`);
  },
  deleteOkt: async (externalId: string, okrId: number, oktId: number): Promise<void> => {
    await authAxios.delete(`${API_BASE}/expert-programs/${externalId}/okrs/${okrId}/okts/${oktId}`);
  },
  /** Returns { [templateOktId]: completedAchieverCount } for the program */
  getOktCompletionStats: async (externalId: string): Promise<Record<string, number>> => {
    const response = await authAxios.get(
      `${API_BASE}/expert-programs/${externalId}/okrs/completion-stats`,
    );
    return response.data as Record<string, number>;
  },
  publishProgram: async (externalId: string): Promise<ExpertProgramDetail> => {
    const response = await authAxios.post(`${API_BASE}/expert-programs/${externalId}/publish`, {});
    return response.data;
  },
  /**
   * Submit a draft (or previously rejected) program for Forward-team review.
   * Moves it to PENDING_REVIEW — this is NOT a publish: the program stays
   * invisible to achievers until a reviewer approves it, which is what
   * publishProgram() then performs.
   */
  submitProgramForReview: async (externalId: string): Promise<ExpertProgramDetail> => {
    const response = await authAxios.post(
      `${API_BASE}/expert-programs/${externalId}/submit-for-review`,
      {},
    );
    return response.data;
  },
  /** Completed-program report: hero stats, cohort outcomes, individual outcomes, session log, NPS, reflections */
  getProgramReport: async (externalId: string): Promise<ProgramReport> => {
    const response = await authAxios.get(`${API_BASE}/expert-programs/${externalId}/report`);
    return response.data;
  },
  updateAdvisorReflection: async (externalId: string, reflection: string): Promise<string> => {
    const response = await authAxios.patch(
      `${API_BASE}/expert-programs/${externalId}/report/reflection`,
      {
        advisor_reflection: reflection,
      },
    );
    return response.data.advisor_reflection;
  },
  deleteProgram: async (externalId: string): Promise<void> => {
    await authAxios.delete(`${API_BASE}/expert-programs/${externalId}`);
  },
  /** Clone a program (any status) into a new draft. Lead only. */
  cloneProgram: async (externalId: string): Promise<ExpertProgramDetail> => {
    const response = await authAxios.post(`${API_BASE}/expert-programs/${externalId}/clone`, {});
    return response.data;
  },
  getWeeklyPlan: async (externalId: string): Promise<WeeklyPlanResponse> => {
    const response = await authAxios.get(`${API_BASE}/expert-programs/${externalId}/weekly-plan`);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const raw = response.data as any;
    return {
      sessionId: raw.session_id ?? raw.sessionId ?? 0,
      activeFrom: raw.active_from ?? raw.activeFrom,
      activeTill: raw.active_till ?? raw.activeTill,
      kickoffDate: raw.kickoff_date ?? raw.kickoffDate ?? null,
      wrapupStatus: raw.wrapup_status ?? raw.wrapupStatus ?? null,
      wrapupDate: raw.wrapup_date ?? raw.wrapupDate ?? null,
      weeks: (raw.weeks ?? []).map((w: any) => ({
        weekNumber: w.week_number ?? w.weekNumber,
        startDate: w.start_date ?? w.startDate,
        endDate: w.end_date ?? w.endDate,
        tasks: (w.tasks ?? []).map((t: any) => ({
          id: t.id,
          okrId: t.okr_id ?? t.okrId,
          okrTitle: t.okr_title ?? t.okrTitle ?? null,
          okrStatus: t.okr_status ?? t.okrStatus ?? null,
          title: t.title,
          status: t.status,
          dueDate: t.due_date ?? t.dueDate ?? null,
          updatedAt: t.updated_at ?? t.updatedAt,
        })),
        meetings: (w.meetings ?? w.sessions ?? []).map((m: any) => ({
          id: m.id,
          title: m.title,
          groupSession: m.group_session ?? m.groupSession ?? null,
          meetingFormat: m.meeting_format ?? m.meetingFormat ?? null,
          status: m.status ?? null,
          startTime: m.start_time ?? m.startTime,
          endTime: m.end_time ?? m.endTime,
          timezone: m.timezone ?? null,
          meetingUrl: m.meeting_url ?? m.meetingUrl ?? null,
          participants: (m.participants ?? []).map((p: any) => ({
            name: p.name,
            profileUrl: p.profile_url ?? p.profileUrl ?? null,
          })),
        })),
        okrs: (w.okrs ?? w.goals ?? []).map((o: any) => ({
          id: o.id,
          title: o.title,
          status: o.status,
          updatedAt: o.updated_at ?? o.updatedAt,
          progressPercentage: o.progress_percentage ?? o.progressPercentage,
        })),
      })),
    };
  },
  /** `allTypes` adds Peer Advisory and Organisation program sessions (XO Home). */
  getMyMeetings: async (
    workspace?: "achiever" | "advisor",
    allTypes = false,
  ): Promise<ExpertProgramMeeting[]> => {
    const response = await authAxios.get(`${API_BASE}/expert-programs/meetings/me`, {
      params: workspace || allTypes ? { workspace, all_types: allTypes || undefined } : undefined,
    });
    return response.data;
  },
  getMetrics: async (externalId: string): Promise<AchieverMetricsResponse> => {
    const response = await authAxios.get(`${API_BASE}/expert-programs/${externalId}/metrics`);
    return response.data;
  },

  getWrapupWeeksJourney: async (externalId: string): Promise<WrapupWeeksJourney> => {
    const response = await authAxios.get(
      `${API_BASE}/expert-programs/${externalId}/wrapup/weeks-journey`,
    );
    return response.data;
  },

  // ── Program Stars (same request/response shape as PAB stars) ───────────────

  getStarRecommendations: async (externalId: string, okrId: number): Promise<StarStory[]> => {
    const response = await authAxios.post<PaginatedStarStoriesResponse>(
      `${API_BASE}/expert-programs/${externalId}/okrs/${okrId}/stars/ai-recommendations`,
    );
    return response.data.items;
  },

  saveStarStory: async (
    externalId: string,
    okrId: number,
    starStory: StarStoryRequest,
  ): Promise<StarStoryWithMeta> => {
    const response = await authAxios.post<StarStoryWithMeta>(
      `${API_BASE}/expert-programs/${externalId}/okrs/${okrId}/stars`,
      {
        situation: starStory.situation,
        task: starStory.task,
        action: starStory.action,
        result: starStory.result,
        title: starStory.title ?? null,
      },
    );
    return response.data;
  },

  getStarsForOkr: async (externalId: string, okrId: number): Promise<StarStoryWithMeta[]> => {
    const response = await authAxios.get(
      `${API_BASE}/expert-programs/${externalId}/okrs/${okrId}/stars`,
    );
    return response.data;
  },

  getAllStars: async (externalId: string): Promise<StarStoryWithMeta[]> => {
    const response = await authAxios.get(
      `${API_BASE}/expert-programs/${externalId}/okrs/all/stars`,
    );
    return response.data;
  },

  getStarById: async (
    externalId: string,
    okrId: number,
    starId: number,
  ): Promise<StarStoryWithMeta> => {
    const response = await authAxios.get(
      `${API_BASE}/expert-programs/${externalId}/okrs/${okrId}/stars/${starId}`,
    );
    return response.data;
  },

  updateStarStory: async (
    externalId: string,
    okrId: number,
    starId: number,
    updates: Partial<Pick<StarStory, "situation" | "task" | "action" | "result" | "title">>,
  ): Promise<StarStoryWithMeta> => {
    const response = await authAxios.patch(
      `${API_BASE}/expert-programs/${externalId}/okrs/${okrId}/stars/${starId}`,
      updates,
    );
    return response.data;
  },

  deleteStarStory: async (externalId: string, okrId: number, starId: number): Promise<void> => {
    await authAxios.delete(
      `${API_BASE}/expert-programs/${externalId}/okrs/${okrId}/stars/${starId}`,
    );
  },

  generateAdvisorTags: async (externalId: string, advisorId: string): Promise<string[]> => {
    const response = await authAxios.post<{ tags: string[] }>(
      `${API_BASE}/expert-programs/${externalId}/advisors/${advisorId}/generate-tags`,
    );
    return response.data.tags;
  },

  submitAdvisorFeedback: async (
    programId: number,
    // v3: advisor identity is the user_id (SHA256 string), not an int PK.
    advisorId: string,
    feedbackData: {
      appreciateAboutAdvisor: string[];
      writtenTestimonial: string;
      isRecommended: boolean;
    },
  ): Promise<AdvisorProgramFeedbackResponse> => {
    const response = await authAxios.post(
      `${API_BASE}/programs/${programId}/advisors/${advisorId}/feedback`,
      feedbackData,
    );
    return response.data;
  },

  getAdvisorFeedback: async (
    programId: number,
    advisorId: string,
  ): Promise<AdvisorProgramFeedbackResponse | null> => {
    try {
      const response = await authAxios.get(
        `${API_BASE}/programs/${programId}/advisors/${advisorId}/feedback`,
      );
      return response.data;
    } catch {
      return null;
    }
  },

  sendCohortMessage: async (externalId: string, body: string): Promise<void> => {
    await authAxios.post(`${API_BASE}/expert-programs/${externalId}/messages`, { body });
  },

  /**
   * Create a group or direct conversation for a program.
   * POST /api/v1/programs/{program_id}/messages/conversation?conversation_type=group|direct
   */
  createConversation: async (
    programId: number,
    conversationType: "group" | "direct",
  ): Promise<ProgramConversation> => {
    const response = await authAxios.post(
      `${API_BASE}/programs/${programId}/messages/conversation`,
      null,
      { params: { conversation_type: conversationType } },
    );
    return response.data;
  },

  /**
   * Get all messages (group + current user's direct conversations) for a program.
   * GET /api/v1/programs/{program_id}/messages
   */
  getProgramMessages: async (
    programId: number,
    opts?: { skipForbiddenRedirect?: boolean },
  ): Promise<PabMessage[]> => {
    const response = await authAxios.get(
      `${API_BASE}/programs/${programId}/messages`,
      opts?.skipForbiddenRedirect ? ({ _skipForbiddenRedirect: true } as never) : undefined,
    );
    return response.data;
  },

  /**
   * Get direct messages with a specific advisor for a program.
   * GET /api/v1/programs/{program_id}/messages/direct?advisor_id={advisor_id}
   */
  getDirectProgramMessages: async (programId: number, advisorId: number): Promise<PabMessage[]> => {
    const response = await authAxios.get(`${API_BASE}/programs/${programId}/messages/direct`, {
      params: { advisor_id: advisorId },
    });
    return response.data;
  },

  /**
   * Send a message to the group or directly to an advisor.
   * POST /api/v1/programs/{program_id}/messages
   * Omit advisorId (or pass null) to send to the group conversation.
   */
  sendProgramMessage: async (
    programId: number,
    body: string,
    advisorId?: number | null,
    /** See pabSessions.sendMessage — upload first, then link by id. */
    attachmentIds?: number[],
  ): Promise<PabMessage> => {
    const payload: SendProgramMessageRequest = { body };
    if (advisorId != null) {
      payload.advisor_id = advisorId;
    }
    if (attachmentIds?.length) {
      payload.attachmentIds = attachmentIds;
    }
    const response = await authAxios.post(`${API_BASE}/programs/${programId}/messages`, payload);
    return response.data;
  },

  /**
   * Get direct messages between the current user and another user in a program.
   * GET /api/v1/programs/{program_id}/messages/direct/{other_user_id}
   */
  getDirectProgramMessagesByUser: async (
    programId: number,
    otherUserId: string,
    opts?: { skipForbiddenRedirect?: boolean },
  ): Promise<PabMessage[]> => {
    const response = await authAxios.get(
      `${API_BASE}/programs/${programId}/messages/direct/${otherUserId}`,
      opts?.skipForbiddenRedirect ? ({ _skipForbiddenRedirect: true } as never) : undefined,
    );
    return response.data;
  },

  /**
   * Mark a program message as read
   * PATCH /api/v1/programs/{program_id}/messages/{message_id}
   */
  markMessageAsRead: async (programId: number, messageId: number): Promise<PabMessage> => {
    const response = await authAxios.patch(
      `${API_BASE}/programs/${programId}/messages/${messageId}`,
      { isRead: true },
    );
    return response.data;
  },

  /**
   * Send a direct message to a specific user in a program.
   * POST /api/v1/programs/{program_id}/messages/direct
   */
  sendDirectProgramMessage: async (
    programId: number,
    recipientUserId: string,
    body: string,
    /** See pabSessions.sendDirectMessageByUserId. */
    attachmentIds?: number[],
  ): Promise<PabMessage> => {
    const response = await authAxios.post(`${API_BASE}/programs/${programId}/messages/direct`, {
      recipient_user_id: recipientUserId,
      body,
      ...(attachmentIds?.length ? { attachmentIds } : {}),
    });
    return response.data;
  },
  getAchieverProgress: async (externalId: string): Promise<AchieverProgress> => {
    const response = await authAxios.get(
      `${API_BASE}/expert-programs/${externalId}/achiever-progress`,
    );
    return response.data;
  },
  deleteSession: async (externalId: string, sessionId: number): Promise<void> => {
    await authAxios.delete(`${API_BASE}/expert-programs/${externalId}/sessions/${sessionId}`);
  },
  /** Removes every GROUP session after the first one. Called right after the
   *  first session is rescheduled: the others are booked inside a range measured
   *  from it, so they have to be re-created against the new range. The expert has
   *  already confirmed this in FirstSessionRescheduleDialog. */
  /** keepSessionId: the first session that was just moved. Required in practice —
   *  after the move it may no longer be the earliest, so the server can't infer it. */
  clearLaterSessions: async (
    externalId: string,
    keepSessionId?: number,
  ): Promise<{ removed: { id: number; title: string }[]; removedCount: number }> => {
    const response = await authAxios.post(
      `${API_BASE}/expert-programs/${externalId}/sessions/clear-later`,
      keepSessionId != null ? { keep_session_id: keepSessionId } : {},
    );
    return response.data;
  },
  updateSession: async (
    externalId: string,
    sessionId: number,
    data: {
      title?: string;
      description?: string;
      start_time?: string;
      end_time?: string;
      /** Organisation programs: the IANA zone the date was picked in. */
      timezone?: string;
    },
  ): Promise<ExpertSession> => {
    const response = await authAxios.put(
      `${API_BASE}/expert-programs/${externalId}/sessions/${sessionId}`,
      data,
    );
    return response.data;
  },
  /**
   * The briefing artifact for one session.
   *
   * Readable by every member — experts to edit, the cohort to read — so this is
   * safe to call from both the expert and achiever surfaces. `canEdit` in the
   * response says which one the caller is; don't re-derive it from the local
   * role, or the button and the endpoint can disagree.
   *
   * Note the snake_case: the expert-programs endpoints serialise that way
   * (`is_template`, `session_type`), unlike the camelCase PAB ones.
   */
  getSessionPrep: async (externalId: string, sessionId: number): Promise<ExpertSessionPrep> => {
    const response = await authAxios.get(
      `${API_BASE}/expert-programs/${externalId}/sessions/${sessionId}/prep`,
    );
    return response.data;
  },
  /** Replaces goals and agenda wholesale. Experts and co-experts only (403 otherwise). */
  updateSessionPrep: async (
    externalId: string,
    sessionId: number,
    data: {
      session_prep_goals: string[];
      meeting_agendas: { description: string; duration: number }[];
    },
  ): Promise<ExpertSessionPrep> => {
    const response = await authAxios.put(
      `${API_BASE}/expert-programs/${externalId}/sessions/${sessionId}/prep`,
      data,
    );
    return response.data;
  },
  /**
   * Publishes the saved briefing to the cohort — emails every ACTIVE achiever
   * member and drops an in-app notification. Reads the artifact from the row,
   * so save first: this cannot announce something different from what's stored.
   */
  markSessionPrepReady: async (
    externalId: string,
    sessionId: number,
  ): Promise<{ message: string; notified: number }> => {
    const response = await authAxios.post(
      `${API_BASE}/expert-programs/${externalId}/sessions/${sessionId}/prep/ready`,
      {},
    );
    return response.data;
  },
  /**
   * Called after the lead advisor picks a new slot via the NylasScheduling widget
   * in reschedule mode (rescheduleBookingRef). Nylas's Bookings API has already
   * updated the SAME booking/event in place — this just persists the new time.
   */
  rescheduleSession: async (
    externalId: string,
    sessionId: number,
    data: {
      title: string;
      description?: string;
      start_time?: string;
      end_time?: string;
      calendar_event_id: string;
      meeting_url?: string;
    },
  ): Promise<ExpertSession> => {
    const response = await authAxios.post(
      `${API_BASE}/expert-programs/${externalId}/sessions/${sessionId}/reschedule`,
      data,
    );
    return response.data;
  },
  inviteCoAdvisorByEmail: async (
    externalId: string,
    email: string,
    message?: string,
  ): Promise<{ message: string }> => {
    const response = await authAxios.post(
      `${API_BASE}/expert-programs/${externalId}/members/invite-by-email`,
      { email, message: message?.trim() || undefined },
    );
    return response.data;
  },
  resendCoAdvisorInvitation: async (
    externalId: string,
    invitationId: number,
  ): Promise<{ message: string }> => {
    const response = await authAxios.post(
      `${API_BASE}/expert-programs/${externalId}/members/invitations/${invitationId}/resend`,
    );
    return response.data;
  },
  removeMember: async (externalId: string, membershipId: number): Promise<void> => {
    await authAxios.delete(`${API_BASE}/expert-programs/${externalId}/members/${membershipId}`);
  },
  deleteCoAdvisorInvitation: async (externalId: string, invitationId: number): Promise<void> => {
    await authAxios.delete(
      `${API_BASE}/expert-programs/${externalId}/members/invitations/${invitationId}`,
    );
  },
  // Same endpoint as deleteCoAdvisorInvitation — it's role-agnostic on the backend
  // (rescinds any pending invitation with no membership yet) — named separately
  // here just for call-site clarity when rescinding an achiever invite.
  deleteAchieverInvitation: async (externalId: string, invitationId: number): Promise<void> => {
    await authAxios.delete(
      `${API_BASE}/expert-programs/${externalId}/members/invitations/${invitationId}`,
    );
  },

  /**
   * Every program invitation waiting on the caller, grouped by program type.
   *
   * Replaces the per-type listings. All three types live in ONE table
   * (program_invitations, discriminated by the joined program's type), so this
   * is a single request that already covers Expert and Peer Advisory — and the
   * next program type costs nothing.
   *
   * PAB is deliberately NOT sourced from here on the dashboard: its card shows
   * the achiever's full profile (bio, growth areas, LinkedIn) so an advisor can
   * decide whether to join that person's board, and that data comes from
   * /pab-sessions/invited/advisor. This endpoint still returns PAB rows for any
   * caller that only needs the program and inviter.
   */
  getMyProgramInvitations: async (): Promise<MyProgramInvitations> => {
    const response = await authAxios.get(`${API_BASE}/programs/invitations/me`);
    return response.data;
  },

  respondToCoAdvisorInvitation: async (
    externalId: string,
    invitationId: number,
    invStatus: "ACCEPTED" | "REJECTED",
  ): Promise<ProgramCoAdvisorInvitation> => {
    const response = await authAxios.patch(
      `${API_BASE}/expert-programs/${externalId}/co-advisors/invitations/${invitationId}`,
      { status: invStatus },
    );
    return response.data;
  },

  inviteAchievers: async (
    externalId: string,
    emails: string[],
  ): Promise<{ invited: string[]; skipped: string[]; invalid: string[] }> => {
    const response = await authAxios.post(
      `${API_BASE}/expert-programs/${externalId}/achievers/invite`,
      { emails },
    );
    return response.data;
  },

  /** The achiever's wrap-up progress on each program they've started wrapping up. */
  getMyWrapups: async (): Promise<ProgramWrapupProgress[]> => {
    const response = await authAxios.get(`${API_BASE}/expert-programs/wrapup/me`);
    return response.data;
  },
  /** Saves this program's wrap-up step, and marks it submitted with `completed`. */
  updateWrapupProgress: async (
    externalId: string,
    body: { step?: number; completed?: boolean },
  ): Promise<ProgramWrapupProgress> => {
    const response = await authAxios.put(
      `${API_BASE}/expert-programs/${externalId}/wrapup/progress`,
      body,
    );
    return response.data;
  },
  archiveProgram: async (
    externalId: string,
    lockedAt: string | null,
  ): Promise<{ id: number; status: string; lockedAt: string | null }> => {
    const response = await authAxios.post(`${API_BASE}/expert-programs/${externalId}/archive`, {
      lockedAt,
    });
    return response.data;
  },

  getOverallFeedback: async (externalId: string): Promise<GetOverallPabFeedbackResponse | null> => {
    try {
      const response = await authAxios.get(
        `${API_BASE}/expert-programs/${externalId}/feedback/overall`,
      );
      return response.data ?? null;
    } catch {
      return null;
    }
  },

  submitOverallFeedback: async (
    externalId: string,
    feedbackData: OverallPabFeedbackRequest,
  ): Promise<OverallPabFeedbackResponse> => {
    const response = await authAxios.post(
      `${API_BASE}/expert-programs/${externalId}/feedback/overall`,
      feedbackData,
    );
    return response.data;
  },

  updateOverallFeedback: async (
    externalId: string,
    feedbackData: OverallPabFeedbackRequest,
  ): Promise<GetOverallPabFeedbackResponse> => {
    const response = await authAxios.put(
      `${API_BASE}/expert-programs/${externalId}/feedback/overall`,
      feedbackData,
    );
    return response.data;
  },

  getPublicPreview: async (programId: string): Promise<PublicProgramPreviewOut> => {
    const response = await publicAxios.get(`${API_BASE}/public/expert-programs/${programId}`);
    return response.data;
  },
};

// ── Resources ────────────────────────────────────────────────────────────────
// Files uploaded against a PAB or an Expert Program. Both are `programs` rows,
// so one set of endpoints serves both — keyed on the NUMERIC program id, not an
// Expert Program's string externalId.

export type ResourceSource = "RESOURCE" | "PREREQUISITE" | "MESSAGE";

export interface ProgramResource {
  id: number;
  programId: number;
  filename: string;
  contentType: string | null;
  sizeBytes: number | null;
  /** Optional free-text chip on the row ("Essential", "Session 5 Prep"). */
  label: string | null;
  /** Which entry point created it — Resources tab, prerequisites, or chat. */
  source: ResourceSource;
  version: number;
  createdAt: string;
  uploadedByUserId: string;
  uploadedByName: string | null;
  /**
   * Whether the CURRENT viewer may delete this file: true for their own uploads
   * and for admins. Decided by the server per row — do not re-derive it by
   * comparing uploadedByUserId to the current user, or the rule ends up in two
   * places and drifts. Render the delete control from this and nothing else.
   */
  canDelete: boolean;
  /**
   * When the retention job will delete this file — one year after upload.
   * Server-computed from the same setting the Celery task reads, so the
   * countdown shown here and the deletion that actually happens can't drift.
   * Null only if the server omits it (older payloads); treat that as "no
   * warning" rather than "expires now".
   */
  expiresAt: string | null;
}

export interface ProgramResourceListResponse {
  resources: ProgramResource[];
  total: number;
}

export interface ResourceDownloadLink {
  /** Presigned and short-lived — fetch it at click time, never cache it. */
  url: string;
  filename: string;
  expiresIn: number;
}

export const resourcesApi = {
  list: async (programId: number): Promise<ProgramResourceListResponse> => {
    const response = await authAxios.get(`${API_BASE}/programs/${programId}/resources`);
    return response.data;
  },

  upload: async (
    programId: number,
    file: File,
    options?: { label?: string; source?: ResourceSource },
  ): Promise<ProgramResource> => {
    const formData = new FormData();
    formData.append("file", file);
    if (options?.label) formData.append("label", options.label);
    if (options?.source) formData.append("source", options.source);
    const response = await authAxios.post(`${API_BASE}/programs/${programId}/resources`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },

  /**
   * Exchanges a resource id for a short-lived presigned URL.
   *
   * Called at the moment the user clicks download, never stored — the link
   * expires in minutes, which is what stops a copied URL working for anyone who
   * isn't a member. There is no durable file URL to hold on to by design.
   */
  getDownloadLink: async (programId: number, resourceId: number): Promise<ResourceDownloadLink> => {
    const response = await authAxios.get(
      `${API_BASE}/programs/${programId}/resources/${resourceId}/download`,
    );
    return response.data;
  },

  delete: async (programId: number, resourceId: number): Promise<void> => {
    await authAxios.delete(`${API_BASE}/programs/${programId}/resources/${resourceId}`);
  },
};

export interface ProgramWrapupProgress {
  externalId: string;
  wrapupStep: number | null;
  wrapupCompletedAt: string | null;
}

export interface ExpertProgramMeeting {
  id: number;
  program_id: number;
  /** Used to address the program's session-feedback routes. */
  program_external_id: string | null;
  program_name: string | null;
  program_type?: "EXPERT" | "PEER_ADVISORY" | "ORGANISATION" | null;
  title: string;
  description: string | null;
  is_template: boolean;
  session_type: string;
  start_time: string | null;
  end_time: string | null;
  meeting_url: string | null;
  membership_id: number | null;
}

export interface ExpertProgramCoAdvisorSummary {
  user_id: string;
  full_name: string | null;
  profile_picture_url: string | null;
  bio: string | null;
  slug: string | null;
}

export interface PublicAdvisorSummary {
  fullName: string | null;
  profilePictureUrl: string | null;
  currentTitle: string | null;
  currentCompany: string | null;
  bio: string | null;
  publicSlug: string | null;
}

export interface PublicGoalOut {
  id: number;
  title: string;
  description: string | null;
  tasksCount: number;
}

export interface PublicSessionOut {
  id: number;
  title: string;
  description: string | null;
  sessionType: "GROUP" | "ONE_ON_ONE" | null;
  startTime: string | null;
  endTime: string | null;
}

/** One entry of a Peer Advisory program's ordered lists. */
export interface PublicPeerListItem {
  id: string | null;
  text: string;
  order: number;
}

/** Why the viewer cannot apply to a peer program right now. */
export type PeerApplyBlockedReason =
  | "ALREADY_MEMBER"
  | "ALREADY_APPLIED"
  | "DEADLINE_PASSED"
  | "GROUP_FULL"
  | "INVITE_ONLY"
  | "NOT_APPROVED_EXPERT";

/**
 * Everything a Peer Advisory card or preview carries that an Expert Program has
 * no equivalent for. Present only when `programType === "PEER_ADVISORY"`.
 *
 * Nested rather than flattened onto the card so the Expert shape does not grow a
 * dozen always-null fields — `programType` says whether to read this.
 */
export interface PublicPeerFields {
  /** A peer group's size is a per-program band with a real floor, not a constant. */
  minMembers: number | null;
  maxMembers: number | null;
  /**
   * The application cut-off. Independent of the program's own dates, and the
   * real reason a peer program stops accepting people.
   */
  applicationDeadline: string | null;
  visibility: "PUBLIC" | "INVITE_ONLY" | "ORG_CODE" | null;
  sessionMode: "SINGLE" | "MULTI" | null;
  facilitationMode: "SELF" | "INVITED" | "REQUEST_FA" | null;
  /** LEAD + FACILITATOR. Kept apart from leadAdvisor/coAdvisors, whose names describe the Expert roles. */
  facilitators: PublicAdvisorSummary[];
  prerequisites: PublicPeerListItem[];
  expectedOutcomes: PublicPeerListItem[];
  exampleChallenges: PublicPeerListItem[];
  groupAgreement: PublicPeerListItem[];
  audienceCriteria: {
    functions?: string[];
    levels?: string[];
    industries?: string[];
    org_types?: string[];
    years_of_experience?: string | null;
  } | null;

  /**
   * Whether this viewer may apply, resolved SERVER-side. Joining a peer group is
   * an application the facilitator reviews, so it is not the boolean isEnrolled
   * the Expert flow uses — and the rule is a conjunction of capability,
   * membership, deadline and seat count. Never re-derive it here; a client copy
   * would drift from what the apply endpoint actually enforces.
   */
  viewerCanApply: boolean;
  /** null when the viewer can apply, and for an anonymous viewer (who is asked to sign in). */
  viewerApplyBlockedReason: PeerApplyBlockedReason | null;
  viewerApplicationStatus:
    | "DRAFT"
    | "SUBMITTED"
    | "ACCEPTED"
    | "WAITLISTED"
    | "INFO_REQUESTED"
    | "REJECTED"
    | "WITHDRAWN"
    | null;
  /** Which wizard step to resume a DRAFT on. */
  viewerApplicationLastStep: string | null;
}

export interface PublicProgramCardOut {
  externalId: string;
  name: string;
  description: string | null;
  /**
   * The discriminator: which badge to show, which stats the card has, and
   * whether the action is "Enroll" or "Apply".
   */
  programType: "EXPERT" | "PEER_ADVISORY";
  /** Present only when programType === "PEER_ADVISORY". */
  peer: PublicPeerFields | null;
  isFeatured: boolean;
  /**
   * The caller's own active membership role on this program, or null for
   * anonymous visitors and non-members. Explore reports the relationship rather
   * than removing the row, so this is what decides which action to offer:
   * LEAD/CO_ADVISOR/FACILITATOR means the viewer runs it and must never see Enroll.
   */
  viewerRole: "LEAD" | "CO_ADVISOR" | "FACILITATOR" | "ACHIEVER" | "PEER" | null;
  /**
   * The viewer is a participant. True for ACHIEVER *and* PEER — a peer group's
   * participants hold the PEER role, so keying on ACHIEVER alone read them as
   * non-members.
   */
  isEnrolled: boolean;
  startsAt: string | null;
  durationWeeks: number;
  /** Expert Programs: takes precedence over durationWeeks when set (1, 2 or 4). */
  durationDays?: number | null;
  sessionMode?: ExpertSessionMode;
  sessionsCount: number;
  slotsLeft: number;
  membersCount: number;
  maxSlots: number;
  leadAdvisor: PublicAdvisorSummary | null;
  coAdvisors: PublicAdvisorSummary[];
  /** Integer cents. `null` means the expert hasn't set a price yet. */
  priceCents: number | null;
}

export interface PublicProgramExploreOut {
  /** Never paginated — every is_featured program, regardless of page/pageSize. */
  featured: PublicProgramCardOut[];
  /**
   * This page's slice of the catalogue. Featured programs are INCLUDED here as
   * well as in `featured` — the two lists overlap rather than partitioning the
   * catalogue, so a featured program shows in the carousel and in the grid.
   */
  programs: PublicProgramCardOut[];
  page: number;
  pageSize: number;
  /** Total programs in the grid set (what page/pageSize page through). */
  totalPrograms: number;
  totalPages: number;
}

export interface PublicProgramPreviewOut {
  externalId: string;
  name: string;
  description: string | null;
  status: ProgramStatus;
  /** See PublicProgramCardOut.programType. */
  programType: "EXPERT" | "PEER_ADVISORY";
  /**
   * Present only when programType === "PEER_ADVISORY".
   *
   * Note this endpoint 404s for a peer program the viewer may not apply to, so
   * reaching a peer preview at all means they are an approved Expert.
   */
  peer: PublicPeerFields | null;
  isFeatured: boolean;
  /** See PublicProgramCardOut.viewerRole. */
  viewerRole: "LEAD" | "CO_ADVISOR" | "FACILITATOR" | "ACHIEVER" | "PEER" | null;
  /**
   * The viewer is a participant (ACHIEVER or PEER). Authoritative in a way the
   * payment entitlement list is not: a free join creates a membership but no
   * entitlement.
   */
  isEnrolled: boolean;
  startsAt: string | null;
  endsAt: string | null;
  slug: string | null;
  durationWeeks: number;
  /** Expert Programs: takes precedence over durationWeeks when set (1, 2 or 4). */
  durationDays?: number | null;
  sessionMode?: ExpertSessionMode;
  slotsLeft: number;
  membersCount: number;
  maxSlots: number;
  okrsCount: number;
  sessionsCount: number;
  weeklyTimeCommitment: string | null;
  whoIsThisProgramFor: string | null;
  programOutcomes: string | null;
  prerequisites: string | null;
  leadAdvisor: PublicAdvisorSummary | null;
  coAdvisors: PublicAdvisorSummary[];
  achievers: PublicAdvisorSummary[];
  goals: PublicGoalOut[];
  sessions: PublicSessionOut[];
  /** Integer cents. `null` means the expert hasn't set a price yet. */
  priceCents: number | null;
}

export type ExpertSessionMode = "SINGLE" | "MULTI";

export interface ExpertProgramDetail {
  id: number;
  external_id: string;
  name: string;
  description: string | null;
  status: string;
  program_type: string;
  slug: string | null;
  invite_token: string | null;
  starts_at: string | null;
  ends_at: string | null;
  who_is_this_program_for: string | null;
  program_outcomes: string | null;
  duration_weeks: number | null;
  /** Day-based length (1, 2 or 4). At most one of duration_days / duration_weeks is set. */
  duration_days?: number | null;
  /** SINGLE: one group session that is both kick-off and wrap-up. */
  session_mode?: ExpertSessionMode;
  /**
   * End time (UTC ISO) of the last group session -- the wrap-up. Wrap-up opens
   * once this has passed. ends_at is only a date, so it can't answer that on
   * the session's own day.
   */
  wrapup_ends_at?: string | null;
  weekly_time_commitment: string | null;
  prerequisites: string | null;
  cloned_from_id: number | null;
  created_by_user_id: string;
  /**
   * The current user's own per-program role, from `program_members`. Gate
   * Lead-only UI (clone, submit for review) on this — NOT on
   * `created_by_user_id`, which is only the original creator and goes stale the
   * moment leadership is transferred. `null` means no active membership.
   */
  my_role: "LEAD" | "CO_ADVISOR" | "FACILITATOR" | "ACHIEVER" | "PEER" | "ADVISOR" | null;
  created_at: string;
  updated_at: string;
  members_count: number;
  max_slots: number;
  okrs_count: number;
  sessions_count: number;
  co_advisors: ExpertProgramCoAdvisorSummary[];
  /**
   * Enrollment price in integer cents, resolved from this program's price_plans
   * row. `null` means the expert hasn't set one yet — distinct from 0 (free).
   */
  price_cents: number | null;
  /** Set when the program was submitted for review; reset on each resubmit. */
  submitted_at: string | null;
  /** When the approve/reject verdict was recorded. */
  reviewed_at: string | null;
  /** Reviewer feedback, populated only while status is REJECTED. */
  rejection_reason: string | null;
}

export interface MemberAdvisorProfile {
  // v3: profile is a user_profiles row keyed by user_id (SHA256 string) --
  // the backend AdvisorProfileSummary schema has no int advisor_id field.
  user_id: string | null;
  bio: string | null;
  profile_url: string | null;
  primary_function: string | null;
  current_role: string | null;
  company: string | null;
  location: string | null;
  linked_in: string | null;
  personal_website: string | null;
  years_of_experience: string | null;
  public_slug: string | null;
}

export interface MemberAchieverProfile {
  // v3: profile is a user_profiles row keyed by user_id (SHA256 string) --
  // the backend AchieverProfileSummary schema has no int achiever_id field.
  user_id: string | null;
  bio: string | null;
  profile_url: string | null;
  primary_function: string | null;
  current_role: string | null;
  company: string | null;
  location: string | null;
  linked_in: string | null;
  personal_website: string | null;
  years_of_experience: string | null;
}

export interface ProgramMember {
  id: number;
  program_id: number;
  user_id: string;
  role: string;
  status: string;
  onboarding_step: string | null;
  last_viewed_at: string | null;
  enrolled_at: string;
  completed_at: string | null;
  created_at: string;
  full_name: string | null;
  email: string | null;
  advisor_profile: MemberAdvisorProfile | null;
  achiever_profile: MemberAchieverProfile | null;
  /** Raw wire field — do not read this directly, use `profilePictureUrl` below (populated by withProfilePictureUrl in getMembers). */
  profile_picture_url: string | null;
  /** Resolved avatar URL for this member (lead advisor, co-advisor, or achiever alike) — always read this, not `profile_picture_url`. */
  profilePictureUrl: string | null;
  /** Set for PENDING co-advisors — the invitation tracking their send time / follow-up state. */
  invitation_id: number | null;
  invitation_sent_at: string | null;
}

// The /members endpoint (MembersGroupedOut) isn't CamelModel-based like most
// other schemas, so it comes back snake_case over the wire -- this is the one
// field call sites should never read in its raw form (see ProgramMember.profilePictureUrl).
function withProfilePictureUrl(member: ProgramMember): ProgramMember {
  return { ...member, profilePictureUrl: member.profile_picture_url };
}

export interface AdvisorProgramStats {
  totalEnrollees: number;
  totalPrograms: number;
  goalsTemplates: number;
  sessionsPlanned: number;
}

export interface RecentEnrollment {
  userId: string;
  fullName: string;
  profilePictureUrl: string | null;
  programName: string;
  programExternalId: string;
  enrolledAt: string;
}

/** GET /expert-programs/next-session -- the Programs page "Next session" banner. */
export interface NextProgramSession {
  sessionId: number;
  title: string | null;
  description: string | null;
  startTime: string;
  endTime: string | null;
  meetingUrl: string | null;
  /** 1-based position among the program's scheduled group sessions. */
  sessionNumber: number;
  programExternalId: string;
  programName: string;
  programDescription: string | null;
  durationWeeks: number | null;
  durationDays: number | null;
  sessionsCount: number;
  maxSlots: number | null;
  startsAt: string | null;
}

export const MAX_EXPERT_PROGRAM_SLOTS = 10;

/**
 * One bucket per state the expert can act on. `draft`, `in_review` and
 * `rejected` used to arrive as a single `draft` bucket that this client
 * re-split — each holds exactly one status now:
 *
 * - `active`    — ACTIVE, LIVE
 * - `draft`     — DRAFT only
 * - `in_review` — PENDING_REVIEW only
 * - `rejected`  — REJECTED only
 * - `past`      — COMPLETED, ARCHIVED
 */
export interface GroupedAdvisorPrograms {
  active: ExpertProgramDetail[];
  draft: ExpertProgramDetail[];
  in_review: ExpertProgramDetail[];
  rejected: ExpertProgramDetail[];
  past: ExpertProgramDetail[];
}

export type InvitationStatus =
  | "suggested"
  | "invited"
  | "accepted"
  | "rejected"
  | "withdrawn"
  | "expired";

export type InvitationRole = "LEAD_ADVISOR" | "CO_ADVISOR" | "ACHIEVER";

export interface ProgramInvitation {
  id: number;
  invited_email: string;
  invited_by_user_id: string;
  invited_by_name: string | null;
  status: InvitationStatus;
  role: InvitationRole | null;
  created_at: string;
  updated_at: string;
}

/**
 * One pending invitation from GET /programs/invitations/me.
 *
 * camelCase because that router serialises through CamelModel, unlike the
 * Expert/Peer program schemas which are snake_case — the two conventions
 * genuinely differ per router, so this is not a mistake to "fix".
 */
export interface ProgramInvitationSummary {
  id: number;
  role: string | null;
  invitedEmail: string | null;
  inviteMessage: string | null;
  createdAt: string | null;
  invitedByName: string | null;
  /** Set on invitations that expire (Organisation: 48h). */
  expiresAt?: string | null;
  /** Server-decided; an expired invitation can't be accepted. */
  isExpired?: boolean;
  program: {
    id: number;
    externalId: string;
    name: string;
    description: string | null;
    programType: "PAB" | "EXPERT" | "PEER_ADVISORY" | "ORGANISATION";
    status: string;
  };
}

export interface MyProgramInvitations {
  /** Keyed by program_type; every known type is present, empty when none. */
  invitations: Record<string, ProgramInvitationSummary[]>;
  total: number;
}

export interface ProgramCoAdvisorInvitation {
  id: number;
  invited_email: string;
  invited_by_user_id: string;
  invited_by_name: string | null;
  status: string;
  role: string | null;
  created_at: string;
  updated_at: string;
  program_id: number;
  program_name: string | null;
  program_external_id: string | null;
}

export interface ProgramMembersGrouped {
  lead_advisors: ProgramMember[];
  co_advisors: ProgramMember[];
  achievers: ProgramMember[];
  pending_email_invites: string[];
  pending_invitations: ProgramInvitation[];
  /** Pending achiever invitations — email-only, never get a ProgramMember row (see invite_achievers_by_email), so unlike co-advisors these are never mixed into `achievers`. */
  pending_achiever_invitations: ProgramInvitation[];
  /** Backend-computed total co-expert count (co_advisors + pending_invitations, already deduped and excluding declined invites) — never re-derive this on the frontend. */
  total_co_experts: number;
  /** Backend-computed count of co_advisors with status ACTIVE. */
  accepted_co_experts: number;
}

export interface MemberOkrsProgress {
  progress_percentage: number;
  total_goals?: number;
  completed_goals?: number;
  total_tasks?: number;
  completed_tasks?: number;
}

export interface ExpertOKR {
  id: number;
  title: string;
  description: string | null;
  status: string;
  progressPercentage: number | null;
  isTemplate: boolean | null;
  clonedFromId: number | null;
  membershipId: number | null;
  dueDate: string | null;
  /** Total enrolled achievers — populated by backend on advisor template view */
  totalAchievers: number;
  /** Number of OKTs in this OKR fully completed by every achiever (backend-computed) */
  completedOktCount: number;
  okts: ExpertOKT[];
}

/**
 * The briefing artifact for one Expert Program session — goals + agenda.
 *
 * snake_case because the expert-programs endpoints serialise that way, unlike
 * the camelCase PAB ones.
 */
export interface ExpertSessionPrep {
  session_id: number;
  title: string | null;
  start_time: string | null;
  end_time: string | null;
  session_prep_goals: string[];
  meeting_agendas: { description: string; duration: number }[];
  /**
   * Whether THIS caller may edit: true for experts and co-experts, false for
   * the cohort. Server-decided — render the editor from this and nothing else,
   * so the UI can't offer a save the API would reject.
   */
  can_edit: boolean;
  /** GROUP / ONE_ON_ONE. */
  session_type: string | null;
  meeting_url: string | null;
  /** Everyone ACTIVE on the program — Expert Program sessions are program-wide.
   *  Roles use the PAB vocabulary so one component labels both. */
  participants: {
    id: number;
    full_name: string;
    profile_url: string | null;
    role: "ACHIEVER" | "ADVISOR";
  }[];
  /** The expert who authored the briefing. */
  prepared_by_name: string | null;
}

export interface ExpertSession {
  id: number;
  title: string;
  description: string | null;
  is_template: boolean | null;
  session_type: string | null;
  start_time: string | null;
  end_time: string | null;
  meeting_type: string | null;
  meeting_url: string | null;
  calendar_event_id: string | null;
  membership_id: number | null;
  booking_ref: string | null;
  /** Chronologically first GROUP session — it sets the program's start date and
   *  anchors the allowed date range for every other session. */
  is_first_group_session?: boolean;
  /** Chronologically last GROUP session — it closes out the program's schedule. */
  is_last_group_session?: boolean;
  /** The server's own answer to "may this be deleted", so the trash button is
   *  hidden on exactly the sessions DELETE would reject. False for the first and
   *  last GROUP session, and for every session on a COMPLETED program. */
  can_delete?: boolean;
  /** Moving this session invalidates the later ones, so warn before rescheduling
   *  and call clearLaterSessions afterwards. True only for the first GROUP
   *  session. Mirrors PAB's rescheduleClearsLaterSessions. */
  reschedule_clears_later_sessions?: boolean;
}

export interface ExpertOKT {
  id: number;
  okrId: number;
  title: string;
  description: string | null;
  status: string;
  isTemplate: boolean | null;
  clonedFromId: number | null;
  membershipId: number | null;
  startDate: string | null;
  dueDate: string | null;
  /** Number of achievers who completed this OKT. Only populated on template OKTs returned by the advisor OKR list endpoint. */
  completionCount: number;
  notesCount?: number;
}

export interface AchieverProgressOkt {
  id: number;
  title: string;
  completed_by_count: number;
}

export interface ExpertOKRSuggestion {
  id: number;
  title: string;
  description: string | null;
}

export interface ExpertOKTSuggestion {
  id: number;
  title: string;
  description: string | null;
}

export interface AchieverProgressOkr {
  id: number;
  title: string;
  okts: AchieverProgressOkt[];
}

export interface AchieverProgress {
  achiever_count: number;
  okrs: AchieverProgressOkr[];
}

// ── Program Report (completed-program report page) ──────────────────────────

export interface ProgramReportMetrics {
  enrolledCount: number;
  avgGoalCompletionPct: number;
  avgSessionAttendancePct: number;
  avgSessionsPerPerson: number;
  sessionsTotal: number;
  goalsTracked: number;
}

export interface ProgramReportCohortOutcome {
  okrId: number;
  title: string;
  strengthLabel: "Strong" | "Moderate" | "Weak";
  pct: number;
  achieversCompleted: number;
  achieversTotal: number;
  tasksDone: number;
  tasksTotal: number;
}

export interface ProgramReportIndividualOutcome {
  userId: string;
  fullName: string | null;
  roleTitle: string | null;
  profilePictureUrl: string | null;
  goalsCompleted: number;
  goalsTotal: number;
  sessionsCompleted: number;
  sessionsTotal: number;
  npsRating: number | null;
}

export interface SessionParticipant {
  userId: string;
  fullName: string | null;
  roleTitle: string | null;
  profilePictureUrl: string | null;
  attended: boolean;
}

export interface ProgramReportSessionLogItem {
  sessionId: number;
  title: string;
  sessionType: string | null;
  startTime: string | null;
  attendedCount: number;
  totalCount: number;
  isDone: boolean;
  participants: SessionParticipant[];
}

export interface ProgramReportNps {
  score: number | null;
  label: string | null;
  responsesCount: number;
  totalCount: number;
}

export interface ProgramReportVoice {
  fullName: string | null;
  comment: string;
}

export interface ProgramReport {
  externalId: string;
  name: string;
  description: string | null;
  status: string;
  startsAt: string | null;
  endsAt: string | null;
  weeks: number;
  metrics: ProgramReportMetrics;
  cohortOutcomes: ProgramReportCohortOutcome[];
  individualOutcomes: ProgramReportIndividualOutcome[];
  sessionLog: ProgramReportSessionLogItem[];
  nps: ProgramReportNps;
  participantVoices: ProgramReportVoice[];
  advisorReflection: string | null;
}
